import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, PolygonF, DrawingManagerF } from '@react-google-maps/api';
import { Table, Button, Modal, Form, Row, Col, Card, Badge, Nav, InputGroup, ListGroup, Dropdown, DropdownButton, OverlayTrigger, Tooltip, Alert, Spinner } from 'react-bootstrap';
import { getZones, createZone, updateZone, deleteZone, deleteZonesBulk, getTariffs, createTariff, updateTariff, deleteTariff, getPriceRules, createPriceRule, updatePriceRule, deletePriceRule, getMinPriceRules, createMinPriceRule, updateMinPriceRule, deleteMinPriceRule, getClients, getVehicleTypes, createVehicleType, updateVehicleType, getQuote, getDistanceRules, createDistanceRule, updateDistanceRule, deleteDistanceRule, getBases, createBase, updateBase, getBookingAutoRules, createBookingAutoRule, updateBookingAutoRule, deleteBookingAutoRule, getExchangeRates, updateExchangeRate, createExchangeRate, deleteExchangeRate, getProviders, createProvider, updateProvider, deleteProvider, seedProviderZeroTariffs, exportTariffsToExcel, bulkUpdateGeniusDiscount, previewKmlZones, bulkImportZones, getZoneGroups, createZoneGroup, deleteZoneGroup, getMarginRules, createMarginRule, updateMarginRule, deleteMarginRule, getTariffChangeLogs, getMinPriceChangeLogs, getContracts, getSettlementGroups, getDrivers, getVehicles } from '../api';
import AddressAutocomplete from './AddressAutocomplete';
import SearchableSelect from './SearchableSelect';
import SearchableMultiSelect from './SearchableMultiSelect';
import { Search } from 'lucide-react';

const MAP_CENTER = { lat: -33.4489, lng: -70.6693 }; // Santiago

// --- COMPONENTES AUXILIARES ---

const getClientDisplay = (clientIdsStr, clientsList = []) => {
    if (!clientIdsStr || clientIdsStr === 'ALL') {
        return <Badge bg="success">Global</Badge>;
    }
    const ids = clientIdsStr.toString().split(',').map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) {
        return <Badge bg="success">Global</Badge>;
    }
    if (ids.length === 1) {
        const clientName = clientsList.find(c => String(c.id) === String(ids[0]))?.name || `ID ${ids[0]}`;
        return <Badge bg="primary">{clientName}</Badge>;
    }
    if (clientsList.length > 0 && ids.length === clientsList.length) {
        return <Badge bg="success">Global</Badge>;
    }
    const clientNames = ids.map(id => {
        return clientsList.find(c => String(c.id) === String(id))?.name || `ID ${id}`;
    });
    const tooltip = (
        <Tooltip id={`tooltip-clients-${clientIdsStr.replace(/[^a-zA-Z0-9]/g, '-')}`}>
            <div className="text-start">
                <strong>Clientes asociados:</strong>
                <ul className="mb-0 ps-3 mt-1">
                    {clientNames.map((name, idx) => <li key={idx}>{name}</li>)}
                </ul>
            </div>
        </Tooltip>
    );
    return (
        <OverlayTrigger placement="top" overlay={tooltip}>
            <Badge bg="info" style={{ cursor: 'pointer' }}>Varios ({ids.length})</Badge>
        </OverlayTrigger>
    );
};

const getVehicleTypeDisplay = (vehicleTypeIdsStr, vehicleTypesList = []) => {
    if (!vehicleTypeIdsStr || vehicleTypeIdsStr === 'ALL') {
        return <Badge bg="success">Todos</Badge>;
    }
    const ids = vehicleTypeIdsStr.toString().split(',').map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) {
        return <Badge bg="success">Todos</Badge>;
    }
    if (ids.length === 1) {
        const vtName = vehicleTypesList.find(v => String(v.id) === String(ids[0]))?.name || `ID ${ids[0]}`;
        return <Badge bg="primary">{vtName}</Badge>;
    }
    if (vehicleTypesList.length > 0 && ids.length === vehicleTypesList.length) {
        return <Badge bg="success">Todos</Badge>;
    }
    const vtNames = ids.map(id => {
        return vehicleTypesList.find(v => String(v.id) === String(id))?.name || `ID ${id}`;
    });
    const tooltip = (
        <Tooltip id={`tooltip-vehicles-${vehicleTypeIdsStr.replace(/[^a-zA-Z0-9]/g, '-')}`}>
            <div className="text-start">
                <strong>Vehículos asociados:</strong>
                <ul className="mb-0 ps-3 mt-1">
                    {vtNames.map((name, idx) => <li key={idx}>{name}</li>)}
                </ul>
            </div>
        </Tooltip>
    );
    return (
        <OverlayTrigger placement="top" overlay={tooltip}>
            <Badge bg="info" style={{ cursor: 'pointer' }}>Varios ({ids.length})</Badge>
        </OverlayTrigger>
    );
};

const getProviderDisplay = (providerIdsStr, providersList = []) => {
    if (!providerIdsStr || providerIdsStr === 'ALL') {
        return <Badge bg="success">Todos</Badge>;
    }
    if (providerIdsStr === 'NONE') {
        return <Badge bg="secondary">Ninguno</Badge>;
    }
    const ids = providerIdsStr.toString().split(',').map(id => id.trim()).filter(Boolean);
    if (ids.length === 0) {
        return <Badge bg="success">Todos</Badge>;
    }
    if (ids.length === 1) {
        const provName = providersList.find(p => String(p.id) === String(ids[0]))?.name || `ID ${ids[0]}`;
        return <Badge bg="warning" text="dark">{provName}</Badge>;
    }
    if (providersList.length > 0 && ids.length === providersList.length) {
        return <Badge bg="success">Todos</Badge>;
    }
    const provNames = ids.map(id => {
        return providersList.find(p => String(p.id) === String(id))?.name || `ID ${id}`;
    });
    const tooltip = (
        <Tooltip id={`tooltip-providers-${providerIdsStr.replace(/[^a-zA-Z0-9]/g, '-')}`}>
            <div className="text-start">
                <strong>Proveedores asociados:</strong>
                <ul className="mb-0 ps-3 mt-1">
                    {provNames.map((name, idx) => <li key={idx}>{name}</li>)}
                </ul>
            </div>
        </Tooltip>
    );
    return (
        <OverlayTrigger placement="top" overlay={tooltip}>
            <Badge bg="warning" text="dark" style={{ cursor: 'pointer' }}>Varios ({ids.length})</Badge>
        </OverlayTrigger>
    );
};

const useGoogleMapsLoaded = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    useEffect(() => {
        const checkGoogle = () => { if (window.google && window.google.maps && window.google.maps.drawing) setIsLoaded(true); };
        if (window.google && window.google.maps && window.google.maps.drawing) setIsLoaded(true);
        else {
            const interval = setInterval(checkGoogle, 200);
            const timeout = setTimeout(() => clearInterval(interval), 10000);
            return () => { clearInterval(interval); clearTimeout(timeout); };
        }
    }, []);
    return isLoaded;
};

// --- BASES MANAGER ---
const BasesManager = () => {
    const [bases, setBases] = useState([]);
    const [editBase, setEditBase] = useState(null);
    const [currency, setCurrency] = useState('');
    const [editName, setEditName] = useState(''); // Nuevo: Editar Nombre
    const [editPhone, setEditPhone] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [newBase, setNewBase] = useState({ name: '', country_code: 'CL', currency_code: 'CLP', timezone: 'America/Santiago', phone: '' });

    const load = useCallback(async () => {
        try { const data = await getBases(); setBases(data); } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleUpdate = async () => {
        if (!editBase) return;
        try {
            await updateBase(editBase.id, { name: editName, currency_code: currency, phone: editPhone });
            setEditBase(null); load();
        } catch (e) { alert("Error al actualizar la base."); }
    };

    const handleCreate = async () => {
        if (!newBase.name) return;
        try {
            await createBase(newBase);
            setShowCreate(false);
            setNewBase({ name: '', country_code: 'CL', currency_code: 'CLP', timezone: 'America/Santiago', phone: '' });
            load();
        } catch (e) { alert("Error al crear la sucursal."); }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Bases Operativas (Sucursales)</h5>
                <Button size="sm" variant="success" onClick={() => setShowCreate(true)}>+ Nueva Sucursal</Button>
            </div>
            <Table hover bordered size="sm">
                <thead className="bg-light"><tr><th>Nombre</th><th>País</th><th>Zona Horaria</th><th>Moneda Local</th><th>Teléfono Contacto</th><th>Acción</th></tr></thead>
                <tbody>
                    {bases.map(b => (
                        <tr key={b.id}>
                            <td>
                                {editBase?.id === b.id ? (
                                    <Form.Control size="sm" value={editName} onChange={e => setEditName(e.target.value)} />
                                ) : <span className="fw-bold">{b.name}</span>}
                            </td>
                            <td>{b.country_code}</td><td className="small text-muted">{b.timezone}</td>
                            <td>
                                {editBase?.id === b.id ? (
                                    <Form.Select size="sm" value={currency} onChange={e => setCurrency(e.target.value)}>
                                        <option value="CLP">CLP</option><option value="USD">USD</option><option value="ARS">ARS</option><option value="DOP">DOP</option><option value="PEN">PEN</option><option value="MXN">MXN</option><option value="COP">COP</option>
                                    </Form.Select>
                                ) : <Badge bg={b.currency_code === 'CLP' ? 'primary' : 'warning'} text="dark">{b.currency_code}</Badge>}
                            </td>
                            <td>
                                {editBase?.id === b.id ? (
                                    <Form.Control size="sm" placeholder="+1 809..." value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                                ) : (
                                    b.phone ? <Badge bg="light" text="dark" className="border">📞 {b.phone}</Badge> : <span className="text-muted small">—</span>
                                )}
                            </td>
                            <td>
                                {editBase?.id === b.id ? (
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="success" onClick={handleUpdate}>💾</Button>
                                        <Button size="sm" variant="secondary" onClick={() => setEditBase(null)}>✕</Button>
                                    </div>
                                ) : (
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-primary" onClick={() => { setEditBase(b); setEditName(b.name); setCurrency(b.currency_code); setEditPhone(b.phone || ''); }}>✏️</Button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton><Modal.Title>Nueva Sucursal</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-2"><Form.Label>Nombre</Form.Label><Form.Control placeholder="Ej: AR - Mendoza" value={newBase.name} onChange={e => setNewBase({...newBase, name: e.target.value})} /></Form.Group>
                    <Row>
                        <Col>
                            <Form.Group className="mb-2">
                                <Form.Label>País (ISO)</Form.Label>
                                <Form.Control placeholder="Ej: PE" maxLength={2} value={newBase.country_code} onChange={e => setNewBase({...newBase, country_code: e.target.value.toUpperCase()})} />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-2">
                                <Form.Label>Moneda</Form.Label>
                                <Form.Select value={newBase.currency_code} onChange={e => setNewBase({...newBase, currency_code: e.target.value})}>
                                    <option value="CLP">CLP (Chile)</option>
                                    <option value="USD">USD (Dólar)</option>
                                    <option value="ARS">ARS (Argentina)</option>
                                    <option value="DOP">DOP (R. Dom.)</option>
                                    <option value="PEN">PEN (Perú)</option>
                                    <option value="MXN">MXN (México)</option>
                                    <option value="COP">COP (Colombia)</option>
                                    <option value="BRL">BRL (Brasil)</option>
                                    <option value="UYU">UYU (Uruguay)</option>
                                    <option value="PYG">PYG (Paraguay)</option>
                                    <option value="BOB">BOB (Bolivia)</option>
                                    <option value="VES">VES (Venezuela)</option>
                                    <option value="EUR">EUR (Euro)</option>
                                    <option value="GBP">GBP (Libra Esterlina)</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-2">
                        <Form.Label>Zona Horaria</Form.Label>
                        <Form.Select value={newBase.timezone} onChange={e => setNewBase({...newBase, timezone: e.target.value})}>
                            <option value="America/Santiago">Chile (Santiago)</option>
                            <option value="America/Argentina/Buenos_Aires">Argentina (Bs Aires)</option>
                            <option value="America/Argentina/Mendoza">Argentina (Mendoza)</option>
                            <option value="America/Lima">Perú (Lima)</option>
                            <option value="America/Bogota">Colombia (Bogotá)</option>
                            <option value="America/Mexico_City">México (Ciudad de México)</option>
                            <option value="America/Sao_Paulo">Brasil (São Paulo)</option>
                            <option value="America/Santo_Domingo">Rep. Dominicana (Punta Cana/SDQ)</option>
                            <option value="America/Panama">Panamá</option>
                            <option value="America/Guayaquil">Ecuador (Guayaquil/Quito)</option>
                            <option value="America/Montevideo">Uruguay (Montevideo)</option>
                            <option value="America/Asuncion">Paraguay (Asunción)</option>
                            <option value="America/La_Paz">Bolivia (La Paz)</option>
                            <option value="America/Caracas">Venezuela (Caracas)</option>
                            <option value="America/New_York">USA (New York/Miami)</option>
                            <option value="America/Los_Angeles">USA (Los Angeles/Las Vegas)</option>
                            <option value="Europe/Madrid">España (Madrid/Barcelona)</option>
                            <option value="Europe/London">Reino Unido (Londres)</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Teléfono Contacto Base (Booking / Operaciones)</Form.Label>
                        <Form.Control placeholder="Ej: +1 809 123 4567" value={newBase.phone || ''} onChange={e => setNewBase({...newBase, phone: e.target.value})} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer><Button variant="primary" onClick={handleCreate}>Crear</Button></Modal.Footer>
            </Modal>
        </div>
    );
};

// --- EXCHANGE RATES MANAGER ---
const ExchangeRatesManager = () => {
    const [rates, setRates] = useState([]);
    const [bases, setBases] = useState([]);
    const [editRate, setEditRate] = useState(null); 
    const [newValue, setNewValue] = useState('');
    const [newTo, setNewTo] = useState(''); 
    const [newTolerance, setNewTolerance] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [newRate, setNewRate] = useState({ currency_from: 'USD', currency_to: '', rate: '', tolerance_percent: '10.0' });

    const load = useCallback(async () => {
        try {
            const [rData, bData] = await Promise.all([getExchangeRates(), getBases()]);
            setRates(rData); setBases(bData);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleUpdate = async () => {
        if (!editRate || !newValue || !newTo) return;
        try {
            await updateExchangeRate(editRate.id, { 
                currency_to: newTo, 
                rate: parseFloat(newValue),
                tolerance_percent: parseFloat(newTolerance || '10.0')
            });
            setEditRate(null); load();
        } catch (e) { alert("Error al actualizar tasa"); }
    };

    const handleCreate = async () => {
        if (!newRate.currency_to || !newRate.rate) return;
        try {
            await createExchangeRate({ 
                ...newRate, 
                rate: parseFloat(newRate.rate),
                tolerance_percent: parseFloat(newRate.tolerance_percent || '10.0')
            });
            setShowCreate(false); setNewRate({ currency_from: 'USD', currency_to: '', rate: '', tolerance_percent: '10.0' }); load();
        } catch (e) { alert("Error al crear tasa."); }
    };

    const handleDeleteRate = async (id) => {
        if (window.confirm("¿Eliminar esta tasa?")) {
            setRates(prev => prev.filter(r => r.id !== id));
            try { await deleteExchangeRate(id); load(); } catch (e) { alert("Error al borrar."); load(); }
        }
    };

    const usedCurrencies = [...new Set(bases.map(b => b.currency_code))].filter(c => c !== 'USD');

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Tasas de Cambio y Tolerancia (Base: USD)</h5>
                <Button size="sm" variant="success" onClick={() => setShowCreate(true)}>+ Nueva Tasa</Button>
            </div>
            <Row>
                <Col md={8}>
                    <Table hover bordered size="sm" className="align-middle">
                        <thead className="bg-light"><tr><th>Par</th><th>Tasa Actual</th><th>Tolerancia</th><th>Última Act.</th><th>Acciones</th></tr></thead>
                        <tbody>
                            {rates.map(r => (
                                <tr key={r.id}>
                                    <td className="fw-bold">
                                        {editRate?.id === r.id ? (
                                            <div className="d-flex align-items-center gap-1"><span>USD ➔</span><Form.Control size="sm" style={{width:'70px'}} value={newTo} onChange={e => setNewTo(e.target.value.toUpperCase())} /></div>
                                        ) : `${r.currency_from} ➔ ${r.currency_to}`}
                                    </td>
                                    <td>
                                        {editRate?.id === r.id ? (<Form.Control size="sm" type="number" step="0.0001" value={newValue} onChange={e => setNewValue(e.target.value)} />) : <span className="fs-5">{r.rate}</span>}
                                    </td>
                                    <td>
                                        {editRate?.id === r.id ? (
                                            <div className="d-flex align-items-center gap-1"><span className="text-muted">±</span><Form.Control size="sm" type="number" step="0.1" style={{width:'80px'}} value={newTolerance} onChange={e => setNewTolerance(e.target.value)} /><span>%</span></div>
                                        ) : <span>±{r.tolerance_percent || 10.0}%</span>}
                                    </td>
                                    <td className="small text-muted">{new Date(r.updated_at).toLocaleString()}</td>
                                    <td>
                                        {editRate?.id === r.id ? (
                                            <div className="d-flex gap-1"><Button size="sm" variant="success" onClick={handleUpdate}>💾</Button><Button size="sm" variant="secondary" onClick={() => setEditRate(null)}>✕</Button></div>
                                        ) : (
                                            <div className="d-flex gap-1"><Button size="sm" variant="outline-primary" onClick={() => { setEditRate(r); setNewValue(r.rate); setNewTo(r.currency_to); setNewTolerance(r.tolerance_percent || 10.0); }}>✏️</Button><Button size="sm" variant="outline-danger" onClick={() => handleDeleteRate(r.id)}>🗑️</Button></div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
                <Col md={4}><Card className="bg-light border-0"><Card.Body><h6>En Uso en Bases</h6><div className="d-flex gap-2 mt-2 flex-wrap">{usedCurrencies.map(c => <Badge key={c} bg="secondary" className="p-2">{c}</Badge>)}</div></Card.Body></Card></Col>
            </Row>
            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton><Modal.Title>Nueva Divisa</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3"><Form.Label>Hacia (Ej: CLP, ARS)</Form.Label><Form.Control value={newRate.currency_to} onChange={e => setNewRate({...newRate, currency_to: e.target.value.toUpperCase()})} /></Form.Group>
                    <Form.Group className="mb-3"><Form.Label>Tasa (1 USD = ?)</Form.Label><Form.Control type="number" step="0.0001" value={newRate.rate} onChange={e => setNewRate({...newRate, rate: e.target.value})} /></Form.Group>
                    <Form.Group className="mb-3"><Form.Label>Tolerancia de Variación (%)</Form.Label><Form.Control type="number" step="0.1" value={newRate.tolerance_percent} onChange={e => setNewRate({...newRate, tolerance_percent: e.target.value})} placeholder="10.0" /></Form.Group>
                </Modal.Body>
                <Modal.Footer><Button variant="primary" onClick={handleCreate}>Crear</Button></Modal.Footer>
            </Modal>
        </div>
    );
};

// --- MANTENEDOR / GENERADOR DE RUTAS EN $0 PARA PROVEEDORES ---
const ProviderZeroRoutesModal = ({ show, onHide, provider, bases = [], allProviders = [] }) => {
    const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
    const [clients, setClients] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [zones, setZones] = useState([]);
    const [zoneGroups, setZoneGroups] = useState([]);
    const [baseTariffs, setBaseTariffs] = useState([]);
    const [providerTariffs, setProviderTariffs] = useState([]);
    const [isLoadingBaseTariffs, setIsLoadingBaseTariffs] = useState(false);

    // 1. Clientes: 'ALL' (Todos / Global) vs 'SPECIFIC' (Uno o varios)
    const [clientMode, setClientMode] = useState('ALL');
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [clientSearch, setClientSearch] = useState('');

    // 2. Tipos de vehículo
    const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);

    // 3. Sucursal y modo de selección de rutas ('copy' | 'matrix')
    const [selectedBaseId, setSelectedBaseId] = useState('');
    const [builderMode, setBuilderMode] = useState('copy');
    const [referenceProviderFilter, setReferenceProviderFilter] = useState('ALL');
    const [copyRouteSearch, setCopyRouteSearch] = useState('');
    const [selectedRouteKeys, setSelectedRouteKeys] = useState([]);
    const [isBidirectionalCopy, setIsBidirectionalCopy] = useState(false);

    // Modo B: Matriz por Zonas / Grupos
    const [originSearch, setOriginSearch] = useState('');
    const [destSearch, setDestSearch] = useState('');
    const [selectedOriginKeys, setSelectedOriginKeys] = useState([]); // ej: 'zone:12' o 'group:4'
    const [selectedDestKeys, setSelectedDestKeys] = useState([]);
    const [isBidirectionalMatrix, setIsBidirectionalMatrix] = useState(true);
    const [allowSameZoneMatrix, setAllowSameZoneMatrix] = useState(false);

    // Estado de validación y envío
    const [validationError, setValidationError] = useState('');
    const [successBanner, setSuccessBanner] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const providerBaseIds = useMemo(() => {
        if (!provider) return [];
        if (Array.isArray(provider.bases) && provider.bases.length > 0) {
            return provider.bases.map(b => b.id);
        }
        return provider.base_id ? [provider.base_id] : [];
    }, [provider]);

    // Cargar catálogos al abrir el modal para un proveedor
    useEffect(() => {
        if (!show || !provider) return;
        let cancelled = false;

        setValidationError('');
        setSuccessBanner('');
        setClientMode('ALL');
        setSelectedClientIds([]);
        setClientSearch('');
        setCopyRouteSearch('');
        setOriginSearch('');
        setDestSearch('');
        setSelectedRouteKeys([]);
        setSelectedOriginKeys([]);
        setSelectedDestKeys([]);
        setReferenceProviderFilter('ALL');
        setIsBidirectionalCopy(false);
        setIsBidirectionalMatrix(true);
        setAllowSameZoneMatrix(false);

        const defaultBase = providerBaseIds[0] || (bases[0]?.id ?? '');
        setSelectedBaseId(defaultBase ? String(defaultBase) : '');

        const fetchCatalogs = async () => {
            setIsLoadingCatalogs(true);
            try {
                const [cList, vtList, zList, zgList, provTList] = await Promise.all([
                    getClients().catch(() => []),
                    getVehicleTypes().catch(() => []),
                    getZones().catch(() => []),
                    getZoneGroups().catch(() => []),
                    getTariffs({ provider_id: provider.id, limit: 5000 }).catch(() => [])
                ]);
                if (cancelled) return;
                const sortedClients = (cList || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setClients(sortedClients);

                const applicableVts = (vtList || []).filter(vt => !vt.provider_id || Number(vt.provider_id) === Number(provider.id));
                setVehicleTypes(applicableVts);
                // Preseleccionar tipos globales estándar + propios del proveedor
                setSelectedVehicleIds(applicableVts.map(vt => String(vt.id)));

                setZones(zList || []);
                setZoneGroups(zgList || []);
                setProviderTariffs(provTList || []);
            } finally {
                if (!cancelled) setIsLoadingCatalogs(false);
            }
        };

        fetchCatalogs();
        return () => { cancelled = true; };
    }, [show, provider, providerBaseIds, bases]);

    // Cargar tarifas existentes de la sucursal seleccionada para Modo "Copiar rutas existentes"
    useEffect(() => {
        if (!show || !selectedBaseId) {
            setBaseTariffs([]);
            return;
        }
        let cancelled = false;
        const loadBaseRoutes = async () => {
            setIsLoadingBaseTariffs(true);
            try {
                const tData = await getTariffs({ base_id: Number(selectedBaseId), limit: 5000 });
                if (!cancelled) {
                    setBaseTariffs(tData || []);
                    // Si en esta sucursal aún no existen tarifas previas, sugerir automáticamente el armador por zonas
                    if ((!tData || tData.length === 0)) {
                        setBuilderMode('matrix');
                    } else {
                        setBuilderMode('copy');
                    }
                }
            } catch (err) {
                if (!cancelled) setBaseTariffs([]);
            } finally {
                if (!cancelled) setIsLoadingBaseTariffs(false);
            }
        };
        loadBaseRoutes();
        setSelectedRouteKeys([]);
        setSelectedOriginKeys([]);
        setSelectedDestKeys([]);
        return () => { cancelled = true; };
    }, [show, selectedBaseId]);

    const providerExistingRouteSet = useMemo(() => {
        const s = new Set();
        (providerTariffs || []).forEach(t => {
            const key = `${t.origin_zone_id || 0}|${t.destination_zone_id || 0}|${t.origin_group_id || 0}|${t.destination_group_id || 0}`;
            s.add(key);
        });
        return s;
    }, [providerTariffs]);

    const providerTariffStats = useMemo(() => {
        const total = (providerTariffs || []).length;
        const zeroCount = (providerTariffs || []).filter(t => !t.cost_local || Number(t.cost_local) <= 0).length;
        const pricedCount = total - zeroCount;
        return { total, zeroCount, pricedCount };
    }, [providerTariffs]);

    // Clientes filtrados por búsqueda
    const filteredClients = useMemo(() => {
        const q = clientSearch.toLowerCase().trim();
        if (!q) return clients;
        return clients.filter(c => (c.name || '').toLowerCase().includes(q) || String(c.id) === q);
    }, [clients, clientSearch]);

    const bookingClient = useMemo(() => {
        return clients.find(c => Number(c.id) === 1 || (c.name || '').toLowerCase().includes('booking'));
    }, [clients]);

    // Proveedores presentes en las tarifas de la sucursal seleccionada (para filtro de referencia)
    const referenceProvidersInBase = useMemo(() => {
        const ids = new Set();
        let hasInternal = false;
        (baseTariffs || []).forEach(t => {
            if (t.provider_id) {
                if (Number(t.provider_id) !== Number(provider?.id)) {
                    ids.add(Number(t.provider_id));
                }
            } else {
                hasInternal = true;
            }
        });
        const list = Array.from(ids).map(pid => {
            const found = allProviders.find(p => Number(p.id) === pid);
            return { id: pid, name: found?.name || `Proveedor #${pid}` };
        }).sort((a, b) => a.name.localeCompare(b.name));
        return { hasInternal, list };
    }, [baseTariffs, allProviders, provider]);

    // Rutas únicas deduplicadas de la sucursal en Modo A
    const uniqueBaseRoutes = useMemo(() => {
        const map = new Map();
        (baseTariffs || []).forEach(t => {
            if (referenceProviderFilter === 'INTERNAL' && t.provider_id) return;
            if (referenceProviderFilter !== 'ALL' && referenceProviderFilter !== 'INTERNAL') {
                if (String(t.provider_id) !== String(referenceProviderFilter)) return;
            }
            const ozId = t.origin_zone_id || null;
            const dzId = t.destination_zone_id || null;
            const ogId = t.origin_group_id || null;
            const dgId = t.destination_group_id || null;
            if ((!ozId && !ogId) || (!dzId && !dgId)) return;

            const key = `${ozId || 0}|${dzId || 0}|${ogId || 0}|${dgId || 0}`;
            if (!map.has(key)) {
                const originLabel = t.origin_zone?.name || t.origin_group?.name || (ozId ? `Zona #${ozId}` : `Grupo #${ogId}`);
                const destLabel = t.destination_zone?.name || t.destination_group?.name || (dzId ? `Zona #${dzId}` : `Grupo #${dgId}`);
                map.set(key, {
                    key,
                    origin_zone_id: ozId,
                    destination_zone_id: dzId,
                    origin_group_id: ogId,
                    destination_group_id: dgId,
                    originLabel,
                    destLabel,
                    originIsGroup: Boolean(ogId),
                    destIsGroup: Boolean(dgId),
                    alreadyOnProvider: providerExistingRouteSet.has(key)
                });
            }
        });
        return Array.from(map.values()).sort((a, b) => {
            const cmp = a.originLabel.localeCompare(b.originLabel);
            return cmp !== 0 ? cmp : a.destLabel.localeCompare(b.destLabel);
        });
    }, [baseTariffs, referenceProviderFilter, providerExistingRouteSet]);

    const filteredExistingRoutes = useMemo(() => {
        const q = copyRouteSearch.toLowerCase().trim();
        if (!q) return uniqueBaseRoutes;
        return uniqueBaseRoutes.filter(r =>
            r.originLabel.toLowerCase().includes(q) ||
            r.destLabel.toLowerCase().includes(q) ||
            `${r.originLabel} ${r.destLabel}`.toLowerCase().includes(q)
        );
    }, [uniqueBaseRoutes, copyRouteSearch]);

    // Nodos geográficos (Grupos + Zonas) de la sucursal seleccionada para Modo B
    const geoLocationsInBase = useMemo(() => {
        if (!selectedBaseId) return [];
        const bId = Number(selectedBaseId);
        const groups = (zoneGroups || [])
            .filter(g => Number(g.base_id) === bId && g.is_active !== false)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(g => ({
                key: `group:${g.id}`,
                type: 'group',
                id: g.id,
                name: g.name,
                badge: 'Grupo'
            }));
        const zItems = (zones || [])
            .filter(z => Number(z.base_id) === bId && z.is_active !== false)
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(z => ({
                key: `zone:${z.id}`,
                type: 'zone',
                id: z.id,
                name: z.name,
                badge: 'Zona'
            }));
        return [...groups, ...zItems];
    }, [selectedBaseId, zoneGroups, zones]);

    const filteredOrigins = useMemo(() => {
        const q = originSearch.toLowerCase().trim();
        if (!q) return geoLocationsInBase;
        return geoLocationsInBase.filter(loc => (loc.name || '').toLowerCase().includes(q));
    }, [geoLocationsInBase, originSearch]);

    const filteredDestinations = useMemo(() => {
        const q = destSearch.toLowerCase().trim();
        if (!q) return geoLocationsInBase;
        return geoLocationsInBase.filter(loc => (loc.name || '').toLowerCase().includes(q));
    }, [geoLocationsInBase, destSearch]);

    // Construir lista final de pares de ruta según el modo activo
    const computedRoutesPayload = useMemo(() => {
        const pairsMap = new Map();
        const addPair = (oz, dz, og, dg) => {
            if ((!oz && !og) || (!dz && !dg)) return;
            const k = `${oz || 0}|${dz || 0}|${og || 0}|${dg || 0}`;
            if (!pairsMap.has(k)) {
                pairsMap.set(k, {
                    origin_zone_id: oz || null,
                    destination_zone_id: dz || null,
                    origin_group_id: og || null,
                    destination_group_id: dg || null
                });
            }
        };

        if (builderMode === 'copy') {
            const selectedSet = new Set(selectedRouteKeys);
            uniqueBaseRoutes.forEach(r => {
                if (!selectedSet.has(r.key)) return;
                addPair(r.origin_zone_id, r.destination_zone_id, r.origin_group_id, r.destination_group_id);
                if (isBidirectionalCopy) {
                    addPair(r.destination_zone_id, r.origin_zone_id, r.destination_group_id, r.origin_group_id);
                }
            });
        } else {
            const parseLoc = (locKey) => {
                const [kind, rawId] = String(locKey).split(':');
                const numId = parseInt(rawId, 10);
                return {
                    zoneId: kind === 'zone' ? numId : null,
                    groupId: kind === 'group' ? numId : null
                };
            };
            selectedOriginKeys.forEach(oKey => {
                selectedDestKeys.forEach(dKey => {
                    if (!allowSameZoneMatrix && oKey === dKey) return;
                    const orig = parseLoc(oKey);
                    const dest = parseLoc(dKey);
                    addPair(orig.zoneId, dest.zoneId, orig.groupId, dest.groupId);
                    if (isBidirectionalMatrix && oKey !== dKey) {
                        addPair(dest.zoneId, orig.zoneId, dest.groupId, orig.groupId);
                    }
                });
            });
        }

        return Array.from(pairsMap.values());
    }, [
        builderMode,
        selectedRouteKeys,
        uniqueBaseRoutes,
        isBidirectionalCopy,
        selectedOriginKeys,
        selectedDestKeys,
        allowSameZoneMatrix,
        isBidirectionalMatrix
    ]);

    const totalPotentialTariffs = computedRoutesPayload.length * selectedVehicleIds.length;

    const clientSummaryLabel = useMemo(() => {
        if (clientMode === 'ALL') return 'Todos los clientes (Global)';
        if (selectedClientIds.length === 0) return 'Ningún cliente seleccionado';
        if (selectedClientIds.length === 1) {
            return clients.find(c => String(c.id) === String(selectedClientIds[0]))?.name || `Cliente #${selectedClientIds[0]}`;
        }
        return `${selectedClientIds.length} clientes seleccionados`;
    }, [clientMode, selectedClientIds, clients]);

    const toggleArrayItem = (arr, itemStr, setter) => {
        setValidationError('');
        setSuccessBanner('');
        if (arr.includes(itemStr)) {
            setter(arr.filter(x => x !== itemStr));
        } else {
            setter([...arr, itemStr]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidationError('');
        setSuccessBanner('');

        if (!provider?.id) return;

        if (clientMode === 'SPECIFIC' && selectedClientIds.length === 0) {
            setValidationError('Debes marcar al menos un cliente o cambiar a "Todos los clientes (Global)".');
            return;
        }
        if (selectedVehicleIds.length === 0) {
            setValidationError('Debes seleccionar al menos un tipo de vehículo.');
            return;
        }
        if (computedRoutesPayload.length === 0) {
            setValidationError(
                builderMode === 'copy'
                    ? 'Debes marcar al menos una ruta de la lista para crearla en $0.'
                    : 'Debes seleccionar al menos un Origen y un Destino válido para armar las rutas en $0.'
            );
            return;
        }

        setIsSubmitting(true);
        try {
            const clientIdsPayload = clientMode === 'ALL'
                ? null
                : selectedClientIds.map(id => String(id).trim()).filter(Boolean).join(',');

            const res = await seedProviderZeroTariffs(provider.id, {
                routes: computedRoutesPayload,
                vehicle_type_ids: selectedVehicleIds.map(id => parseInt(id, 10)),
                client_ids: clientIdsPayload,
                is_bidirectional: false, // Ya expandimos espejo en computedRoutesPayload para conteo exacto
                min_lead_time_minutes: 0
            });

            // Refrescar tarifas actuales del proveedor para actualizar contadores e indicadores
            const refreshed = await getTariffs({ provider_id: provider.id, limit: 5000 }).catch(() => null);
            if (refreshed) setProviderTariffs(refreshed);

            const skippedNote = res.skipped_count > 0 ? ` (${res.skipped_count} ya existían y se omitieron sin duplicar)` : '';
            setSuccessBanner(`✅ Se crearon ${res.created_count} tarifas en $0 para ${provider.name}${skippedNote}. Ya están visibles en su Portal de Proveedor.`);
            setSelectedRouteKeys([]);
            setSelectedOriginKeys([]);
            setSelectedDestKeys([]);
        } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'No se pudieron generar las rutas en $0.';
            setValidationError(detail);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!provider) return null;

    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop="static">
            <form method="post" action={`/transport/providers/${provider.id}/seed-zero-tariffs`} onSubmit={handleSubmit}>
                <Modal.Header closeButton className="bg-light">
                    <div className="d-flex flex-column">
                        <Modal.Title className="h6 fw-bold mb-1">
                            🗺️ Mantenedor de Rutas en $0 — {provider.name}
                        </Modal.Title>
                        <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
                            <span>Habilita rutas en costo <strong>$0</strong> para que el proveedor cargue sus valores desde su portal.</span>
                            <Badge bg="secondary" className="fw-normal">
                                Actuales: {providerTariffStats.total} ({providerTariffStats.zeroCount} en $0 · {providerTariffStats.pricedCount} con precio)
                            </Badge>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body style={{ maxHeight: '76vh', overflowY: 'auto' }}>
                    {validationError && (
                        <div role="alert" aria-live="assertive" className="alert alert-danger py-2 px-3 small fw-semibold mb-3">
                            ⚠️ {validationError}
                        </div>
                    )}
                    {successBanner && (
                        <div role="status" aria-live="polite" className="alert alert-success py-2 px-3 small fw-semibold mb-3 d-flex justify-content-between align-items-center gap-2">
                            <span>{successBanner}</span>
                            <Button type="button" size="sm" variant="outline-success" onClick={onHide}>
                                Cerrar ventana
                            </Button>
                        </div>
                    )}

                    {isLoadingCatalogs ? (
                        <div className="text-center py-5 text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Cargando clientes, vehículos y zonas...
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {/* PASO 1: CLIENTES / CONVENIOS (UNO, VARIOS O TODOS) */}
                            <fieldset className="border rounded p-3 bg-white">
                                <legend className="float-none w-auto px-2 h6 fw-bold text-primary mb-2" style={{ fontSize: '13px' }}>
                                    1. Clientes / Convenios habilitados ({clientSummaryLabel})
                                </legend>
                                <div className="d-flex flex-wrap gap-3 mb-2">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="client_scope_mode"
                                            id="zero-client-mode-all"
                                            value="ALL"
                                            checked={clientMode === 'ALL'}
                                            onChange={() => {
                                                setClientMode('ALL');
                                                setValidationError('');
                                            }}
                                        />
                                        <label className="form-check-label small fw-bold" htmlFor="zero-client-mode-all">
                                            🌐 Todos los clientes (Tarifa Global)
                                        </label>
                                    </div>
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="client_scope_mode"
                                            id="zero-client-mode-specific"
                                            value="SPECIFIC"
                                            checked={clientMode === 'SPECIFIC'}
                                            onChange={() => {
                                                setClientMode('SPECIFIC');
                                                setValidationError('');
                                            }}
                                        />
                                        <label className="form-check-label small fw-bold" htmlFor="zero-client-mode-specific">
                                            🎯 Elegir uno o varios clientes específicos
                                        </label>
                                    </div>
                                </div>

                                {clientMode === 'SPECIFIC' && (
                                    <div className="mt-2 pt-2 border-top">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                            <div className="flex-grow-1" style={{ maxWidth: '340px' }}>
                                                <label htmlFor="zero-client-search" className="form-label small fw-semibold mb-1">
                                                    Buscar cliente o convenio
                                                </label>
                                                <input
                                                    type="text"
                                                    id="zero-client-search"
                                                    name="client_search"
                                                    className="form-control form-control-sm"
                                                    placeholder="Ej: Booking, Travel Mice, Minera..."
                                                    value={clientSearch}
                                                    onChange={e => setClientSearch(e.target.value)}
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                />
                                            </div>
                                            <div className="d-flex flex-wrap gap-1 align-self-end">
                                                {bookingClient && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={selectedClientIds.length === 1 && selectedClientIds[0] === String(bookingClient.id) ? 'primary' : 'outline-primary'}
                                                        onClick={() => {
                                                            setValidationError('');
                                                            setSelectedClientIds([String(bookingClient.id)]);
                                                        }}
                                                    >
                                                        ⚡ Solo {bookingClient.name}
                                                    </Button>
                                                )}
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    onClick={() => {
                                                        setValidationError('');
                                                        const merged = Array.from(new Set([
                                                            ...selectedClientIds,
                                                            ...filteredClients.map(c => String(c.id))
                                                        ]));
                                                        setSelectedClientIds(merged);
                                                    }}
                                                >
                                                    ✓ Marcar visibles ({filteredClients.length})
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => setSelectedClientIds([])}
                                                >
                                                    ✕ Limpiar
                                                </Button>
                                            </div>
                                        </div>

                                        <div
                                            className="row g-1 border rounded p-2 bg-light"
                                            style={{ maxHeight: '165px', overflowY: 'auto', margin: 0 }}
                                        >
                                            {filteredClients.map(c => {
                                                const cid = String(c.id);
                                                const isChecked = selectedClientIds.includes(cid);
                                                const inputId = `zero-client-chk-${cid}`;
                                                return (
                                                    <div key={cid} className="col-12 col-sm-6 col-md-4">
                                                        <div className={`form-check px-2 py-1 rounded ${isChecked ? 'bg-primary bg-opacity-10 fw-semibold' : ''}`} style={{ paddingLeft: '1.8rem' }}>
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="selected_clients"
                                                                id={inputId}
                                                                checked={isChecked}
                                                                onChange={() => toggleArrayItem(selectedClientIds, cid, setSelectedClientIds)}
                                                            />
                                                            <label className="form-check-label small text-truncate d-block" htmlFor={inputId} title={c.name}>
                                                                {c.name}
                                                            </label>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {filteredClients.length === 0 && (
                                                <div className="col-12 text-center text-muted small py-2">
                                                    No hay clientes que coincidan con "{clientSearch}".
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </fieldset>

                            {/* PASO 2: TIPOS DE VEHÍCULO */}
                            <fieldset className="border rounded p-3 bg-white">
                                <legend className="float-none w-auto px-2 h6 fw-bold text-primary mb-2" style={{ fontSize: '13px' }}>
                                    2. Tipos de Vehículo ({selectedVehicleIds.length} seleccionados)
                                </legend>
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                    <span className="text-muted small">
                                        Se creará una fila en $0 por cada tipo de vehículo marcado.
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => {
                                                setValidationError('');
                                                setSelectedVehicleIds(vehicleTypes.map(vt => String(vt.id)));
                                            }}
                                        >
                                            ✓ Todos
                                        </Button>
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => setSelectedVehicleIds([])}
                                        >
                                            ✕ Ninguno
                                        </Button>
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {vehicleTypes.map(vt => {
                                        const vid = String(vt.id);
                                        const checked = selectedVehicleIds.includes(vid);
                                        const inputId = `zero-vt-chk-${vid}`;
                                        return (
                                            <div
                                                key={vid}
                                                className={`form-check border rounded px-3 py-1 ${checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-light'}`}
                                                style={{ paddingLeft: '2rem' }}
                                            >
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="selected_vehicle_types"
                                                    id={inputId}
                                                    checked={checked}
                                                    onChange={() => toggleArrayItem(selectedVehicleIds, vid, setSelectedVehicleIds)}
                                                />
                                                <label className="form-check-label small fw-semibold" htmlFor={inputId}>
                                                    🚗 {vt.name} <span className="text-muted fw-normal">({vt.capacity || 4} pax{vt.provider_id ? ' · Propio' : ''})</span>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            </fieldset>

                            {/* PASO 3: SELECCIÓN DE RUTAS */}
                            <fieldset className="border rounded p-3 bg-white">
                                <legend className="float-none w-auto px-2 h6 fw-bold text-primary mb-2" style={{ fontSize: '13px' }}>
                                    3. Rutas a habilitar en $0 ({computedRoutesPayload.length} rutas resultantes)
                                </legend>

                                <Row className="g-3 align-items-end mb-3">
                                    <Col md={4}>
                                        <label htmlFor="zero-base-select" className="form-label small fw-bold mb-1">
                                            Sucursal / Base Operativa
                                        </label>
                                        <select
                                            id="zero-base-select"
                                            name="base_id"
                                            className="form-select form-select-sm"
                                            value={selectedBaseId}
                                            onChange={e => {
                                                setSelectedBaseId(e.target.value);
                                                setValidationError('');
                                            }}
                                        >
                                            <option value="">Seleccione una sucursal...</option>
                                            {bases.map(b => {
                                                const isAssigned = providerBaseIds.includes(b.id);
                                                return (
                                                    <option key={b.id} value={b.id}>
                                                        {isAssigned ? `⭐ ${b.name} (Asociada al proveedor)` : b.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </Col>
                                    <Col md={8}>
                                        <div className="d-flex flex-wrap gap-3 pt-1">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="route_builder_mode"
                                                    id="zero-mode-copy"
                                                    value="copy"
                                                    checked={builderMode === 'copy'}
                                                    onChange={() => {
                                                        setBuilderMode('copy');
                                                        setValidationError('');
                                                    }}
                                                />
                                                <label className="form-check-label small fw-bold" htmlFor="zero-mode-copy">
                                                    📋 Copiar rutas ya existentes en la sucursal ({uniqueBaseRoutes.length})
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="route_builder_mode"
                                                    id="zero-mode-matrix"
                                                    value="matrix"
                                                    checked={builderMode === 'matrix'}
                                                    onChange={() => {
                                                        setBuilderMode('matrix');
                                                        setValidationError('');
                                                    }}
                                                />
                                                <label className="form-check-label small fw-bold" htmlFor="zero-mode-matrix">
                                                    🗺️ Armar rutas nuevas por Zonas / Grupos ({geoLocationsInBase.length} nodos)
                                                </label>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {builderMode === 'copy' ? (
                                    <div>
                                        <Row className="g-2 align-items-end mb-2">
                                            <Col md={4}>
                                                <label htmlFor="zero-ref-provider" className="form-label small fw-semibold mb-1">
                                                    Filtrar rutas modelo por origen
                                                </label>
                                                <select
                                                    id="zero-ref-provider"
                                                    name="ref_provider_id"
                                                    className="form-select form-select-sm"
                                                    value={referenceProviderFilter}
                                                    onChange={e => setReferenceProviderFilter(e.target.value)}
                                                >
                                                    <option value="ALL">Todas las rutas de esta sucursal</option>
                                                    {referenceProvidersInBase.hasInternal && (
                                                        <option value="INTERNAL">Tarifas propias EmiGo (Sin proveedor)</option>
                                                    )}
                                                    {referenceProvidersInBase.list.map(rp => (
                                                        <option key={rp.id} value={rp.id}>
                                                            Rutas de: {rp.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </Col>
                                            <Col md={4}>
                                                <label htmlFor="zero-copy-route-search" className="form-label small fw-semibold mb-1">
                                                    Buscar ruta (origen o destino)
                                                </label>
                                                <input
                                                    type="text"
                                                    id="zero-copy-route-search"
                                                    name="copy_route_search"
                                                    className="form-control form-control-sm"
                                                    placeholder="Ej: Aeropuerto, Hotel, Centro..."
                                                    value={copyRouteSearch}
                                                    onChange={e => setCopyRouteSearch(e.target.value)}
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                />
                                            </Col>
                                            <Col md={4} className="d-flex flex-wrap gap-1 justify-content-md-end">
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => {
                                                        setValidationError('');
                                                        const merged = Array.from(new Set([
                                                            ...selectedRouteKeys,
                                                            ...filteredExistingRoutes.map(r => r.key)
                                                        ]));
                                                        setSelectedRouteKeys(merged);
                                                    }}
                                                >
                                                    ✓ Seleccionar visibles ({filteredExistingRoutes.length})
                                                </Button>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline-danger"
                                                    onClick={() => setSelectedRouteKeys([])}
                                                >
                                                    ✕ Limpiar
                                                </Button>
                                            </Col>
                                        </Row>

                                        {isLoadingBaseTariffs ? (
                                            <div className="text-center py-4 text-muted small">
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Buscando rutas existentes en la sucursal...
                                            </div>
                                        ) : filteredExistingRoutes.length === 0 ? (
                                            <div className="text-center py-4 bg-light border rounded text-muted small">
                                                No se encontraron rutas previas con este filtro. Puedes cambiar a <strong>"🗺️ Armar rutas nuevas por Zonas / Grupos"</strong> arriba.
                                            </div>
                                        ) : (
                                            <div className="border rounded p-2 bg-light" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                                                <div className="row g-1" style={{ margin: 0 }}>
                                                    {filteredExistingRoutes.map((r, idx) => {
                                                        const isChecked = selectedRouteKeys.includes(r.key);
                                                        const inputId = `zero-route-copy-${idx}`;
                                                        return (
                                                            <div key={r.key} className="col-12 col-md-6">
                                                                <div
                                                                    className={`form-check px-2 py-1 rounded d-flex align-items-center justify-content-between gap-2 ${isChecked ? 'bg-primary bg-opacity-10 fw-semibold' : 'bg-white border'}`}
                                                                    style={{ paddingLeft: '1.9rem' }}
                                                                >
                                                                    <div>
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            name="selected_existing_routes"
                                                                            id={inputId}
                                                                            checked={isChecked}
                                                                            onChange={() => toggleArrayItem(selectedRouteKeys, r.key, setSelectedRouteKeys)}
                                                                        />
                                                                        <label className="form-check-label small" htmlFor={inputId}>
                                                                            {r.originIsGroup ? '📁 ' : ''}{r.originLabel} <span className="text-muted">➔</span> {r.destIsGroup ? '📁 ' : ''}{r.destLabel}
                                                                        </label>
                                                                    </div>
                                                                    {r.alreadyOnProvider && (
                                                                        <Badge bg="secondary" className="fw-normal flex-shrink-0" style={{ fontSize: '10px' }}>
                                                                            ✓ Ya existe
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="form-check mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="bidirectional_copy"
                                                id="zero-bidirectional-copy"
                                                checked={isBidirectionalCopy}
                                                onChange={e => setIsBidirectionalCopy(e.target.checked)}
                                            />
                                            <label className="form-check-label small fw-semibold" htmlFor="zero-bidirectional-copy">
                                                🔄 Asegurar también las rutas de regreso (Ida y Vuelta automático)
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <Row className="g-3">
                                            {/* COLUMNA ORÍGENES */}
                                            <Col md={6}>
                                                <div className="d-flex justify-content-between align-items-end mb-1">
                                                    <label htmlFor="zero-origin-search" className="form-label small fw-bold mb-0">
                                                        Orígenes ({selectedOriginKeys.length} marcados)
                                                    </label>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            style={{ fontSize: '11px', padding: '1px 6px' }}
                                                            onClick={() => {
                                                                setValidationError('');
                                                                setSelectedOriginKeys(Array.from(new Set([...selectedOriginKeys, ...filteredOrigins.map(o => o.key)])));
                                                            }}
                                                        >
                                                            ✓ Visibles
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline-danger"
                                                            style={{ fontSize: '11px', padding: '1px 6px' }}
                                                            onClick={() => setSelectedOriginKeys([])}
                                                        >
                                                            ✕ Limpiar
                                                        </Button>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    id="zero-origin-search"
                                                    name="origin_search"
                                                    className="form-control form-control-sm mb-2"
                                                    placeholder="Filtrar zona o grupo de origen..."
                                                    value={originSearch}
                                                    onChange={e => setOriginSearch(e.target.value)}
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                />
                                                <div className="border rounded p-2 bg-light" style={{ maxHeight: '210px', overflowY: 'auto' }}>
                                                    {filteredOrigins.map(loc => {
                                                        const checked = selectedOriginKeys.includes(loc.key);
                                                        const inputId = `zero-orig-${loc.key.replace(':', '-')}`;
                                                        return (
                                                            <div key={loc.key} className={`form-check py-1 px-2 rounded ${checked ? 'bg-primary bg-opacity-10 fw-semibold' : ''}`} style={{ paddingLeft: '1.8rem' }}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    name="matrix_origins"
                                                                    id={inputId}
                                                                    checked={checked}
                                                                    onChange={() => toggleArrayItem(selectedOriginKeys, loc.key, setSelectedOriginKeys)}
                                                                />
                                                                <label className="form-check-label small d-flex justify-content-between align-items-center" htmlFor={inputId}>
                                                                    <span>{loc.type === 'group' ? '📁 ' : '📍 '}{loc.name}</span>
                                                                    <Badge bg={loc.type === 'group' ? 'info' : 'light'} text="dark" className="border fw-normal" style={{ fontSize: '10px' }}>
                                                                        {loc.badge}
                                                                    </Badge>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                    {filteredOrigins.length === 0 && (
                                                        <div className="text-center text-muted small py-3">Sin zonas en esta sucursal.</div>
                                                    )}
                                                </div>
                                            </Col>

                                            {/* COLUMNA DESTINOS */}
                                            <Col md={6}>
                                                <div className="d-flex justify-content-between align-items-end mb-1">
                                                    <label htmlFor="zero-dest-search" className="form-label small fw-bold mb-0">
                                                        Destinos ({selectedDestKeys.length} marcados)
                                                    </label>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            style={{ fontSize: '11px', padding: '1px 6px' }}
                                                            onClick={() => {
                                                                setValidationError('');
                                                                setSelectedDestKeys(Array.from(new Set([...selectedDestKeys, ...filteredDestinations.map(d => d.key)])));
                                                            }}
                                                        >
                                                            ✓ Visibles
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline-danger"
                                                            style={{ fontSize: '11px', padding: '1px 6px' }}
                                                            onClick={() => setSelectedDestKeys([])}
                                                        >
                                                            ✕ Limpiar
                                                        </Button>
                                                    </div>
                                                </div>
                                                <input
                                                    type="text"
                                                    id="zero-dest-search"
                                                    name="dest_search"
                                                    className="form-control form-control-sm mb-2"
                                                    placeholder="Filtrar zona o grupo de destino..."
                                                    value={destSearch}
                                                    onChange={e => setDestSearch(e.target.value)}
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                />
                                                <div className="border rounded p-2 bg-light" style={{ maxHeight: '210px', overflowY: 'auto' }}>
                                                    {filteredDestinations.map(loc => {
                                                        const checked = selectedDestKeys.includes(loc.key);
                                                        const inputId = `zero-dest-${loc.key.replace(':', '-')}`;
                                                        return (
                                                            <div key={loc.key} className={`form-check py-1 px-2 rounded ${checked ? 'bg-primary bg-opacity-10 fw-semibold' : ''}`} style={{ paddingLeft: '1.8rem' }}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    name="matrix_destinations"
                                                                    id={inputId}
                                                                    checked={checked}
                                                                    onChange={() => toggleArrayItem(selectedDestKeys, loc.key, setSelectedDestKeys)}
                                                                />
                                                                <label className="form-check-label small d-flex justify-content-between align-items-center" htmlFor={inputId}>
                                                                    <span>{loc.type === 'group' ? '📁 ' : '📍 '}{loc.name}</span>
                                                                    <Badge bg={loc.type === 'group' ? 'info' : 'light'} text="dark" className="border fw-normal" style={{ fontSize: '10px' }}>
                                                                        {loc.badge}
                                                                    </Badge>
                                                                </label>
                                                            </div>
                                                        );
                                                    })}
                                                    {filteredDestinations.length === 0 && (
                                                        <div className="text-center text-muted small py-3">Sin zonas en esta sucursal.</div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>

                                        <div className="d-flex flex-wrap gap-4 mt-2">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="bidirectional_matrix"
                                                    id="zero-bidirectional-matrix"
                                                    checked={isBidirectionalMatrix}
                                                    onChange={e => setIsBidirectionalMatrix(e.target.checked)}
                                                />
                                                <label className="form-check-label small fw-semibold" htmlFor="zero-bidirectional-matrix">
                                                    🔄 Crear Ida y Vuelta automático (Espejo)
                                                </label>
                                            </div>
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="allow_same_zone_matrix"
                                                    id="zero-same-zone-matrix"
                                                    checked={allowSameZoneMatrix}
                                                    onChange={e => setAllowSameZoneMatrix(e.target.checked)}
                                                />
                                                <label className="form-check-label small text-muted" htmlFor="zero-same-zone-matrix">
                                                    Permitir rutas internas (mismo Origen y Destino)
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </fieldset>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer className="bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="small text-dark">
                        <strong>Resumen:</strong>{' '}
                        <span className="text-primary fw-bold">{computedRoutesPayload.length} rutas</span> ×{' '}
                        <span className="text-primary fw-bold">{selectedVehicleIds.length} vehículos</span> = hasta{' '}
                        <Badge bg="primary">{totalPotentialTariffs} tarifas en $0</Badge>{' '}
                        <span className="text-muted">· Clientes: <strong>{clientSummaryLabel}</strong></span>
                    </div>
                    <div className="d-flex gap-2">
                        <Button type="button" variant="secondary" size="sm" onClick={onHide} disabled={isSubmitting}>
                            Cerrar
                        </Button>
                        <button
                            type="submit"
                            className="btn btn-success btn-sm fw-bold px-3"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Generando tarifas en $0...' : `🗺️ Crear Tarifas en $0 (${totalPotentialTariffs})`}
                        </button>
                    </div>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

// --- PROVIDERS MANAGER (NUEVO) ---
const ProvidersManager = () => {
    const [providers, setProviders] = useState([]);
    const [bases, setBases] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [settlementGroups, setSettlementGroups] = useState([]);
    const [drivers, setDrivers] = useState([]);

    const [editProvider, setEditProvider] = useState(null); 
    const [editName, setEditName] = useState('');
    const [editBaseIds, setEditBaseIds] = useState([]);
    const [editIsActive, setEditIsActive] = useState(true);
    const [editMobileRangeStart, setEditMobileRangeStart] = useState('');
    const [editMobileRangeEnd, setEditMobileRangeEnd] = useState('');
    const [editContractId, setEditContractId] = useState('');
    const [editSettlementGroupId, setEditSettlementGroupId] = useState('');
    const [editOwnerId, setEditOwnerId] = useState('');
    const [editCurrencyCode, setEditCurrencyCode] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editBookingBrandName, setEditBookingBrandName] = useState('');
    const [editHybridSettlement, setEditHybridSettlement] = useState(false);
    const [editAllowAsapDispatch, setEditAllowAsapDispatch] = useState(false);

    // Estados para Mantenedor de Rutas en $0
    const [zeroRoutesProvider, setZeroRoutesProvider] = useState(null);
    const [openZeroRoutesAfterCreate, setOpenZeroRoutesAfterCreate] = useState(true);

    // Estados para Acuerdo Comercial de Cancelación
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [selectedAgreementProvider, setSelectedAgreementProvider] = useState(null);
    const [agreementPolicy, setAgreementPolicy] = useState('custom');
    const [agreementHoursFree, setAgreementHoursFree] = useState('12.0');
    const [agreementHoursHalf, setAgreementHoursHalf] = useState('3.0');
    const [isSavingAgreement, setIsSavingAgreement] = useState(false);

    const handleOpenAgreement = (p) => {
        setSelectedAgreementProvider(p);
        setAgreementPolicy(p.cancellation_policy || 'custom');
        setAgreementHoursFree(p.cancellation_hours_free !== null && p.cancellation_hours_free !== undefined ? String(p.cancellation_hours_free) : '12.0');
        setAgreementHoursHalf(p.cancellation_hours_half !== null && p.cancellation_hours_half !== undefined ? String(p.cancellation_hours_half) : '3.0');
        setShowAgreementModal(true);
    };

    const handleSaveAgreement = async () => {
        if (!selectedAgreementProvider) return;
        setIsSavingAgreement(true);
        try {
            await updateProvider(selectedAgreementProvider.id, {
                cancellation_policy: agreementPolicy,
                cancellation_hours_free: agreementPolicy === 'custom' ? parseFloat(agreementHoursFree) : null,
                cancellation_hours_half: agreementPolicy === 'custom' ? parseFloat(agreementHoursHalf) : null
            });
            setShowAgreementModal(false);
            load();
        } catch (e) {
            alert("Error al guardar el acuerdo comercial de cancelación.");
        } finally {
            setIsSavingAgreement(false);
        }
    };

    const [searchQuery, setSearchQuery] = useState('');

    const filteredProviders = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return providers;
        return providers.filter(p => {
            const nameMatch = (p.name || '').toLowerCase().includes(query);
            const baseMatch = p.bases && p.bases.some(b => (b.name || '').toLowerCase().includes(query));
            const contractMatch = p.contract_id && contracts.some(c => c.id === p.contract_id && (c.name || '').toLowerCase().includes(query));
            const groupMatch = p.settlement_group_id && settlementGroups.some(g => g.id === p.settlement_group_id && (g.name || '').toLowerCase().includes(query));
            const ownerMatch = p.owner_id && drivers.some(d => d.id === p.owner_id && (d.full_name || '').toLowerCase().includes(query));
            const phoneMatch = (p.phone || '').toLowerCase().includes(query);
            const brandMatch = (p.booking_brand_name || '').toLowerCase().includes(query);
            return nameMatch || baseMatch || contractMatch || groupMatch || ownerMatch || phoneMatch || brandMatch;
        });
    }, [providers, searchQuery, contracts, settlementGroups, drivers]);

    const [showCreate, setShowCreate] = useState(false);
    const [newProvider, setNewProvider] = useState({ 
        new_provider_flag: false, // Placeholder to avoid match issues
        name: '', 
        base_ids: [], 
        is_active: true,
        mobile_range_start: '',
        mobile_range_end: '',
        contract_id: '',
        settlement_group_id: '',
        owner_id: '',
        currency_code: '',
        phone: '',
        booking_brand_name: '',
        hybrid_settlement: false,
        allow_asap_dispatch: false
    });

    const load = useCallback(async () => {
        try {
            const [pData, bData, cData, sgData, dData] = await Promise.all([
                getProviders(), 
                getBases(),
                getContracts(),
                getSettlementGroups(),
                getDrivers({ limit: 1000 })
            ]);
            setProviders(pData); 
            setBases(bData || []);
            setContracts(cData || []);
            setSettlementGroups(sgData || []);
            setDrivers(dData || []);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleUpdate = async () => {
        if (!editProvider || !editName || editBaseIds.length === 0) {
            alert("Debe ingresar un nombre y seleccionar al menos una base.");
            return;
        }
        try {
            await updateProvider(editProvider.id, { 
                name: editName, 
                base_ids: editBaseIds, 
                is_active: editIsActive,
                mobile_range_start: editMobileRangeStart ? parseInt(editMobileRangeStart) : null,
                mobile_range_end: editMobileRangeEnd ? parseInt(editMobileRangeEnd) : null,
                contract_id: editContractId ? parseInt(editContractId) : null,
                settlement_group_id: editSettlementGroupId ? parseInt(editSettlementGroupId) : null,
                owner_id: editOwnerId ? parseInt(editOwnerId) : null,
                currency_code: editCurrencyCode || null,
                phone: editPhone || null,
                booking_brand_name: editBookingBrandName || null,
                hybrid_settlement: editHybridSettlement,
                allow_asap_dispatch: editAllowAsapDispatch
            });
            setEditProvider(null); load();
        } catch (e) { alert("Error al actualizar proveedor"); }
    };

    const handleCreate = async () => {
        if (!newProvider.name || !newProvider.base_ids || newProvider.base_ids.length === 0) {
            alert("Debe ingresar un nombre y seleccionar al menos una base.");
            return;
        }
        try {
            const createdProv = await createProvider({ 
                name: newProvider.name, 
                base_ids: newProvider.base_ids, 
                is_active: newProvider.is_active,
                mobile_range_start: newProvider.mobile_range_start ? parseInt(newProvider.mobile_range_start) : null,
                mobile_range_end: newProvider.mobile_range_end ? parseInt(newProvider.mobile_range_end) : null,
                contract_id: newProvider.contract_id ? parseInt(newProvider.contract_id) : null,
                settlement_group_id: newProvider.settlement_group_id ? parseInt(newProvider.settlement_group_id) : null,
                owner_id: newProvider.owner_id ? parseInt(newProvider.owner_id) : null,
                currency_code: newProvider.currency_code || null,
                phone: newProvider.phone || null,
                booking_brand_name: newProvider.booking_brand_name || null,
                hybrid_settlement: !!newProvider.hybrid_settlement,
                allow_asap_dispatch: !!newProvider.allow_asap_dispatch
            });
            setShowCreate(false); 
            setNewProvider({ 
                name: '', 
                base_ids: [], 
                is_active: true,
                mobile_range_start: '',
                mobile_range_end: '',
                contract_id: '',
                settlement_group_id: '',
                owner_id: '',
                currency_code: '',
                phone: '',
                booking_brand_name: '',
                hybrid_settlement: false,
                allow_asap_dispatch: false
            }); 
            await load();
            if (openZeroRoutesAfterCreate && createdProv?.id) {
                setZeroRoutesProvider(createdProv);
            }
        } catch (e) { alert("Error al crear proveedor."); }
    };

    const handleDeleteProvider = async (id) => {
        if (window.confirm("¿Eliminar este proveedor? Se desvinculará de las tarifas asociadas.")) {
            try { await deleteProvider(id); load(); } catch (e) { alert("Error al borrar."); load(); }
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0">🚚 Proveedores Regionales</h5>
                <div className="d-flex gap-2">
                    <Form.Control
                        type="text"
                        placeholder="Buscar proveedor, base, contrato..."
                        size="sm"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        style={{ width: '250px' }}
                    />
                    <Button size="sm" variant="success" onClick={() => {
                        if (bases.length === 0) {
                            alert("No hay bases regionales creadas para asociar proveedores.");
                            return;
                        }
                        setNewProvider({ 
                            name: '', 
                            base_ids: [], 
                            is_active: true,
                            mobile_range_start: '',
                            mobile_range_end: '',
                            contract_id: '',
                            settlement_group_id: '',
                            owner_id: '',
                            currency_code: '',
                            allow_asap_dispatch: false
                        });
                        setShowCreate(true);
                    }}>+ Nuevo Proveedor</Button>
                </div>
            </div>
            <Row>
                <Col md={12}>
                    <Table hover bordered size="sm" className="align-middle small">
                        <thead className="bg-light">
                            <tr>
                                <th>Nombre Proveedor</th>
                                <th>Bases / Regiones</th>
                                <th>Teléfono Booking</th>
                                <th>Marca Booking</th>
                                <th>Rango Móviles</th>
                                <th>Moneda</th>
                                <th>Contrato Default</th>
                                <th>Grupo Liq. Default</th>
                                <th>Dueño Default</th>
                                <th>Acuerdo Cancelación</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProviders.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Control size="sm" value={editName} onChange={e => setEditName(e.target.value)} />
                                        ) : <span className="fw-bold">{p.name}</span>}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <div style={{ maxHeight: '100px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '4px', borderRadius: '4px', background: '#fff' }}>
                                                {bases.map(b => (
                                                    <Form.Check 
                                                        key={b.id}
                                                        type="checkbox"
                                                        id={`edit-base-${b.id}`}
                                                        label={b.name}
                                                        checked={editBaseIds.includes(b.id)}
                                                        onChange={e => {
                                                            if (e.target.checked) {
                                                                setEditBaseIds([...editBaseIds, b.id]);
                                                            } else {
                                                                setEditBaseIds(editBaseIds.filter(id => id !== b.id));
                                                            }
                                                        }}
                                                        style={{ fontSize: '11px', margin: '2px 0' }}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            p.bases && p.bases.length > 0 
                                                ? p.bases.map(b => b.name).join(', ') 
                                                : (p.base?.name || `ID ${p.base_id}`)
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Control size="sm" placeholder="+1 809..." value={editPhone} onChange={e => setEditPhone(e.target.value)} style={{ width: '130px' }} />
                                        ) : (
                                            p.phone ? <Badge bg="light" text="dark" className="border">📞 {p.phone}</Badge> : <span className="text-muted small">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Control size="sm" placeholder="Transportes Emilia" value={editBookingBrandName} onChange={e => setEditBookingBrandName(e.target.value)} style={{ width: '140px' }} />
                                        ) : (
                                            p.booking_brand_name ? <Badge bg="primary">{p.booking_brand_name}</Badge> : <span className="text-muted small">Transportes Emilia</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <div className="d-flex align-items-center gap-1" style={{ width: '130px' }}>
                                                <Form.Control size="sm" type="number" placeholder="Min" value={editMobileRangeStart} onChange={e => setEditMobileRangeStart(e.target.value)} />
                                                <span>-</span>
                                                <Form.Control size="sm" type="number" placeholder="Max" value={editMobileRangeEnd} onChange={e => setEditMobileRangeEnd(e.target.value)} />
                                            </div>
                                        ) : (
                                            p.mobile_range_start !== null && p.mobile_range_end !== null ? (
                                                <Badge bg="info" className="text-dark font-medium">
                                                    {p.mobile_range_start} - {p.mobile_range_end}
                                                </Badge>
                                            ) : <span className="text-muted small">Sin límite</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Select size="sm" value={editCurrencyCode} onChange={e => setEditCurrencyCode(e.target.value)} style={{ width: '110px' }}>
                                                <option value="">Base (Default)</option>
                                                <option value="CLP">CLP</option>
                                                <option value="USD">USD</option>
                                                <option value="PEN">PEN</option>
                                                <option value="DOP">DOP</option>
                                            </Form.Select>
                                        ) : (
                                            p.currency_code ? <Badge bg="secondary">{p.currency_code}</Badge> : <span className="text-muted small">Default (Base)</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Select size="sm" value={editContractId} onChange={e => setEditContractId(e.target.value)} style={{ width: '130px' }}>
                                                <option value="">Ninguno</option>
                                                {contracts.map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            p.contract_id ? (
                                                <Badge bg="light" className="text-dark border font-normal">
                                                    {contracts.find(c => c.id === p.contract_id)?.name || `ID ${p.contract_id}`}
                                                </Badge>
                                            ) : <span className="text-muted small">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Select size="sm" value={editSettlementGroupId} onChange={e => setEditSettlementGroupId(e.target.value)} style={{ width: '130px' }}>
                                                <option value="">Ninguno</option>
                                                {settlementGroups.map(g => (
                                                    <option key={g.id} value={g.id}>{g.name}</option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            p.settlement_group_id ? (
                                                <Badge bg="light" className="text-dark border font-normal">
                                                    {settlementGroups.find(g => g.id === p.settlement_group_id)?.name || `ID ${p.settlement_group_id}`}
                                                </Badge>
                                            ) : <span className="text-muted small">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <Form.Select size="sm" value={editOwnerId} onChange={e => setEditOwnerId(e.target.value)} style={{ width: '130px' }}>
                                                <option value="">Ninguno</option>
                                                {drivers.map(d => (
                                                    <option key={d.id} value={d.id}>{d.full_name}{d.is_owner ? ' (Dueño)' : ''}</option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            p.owner_id ? (
                                                <Badge bg="light" className="text-dark border font-normal">
                                                    {drivers.find(d => d.id === p.owner_id)?.full_name || `ID ${p.owner_id}`}
                                                </Badge>
                                            ) : <span className="text-muted small">—</span>
                                        )}
                                    </td>
                                    <td>
                                        {p.cancellation_policy === 'always_100' && <Badge bg="success">Siempre 100%</Badge>}
                                        {p.cancellation_policy === 'always_50' && <Badge bg="warning" className="text-dark">Siempre 50%</Badge>}
                                        {p.cancellation_policy === 'always_0' && <Badge bg="danger">Siempre 0%</Badge>}
                                        {(!p.cancellation_policy || p.cancellation_policy === 'custom') && (
                                            <span className="text-muted" style={{ fontSize: '11px' }}>
                                                Personalizado ({p.cancellation_hours_free || 12}h / {p.cancellation_hours_half || 3}h)
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <div className="d-flex flex-column gap-1">
                                                <Form.Check 
                                                    type="switch" 
                                                    id={`edit-active-${p.id}`} 
                                                    label={editIsActive ? "Activo" : "Inactivo"} 
                                                    checked={editIsActive} 
                                                    onChange={e => setEditIsActive(e.target.checked)} 
                                                />
                                                <Form.Check 
                                                    type="switch" 
                                                    id={`edit-hybrid-${p.id}`} 
                                                    label="Liq. Híbrida" 
                                                    title="Liquidación Híbrida: Fijo en Booking / Variable en Corporativos"
                                                    checked={editHybridSettlement} 
                                                    onChange={e => setEditHybridSettlement(e.target.checked)} 
                                                    style={{ fontSize: '12px' }}
                                                />
                                                <Form.Check 
                                                    type="switch" 
                                                    id={`edit-asap-${p.id}`} 
                                                    label="ASAP" 
                                                    title="Habilitar ofertas de viajes urgentes/ASAP a conductores de este proveedor"
                                                    checked={editAllowAsapDispatch} 
                                                    onChange={e => setEditAllowAsapDispatch(e.target.checked)} 
                                                    style={{ fontSize: '12px' }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="d-flex flex-column gap-1 align-items-start">
                                                <Badge bg={p.is_active ? "success" : "secondary"}>
                                                    {p.is_active ? "Activo" : "Inactivo"}
                                                </Badge>
                                                {p.hybrid_settlement && (
                                                    <Badge bg="info" className="text-dark" title="Liquidación Híbrida: Fijo en Booking, Variable en Corporativos">
                                                        ⚡ Híbrido
                                                    </Badge>
                                                )}
                                                {p.allow_asap_dispatch ? (
                                                    <Badge bg="warning" className="text-dark" title="Conductores reciben viajes ASAP">
                                                        ⚡ ASAP
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="light" className="text-muted border" title="Conductores bloqueados para viajes ASAP">
                                                        🚫 Sin ASAP
                                                    </Badge>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {editProvider?.id === p.id ? (
                                            <div className="d-flex gap-1">
                                                <Button size="sm" variant="success" onClick={handleUpdate}>💾</Button>
                                                <Button size="sm" variant="secondary" onClick={() => setEditProvider(null)}>✕</Button>
                                            </div>
                                        ) : (
                                            <div className="d-flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="outline-info"
                                                    title="Configurar Rutas en $0 (Clientes, Vehículos y Zonas)"
                                                    onClick={() => setZeroRoutesProvider(p)}
                                                >
                                                    🗺️
                                                </Button>
                                                <Button size="sm" variant="outline-success" title="Acuerdo Comercial" onClick={() => handleOpenAgreement(p)}>🤝</Button>
                                                <Button size="sm" variant="outline-primary" title="Editar Proveedor" onClick={() => { 
                                                    setEditProvider(p); 
                                                    setEditName(p.name); 
                                                    setEditBaseIds(p.bases ? p.bases.map(b => b.id) : (p.base_id ? [p.base_id] : [])); 
                                                    setEditIsActive(p.is_active); 
                                                    setEditMobileRangeStart(p.mobile_range_start !== null && p.mobile_range_start !== undefined ? String(p.mobile_range_start) : '');
                                                    setEditMobileRangeEnd(p.mobile_range_end !== null && p.mobile_range_end !== undefined ? String(p.mobile_range_end) : '');
                                                    setEditContractId(p.contract_id !== null && p.contract_id !== undefined ? String(p.contract_id) : '');
                                                    setEditSettlementGroupId(p.settlement_group_id !== null && p.settlement_group_id !== undefined ? String(p.settlement_group_id) : '');
                                                    setEditOwnerId(p.owner_id !== null && p.owner_id !== undefined ? String(p.owner_id) : '');
                                                    setEditCurrencyCode(p.currency_code !== null && p.currency_code !== undefined ? p.currency_code : '');
                                                    setEditPhone(p.phone || '');
                                                    setEditBookingBrandName(p.booking_brand_name || '');
                                                    setEditHybridSettlement(!!p.hybrid_settlement);
                                                    setEditAllowAsapDispatch(!!p.allow_asap_dispatch);
                                                }}>✏️</Button>
                                                <Button size="sm" variant="outline-danger" title="Eliminar Proveedor" onClick={() => handleDeleteProvider(p.id)}>🗑️</Button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredProviders.length === 0 && (
                                <tr>
                                    <td colSpan="12" className="text-center text-muted py-3">No se encontraron proveedores que coincidan con la búsqueda.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Modal show={showCreate} onHide={() => setShowCreate(false)}>
                <Modal.Header closeButton><Modal.Title className="h6 fw-bold">Nuevo Proveedor Regional</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Nombre del Proveedor</Form.Label>
                        <Form.Control 
                            value={newProvider.name} 
                            placeholder="Ej: RadioTaxi Iquique, Sol del Norte..."
                            onChange={e => setNewProvider({...newProvider, name: e.target.value})} 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Bases / Sucursales Asociadas</Form.Label>
                        <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '8px', borderRadius: '4px', background: '#fff' }}>
                            {bases.map(b => (
                                <Form.Check 
                                    key={b.id}
                                    type="checkbox"
                                    id={`new-base-${b.id}`}
                                    label={b.name}
                                    checked={newProvider.base_ids?.includes(b.id)}
                                    onChange={e => {
                                        const currentIds = newProvider.base_ids || [];
                                        if (e.target.checked) {
                                            setNewProvider({ ...newProvider, base_ids: [...currentIds, b.id] });
                                        } else {
                                            setNewProvider({ ...newProvider, base_ids: currentIds.filter(id => id !== b.id) });
                                        }
                                    }}
                                    style={{ fontSize: '13px', margin: '4px 0' }}
                                />
                            ))}
                        </div>
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Teléfono Contacto Booking</Form.Label>
                                <Form.Control 
                                    placeholder="Ej: +1 809 123 4567" 
                                    value={newProvider.phone} 
                                    onChange={e => setNewProvider({...newProvider, phone: e.target.value})} 
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                    Teléfono local para el pasajero de Booking.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Marca Visible Booking (Opcional)</Form.Label>
                                <Form.Control 
                                    placeholder="Transportes Emilia (Default)" 
                                    value={newProvider.booking_brand_name} 
                                    onChange={e => setNewProvider({...newProvider, booking_brand_name: e.target.value})} 
                                />
                                <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                    Nombre comercial visible en Booking.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Móvil Min</Form.Label>
                                <Form.Control 
                                    type="number"
                                    placeholder="Ej: 100"
                                    value={newProvider.mobile_range_start} 
                                    onChange={e => setNewProvider({...newProvider, mobile_range_start: e.target.value})} 
                                />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Móvil Max</Form.Label>
                                <Form.Control 
                                    type="number"
                                    placeholder="Ej: 200"
                                    value={newProvider.mobile_range_end} 
                                    onChange={e => setNewProvider({...newProvider, mobile_range_end: e.target.value})} 
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Moneda de Liquidación</Form.Label>
                        <Form.Select 
                            value={newProvider.currency_code} 
                            onChange={e => setNewProvider({...newProvider, currency_code: e.target.value})}
                        >
                            <option value="">Por defecto (Según Sucursal)</option>
                            <option value="CLP">CLP (Pesos Chilenos)</option>
                            <option value="USD">USD (Dólares)</option>
                            <option value="PEN">PEN (Soles)</option>
                            <option value="DOP">DOP (Pesos Dominicanos)</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Contrato por Defecto</Form.Label>
                        <Form.Select 
                            value={newProvider.contract_id} 
                            onChange={e => setNewProvider({...newProvider, contract_id: e.target.value})}
                        >
                            <option value="">Ninguno</option>
                            {contracts.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Grupo de Liquidación por Defecto</Form.Label>
                        <Form.Select 
                            value={newProvider.settlement_group_id} 
                            onChange={e => setNewProvider({...newProvider, settlement_group_id: e.target.value})}
                        >
                            <option value="">Ninguno</option>
                            {settlementGroups.map(g => (
                                <option key={g.id} value={g.id}>{g.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Dueño (Partner) por Defecto</Form.Label>
                        <Form.Select 
                            value={newProvider.owner_id} 
                            onChange={e => setNewProvider({...newProvider, owner_id: e.target.value})}
                        >
                            <option value="">Ninguno</option>
                            {drivers.map(d => (
                                <option key={d.id} value={d.id}>{d.full_name}{d.is_owner ? ' (Dueño)' : ''}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check 
                            type="switch"
                            id="new-active"
                            label="Proveedor Activo"
                            checked={newProvider.is_active}
                            onChange={e => setNewProvider({...newProvider, is_active: e.target.checked})}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check 
                            type="switch"
                            id="new-hybrid-settlement"
                            label="Liquidación Híbrida (Fijo en Booking / Variable en Corporativos)"
                            checked={newProvider.hybrid_settlement || false}
                            onChange={e => setNewProvider({...newProvider, hybrid_settlement: e.target.checked})}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                            Si está activo, los choferes de este proveedor cobran el costo base fijo en Booking y su contrato variable en otros convenios.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check 
                            type="switch"
                            id="new-asap-dispatch"
                            label="Habilitar Despacho Inmediato (ASAP)"
                            checked={newProvider.allow_asap_dispatch || false}
                            onChange={e => setNewProvider({...newProvider, allow_asap_dispatch: e.target.checked})}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                            Por defecto desactivado para proveedores externos. Si se desactiva, los conductores de este proveedor nunca recibirán ofertas de viajes urgentes/ASAP.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="p-2 border rounded bg-light">
                        <Form.Check 
                            type="switch"
                            id="new-open-zero-routes"
                            label="🗺️ Configurar rutas iniciales en $0 al terminar de crear"
                            checked={openZeroRoutesAfterCreate}
                            onChange={e => setOpenZeroRoutesAfterCreate(e.target.checked)}
                            className="fw-bold text-primary"
                        />
                        <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                            Abre de inmediato el mantenedor para elegir clientes (uno, varios o todos), tipos de vehículo y rutas en $0 que verá en su portal.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowCreate(false)}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={handleCreate} className="fw-bold">
                        {openZeroRoutesAfterCreate ? 'Crear y Configurar Rutas $0 ➔' : 'Crear'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <Modal show={showAgreementModal} onHide={() => setShowAgreementModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="h6 fw-bold">🤝 Acuerdo Comercial de Cancelación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <span className="text-muted small">Configura las condiciones de pago al conductor para cancelaciones de Booking.com con cargo correspondientes al proveedor <strong>{selectedAgreementProvider?.name}</strong>.</span>
                    </div>
                    
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Política de Cancelación</Form.Label>
                        <Form.Select 
                            value={agreementPolicy} 
                            onChange={e => setAgreementPolicy(e.target.value)}
                        >
                            <option value="custom">⚙️ Regla por Horas (Personalizada)</option>
                            <option value="always_100">🟢 Siempre pagar 100% al chofer</option>
                            <option value="always_50">🟡 Siempre pagar 50% al chofer</option>
                            <option value="always_0">🔴 Siempre pagar 0% al chofer (No pagar)</option>
                        </Form.Select>
                    </Form.Group>

                    {agreementPolicy === 'custom' && (
                        <Row className="mb-3">
                            <Col>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Límite Pago 0% (Horas)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.5" 
                                        value={agreementHoursFree} 
                                        onChange={e => setAgreementHoursFree(e.target.value)} 
                                    />
                                    <Form.Text className="text-muted" style={{ fontSize: '10px' }}>
                                        Si se cancela con &ge; {agreementHoursFree || 12} horas de anticipación.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Límite Pago 100% (Horas)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        step="0.5" 
                                        value={agreementHoursHalf} 
                                        onChange={e => setAgreementHoursHalf(e.target.value)} 
                                    />
                                    <Form.Text className="text-muted" style={{ fontSize: '10px' }}>
                                        Si se cancela con &lt; {agreementHoursHalf || 3} horas de anticipación.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Card className="bg-light border-0 mb-3" style={{ borderRadius: '8px' }}>
                        <Card.Body className="p-3">
                            <div className="small fw-bold mb-2 text-primary">Previsualización de Reglas:</div>
                            <div className="small text-muted d-flex flex-column gap-2" style={{ fontSize: '12px' }}>
                                {agreementPolicy === 'always_100' && (
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: '16px' }}>🟢</span>
                                        <span>Cualquier cancelación con cargo pagará el <strong>100%</strong> al chofer.</span>
                                    </div>
                                )}
                                {agreementPolicy === 'always_50' && (
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: '16px' }}>🟡</span>
                                        <span>Cualquier cancelación con cargo pagará el <strong>50%</strong> al chofer.</span>
                                    </div>
                                )}
                                {agreementPolicy === 'always_0' && (
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: '16px' }}>🔴</span>
                                        <span>Cualquier cancelación con cargo pagará el <strong>0%</strong> al chofer (sin pago).</span>
                                    </div>
                                )}
                                {agreementPolicy === 'custom' && (
                                    <>
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{ fontSize: '14px' }}>🔴</span>
                                            <span>Con <strong>&ge; {agreementHoursFree || 12} horas</strong> de anticipación: se paga <strong>0%</strong> al chofer.</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{ fontSize: '14px' }}>🟡</span>
                                            <span>Entre <strong>{agreementHoursHalf || 3} y {agreementHoursFree || 12} horas</strong>: se paga <strong>50%</strong> al chofer.</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span style={{ fontSize: '14px' }}>🟢</span>
                                            <span>Con <strong>&lt; {agreementHoursHalf || 3} horas</strong> de anticipación: se paga <strong>100%</strong> al chofer.</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAgreementModal(false)}>Cancelar</Button>
                    <Button variant="success" size="sm" onClick={handleSaveAgreement} disabled={isSavingAgreement} className="fw-bold">
                        {isSavingAgreement ? 'Guardando...' : '💾 Guardar Acuerdo'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <ProviderZeroRoutesModal
                show={Boolean(zeroRoutesProvider)}
                onHide={() => setZeroRoutesProvider(null)}
                provider={zeroRoutesProvider}
                bases={bases}
                allProviders={providers}
            />
        </div>
    );
};

// --- VEHICLE TYPES MANAGER (NUEVO) ---
const VehicleTypesManager = () => {
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [newVT, setNewVT] = useState({
        name: '',
        capacity: 4,
        max_suitcases: 4,
        booking_category: 'STANDARD',
        provider_id: ''
    });

    const [editVT, setEditVT] = useState(null);
    const [editName, setEditName] = useState('');
    const [editCapacity, setEditCapacity] = useState(4);
    const [editMaxSuitcases, setEditMaxSuitcases] = useState(4);
    const [editBookingCategory, setEditBookingCategory] = useState('STANDARD');
    const [editProviderId, setEditProviderId] = useState('');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [vtData, pData] = await Promise.all([
                getVehicleTypes(),
                getProviders()
            ]);
            setVehicleTypes(vtData || []);
            setProviders(pData || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filteredVT = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        return vehicleTypes.filter(vt => {
            const nameMatch = (vt.name || '').toLowerCase().includes(query);
            const bookingMatch = (vt.booking_category || '').toLowerCase().includes(query);
            const provider = providers.find(p => p.id === vt.provider_id);
            const providerName = provider ? provider.name.toLowerCase() : 'global';
            const providerMatch = providerName.includes(query);
            return nameMatch || bookingMatch || providerMatch;
        });
    }, [vehicleTypes, searchQuery, providers]);

    const handleCreate = async () => {
        if (!newVT.name) {
            alert("Debe ingresar un nombre.");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                name: newVT.name,
                capacity: parseInt(newVT.capacity),
                max_suitcases: parseInt(newVT.max_suitcases),
                booking_category: newVT.booking_category,
                provider_id: newVT.provider_id ? parseInt(newVT.provider_id) : null
            };
            await createVehicleType(payload);
            setShowCreate(false);
            setNewVT({ name: '', capacity: 4, max_suitcases: 4, booking_category: 'STANDARD', provider_id: '' });
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Error al crear tipo de vehículo.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (!editVT || !editName) {
            alert("Debe ingresar un nombre.");
            return;
        }
        try {
            setLoading(true);
            const payload = {
                name: editName,
                capacity: parseInt(editCapacity),
                max_suitcases: parseInt(editMaxSuitcases),
                booking_category: editBookingCategory,
                provider_id: editProviderId ? parseInt(editProviderId) : null
            };
            await updateVehicleType(editVT.id, payload);
            setEditVT(null);
            load();
        } catch (e) {
            alert(e.response?.data?.detail || "Error al actualizar tipo de vehículo.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-dark mb-0">🚙 Gestión de Tipos de Vehículo y Capacidades</h4>
                <Button variant="primary" size="sm" onClick={() => setShowCreate(true)} className="fw-bold">
                    + Registrar Tipo de Vehículo
                </Button>
            </div>

            <Card className="shadow-sm border-0 p-3 bg-white mb-4">
                <div className="d-flex gap-2 align-items-center mb-3">
                    <InputGroup size="sm" style={{ maxWidth: '350px' }}>
                        <InputGroup.Text><Search size={14} /></InputGroup.Text>
                        <Form.Control 
                            type="text"
                            placeholder="Buscar por nombre, categoría o proveedor..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                    </InputGroup>
                </div>

                <Table hover responsive size="sm" className="align-middle bg-white">
                    <thead className="bg-light">
                        <tr>
                            <th>ID</th>
                            <th>Nombre del Vehículo</th>
                            <th>Capacidad (Pasajeros)</th>
                            <th>Maletas Máx</th>
                            <th>Categoría Booking.com</th>
                            <th>Asignado a Proveedor</th>
                            <th className="text-end">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && filteredVT.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center py-3 text-muted">Cargando tipos de vehículo...</td>
                            </tr>
                        ) : filteredVT.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="text-center py-3 text-muted">No se encontraron tipos de vehículo.</td>
                            </tr>
                        ) : (
                            filteredVT.map(vt => {
                                const p = providers.find(prov => prov.id === vt.provider_id);
                                return (
                                    <tr key={vt.id}>
                                        <td><span className="font-monospace text-muted">{vt.id}</span></td>
                                        <td><strong>{vt.name}</strong></td>
                                        <td><Badge bg="info">👤 {vt.capacity} Pax</Badge></td>
                                        <td><Badge bg="secondary" className="text-dark">💼 {vt.max_suitcases || 4} Maletas</Badge></td>
                                        <td><span className="font-monospace text-muted">{vt.booking_category || 'STANDARD'}</span></td>
                                        <td>
                                            {p ? (
                                                <Badge bg="purple" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>🚚 {p.name}</Badge>
                                            ) : (
                                                <Badge bg="success">🌍 Global (Sistema)</Badge>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="py-0.5 px-2"
                                                onClick={() => {
                                                    setEditVT(vt);
                                                    setEditName(vt.name);
                                                    setEditCapacity(vt.capacity);
                                                    setEditMaxSuitcases(vt.max_suitcases || 4);
                                                    setEditBookingCategory(vt.booking_category || 'STANDARD');
                                                    setEditProviderId(vt.provider_id || '');
                                                }}
                                            >
                                                Editar
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>
            </Card>

            {/* Modal Crear */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">🚙 Nuevo Tipo de Vehículo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 space-y-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Nombre del Tipo de Vehículo *</Form.Label>
                        <Form.Control 
                            type="text"
                            required
                            placeholder="Ej: Minibús Premium 12pax"
                            value={newVT.name}
                            onChange={e => setNewVT({...newVT, name: e.target.value})}
                        />
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold text-muted small text-uppercase">Pasajeros Máximos *</Form.Label>
                                <Form.Control 
                                    type="number"
                                    required
                                    min="1"
                                    value={newVT.capacity}
                                    onChange={e => setNewVT({...newVT, capacity: e.target.value})}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold text-muted small text-uppercase">Maletas Máximas *</Form.Label>
                                <Form.Control 
                                    type="number"
                                    required
                                    min="0"
                                    value={newVT.max_suitcases}
                                    onChange={e => setNewVT({...newVT, max_suitcases: e.target.value})}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Categoría Homóloga Booking.com *</Form.Label>
                        <Form.Select
                            value={newVT.booking_category}
                            onChange={e => setNewVT({...newVT, booking_category: e.target.value})}
                        >
                            <option value="STANDARD">STANDARD</option>
                            <option value="VAN">VAN</option>
                            <option value="PEOPLE_CARRIER">PEOPLE_CARRIER</option>
                            <option value="LARGE_PEOPLE_CARRIER">LARGE_PEOPLE_CARRIER</option>
                            <option value="EXECUTIVE_PEOPLE_CARRIER">EXECUTIVE_PEOPLE_CARRIER</option>
                            <option value="MINIBUS">MINIBUS</option>
                            <option value="LUXURY">LUXURY</option>
                            <option value="EXECUTIVE">EXECUTIVE</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Proveedor Asociado (Opcional)</Form.Label>
                        <Form.Select
                            value={newVT.provider_id}
                            onChange={e => setNewVT({...newVT, provider_id: e.target.value})}
                        >
                            <option value="">- Global (Sistema) -</option>
                            {providers.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Si seleccionas un proveedor, este tipo de vehículo será exclusivo para él. Si no, será global para el sistema.
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowCreate(false)}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={handleCreate} disabled={loading} className="fw-bold">Registrar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Editar */}
            <Modal show={editVT !== null} onHide={() => setEditVT(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">🚙 Editar Tipo de Vehículo</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 space-y-3">
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Nombre del Tipo de Vehículo *</Form.Label>
                        <Form.Control 
                            type="text"
                            required
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                        />
                    </Form.Group>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold text-muted small text-uppercase">Pasajeros Máximos *</Form.Label>
                                <Form.Control 
                                    type="number"
                                    required
                                    min="1"
                                    value={editCapacity}
                                    onChange={e => setEditCapacity(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="fw-bold text-muted small text-uppercase">Maletas Máximas *</Form.Label>
                                <Form.Control 
                                    type="number"
                                    required
                                    min="0"
                                    value={editMaxSuitcases}
                                    onChange={e => setEditMaxSuitcases(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Categoría Homóloga Booking.com *</Form.Label>
                        <Form.Select
                            value={editBookingCategory}
                            onChange={e => setEditBookingCategory(e.target.value)}
                        >
                            <option value="STANDARD">STANDARD</option>
                            <option value="VAN">VAN</option>
                            <option value="PEOPLE_CARRIER">PEOPLE_CARRIER</option>
                            <option value="LARGE_PEOPLE_CARRIER">LARGE_PEOPLE_CARRIER</option>
                            <option value="EXECUTIVE_PEOPLE_CARRIER">EXECUTIVE_PEOPLE_CARRIER</option>
                            <option value="MINIBUS">MINIBUS</option>
                            <option value="LUXURY">LUXURY</option>
                            <option value="EXECUTIVE">EXECUTIVE</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-muted small text-uppercase">Proveedor Asociado (Opcional)</Form.Label>
                        <Form.Select
                            value={editProviderId}
                            onChange={e => setEditProviderId(e.target.value)}
                        >
                            <option value="">- Global (Sistema) -</option>
                            {providers.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setEditVT(null)}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={handleUpdate} disabled={loading} className="fw-bold">Guardar</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

// --- KML IMPORT MODAL ---
const KmlImportModal = ({ show, onHide, bases, onRefresh }) => {
    const [file, setFile] = useState(null);
    const [previewZones, setPreviewZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [bulkBaseId, setBulkBaseId] = useState('');

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setError(null);
        }
    };

    const handleUploadPreview = async () => {
        if (!file) return;
        setLoading(true);
        setError(null);
        try {
            const data = await previewKmlZones(file);
            setPreviewZones(data.map((z, idx) => ({
                ...z,
                city: z.folder_name || 'Lima',
                country_code: 'PE', 
                key: idx
            })));
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || "No se pudo procesar el KML.");
        } finally {
            setLoading(false);
        }
    };

    const handleBulkBaseChange = (baseId) => {
        setBulkBaseId(baseId);
        setPreviewZones(prev => prev.map(z => ({
            ...z,
            base_id: baseId ? parseInt(baseId) : null,
            base_name: baseId ? bases.find(b => b.id === parseInt(baseId))?.name : null
        })));
    };

    const handleRowValueChange = (key, field, value) => {
        setPreviewZones(prev => prev.map(z => {
            if (z.key === key) {
                const updated = { ...z, [field]: value };
                if (field === 'base_id') {
                    updated.base_name = value ? bases.find(b => b.id === parseInt(value))?.name : null;
                }
                return updated;
            }
            return z;
        }));
    };

    const handleConfirmImport = async () => {
        const missingBase = previewZones.some(z => !z.base_id);
        if (missingBase) {
            alert("⚠️ Por favor, asigna una Sucursal/Base a todas las zonas antes de confirmar.");
            return;
        }

        setImporting(true);
        setError(null);
        try {
            const payload = {
                zones: previewZones.map(z => ({
                    name: z.name,
                    base_id: parseInt(z.base_id),
                    polygon_coords: z.polygon_coords,
                    city: z.city || null,
                    country_code: z.country_code || 'CL'
                }))
            };
            await bulkImportZones(payload);
            alert("✅ Importación completada con éxito.");
            onRefresh();
            onHide();
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || "Error al realizar la importación.");
        } finally {
            setImporting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="bg-success text-white">
                <Modal.Title className="fw-bold">📥 Importar Zonas Geográficas (KML)</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}
                
                {previewZones.length === 0 ? (
                    <div className="p-4 text-center">
                        <p className="text-muted">Sube tu archivo .kml para leer los polígonos correspondientes.</p>
                        <Form.Group className="mb-3 w-50 mx-auto">
                            <Form.Control type="file" accept=".kml" onChange={handleFileChange} />
                        </Form.Group>
                        <Button 
                            variant="success" 
                            disabled={!file || loading} 
                            onClick={handleUploadPreview}
                        >
                            {loading ? 'Procesando archivo...' : 'Ver Vista Previa'}
                        </Button>
                    </div>
                ) : (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded shadow-sm">
                            <div>
                                <h6 className="fw-bold mb-0">Vista Previa de Zonas Detectadas</h6>
                                <small className="text-muted">Se encontraron {previewZones.length} polígonos. Verifica y ajusta las asignaciones antes de guardar.</small>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <label className="small fw-bold mb-0 text-nowrap">Asignar Sucursal a todos:</label>
                                <Form.Select 
                                    size="sm" 
                                    value={bulkBaseId} 
                                    onChange={e => handleBulkBaseChange(e.target.value)}
                                    style={{width:'200px'}}
                                >
                                    <option value="">- Seleccionar Sucursal -</option>
                                    {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Form.Select>
                            </div>
                        </div>

                        <div style={{maxHeight:'400px', overflowY:'auto'}}>
                            <Table striped bordered hover size="sm" className="small align-middle">
                                <thead className="bg-dark text-white text-center position-sticky top-0">
                                    <tr>
                                        <th>Nombre Zona</th>
                                        <th>Carpeta KML</th>
                                        <th>Sucursal/Base</th>
                                        <th>Ciudad</th>
                                        <th>País</th>
                                        <th>Coordenadas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewZones.map(z => (
                                        <tr key={z.key}>
                                            <td>
                                                <Form.Control 
                                                    size="sm" 
                                                    value={z.name} 
                                                    onChange={e => handleRowValueChange(z.key, 'name', e.target.value)} 
                                                />
                                            </td>
                                            <td className="text-muted text-center">{z.folder_name || 'N/A'}</td>
                                            <td>
                                                <Form.Select 
                                                    size="sm" 
                                                    value={z.base_id || ''} 
                                                    onChange={e => handleRowValueChange(z.key, 'base_id', e.target.value)}
                                                    className={!z.base_id ? 'border-danger' : ''}
                                                >
                                                    <option value="">- Requerido -</option>
                                                    {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Control 
                                                    size="sm" 
                                                    value={z.city || ''} 
                                                    onChange={e => handleRowValueChange(z.key, 'city', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <Form.Control 
                                                    size="sm" 
                                                    style={{width:'60px', textTransform:'uppercase'}}
                                                    maxLength={2}
                                                    value={z.country_code || ''} 
                                                    onChange={e => handleRowValueChange(z.key, 'country_code', e.target.value.toUpperCase())} 
                                                />
                                            </td>
                                            <td className="text-center font-monospace">{z.coords_count} pts</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={importing}>
                    Cancelar
                </Button>
                {previewZones.length > 0 && (
                    <Button 
                        variant="success" 
                        disabled={importing} 
                        onClick={handleConfirmImport}
                    >
                        {importing ? 'Importando...' : 'Confirmar Importación'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// --- ZONE GROUPS MODAL ---
const ZoneGroupsModal = ({ show, onHide, bases, onRefresh }) => {
    const [groups, setGroups] = useState([]);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [baseId, setBaseId] = useState('');
    const [loading, setLoading] = useState(false);

    const loadGroups = useCallback(async () => {
        try {
            const data = await getZoneGroups();
            setGroups(data);
        } catch (e) {
            console.error("Error al cargar grupos:", e);
        }
    }, []);

    useEffect(() => {
        if (show) {
            loadGroups();
            setName('');
            setDescription('');
            setBaseId('');
        }
    }, [show, loadGroups]);

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!name || !name.trim()) return;
        setLoading(true);
        try {
            await createZoneGroup({
                name: name.trim(),
                description: description.trim() || null,
                base_id: baseId ? parseInt(baseId) : null,
                is_active: true
            });
            setName('');
            setDescription('');
            setBaseId('');
            await loadGroups();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error(error);
            alert("Error al crear grupo de zonas.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar este grupo? Las zonas asignadas quedarán sin grupo (huérfanas) pero no serán eliminadas.")) {
            try {
                await deleteZoneGroup(id);
                await loadGroups();
                if (onRefresh) onRefresh();
            } catch (error) {
                console.error(error);
                alert("Error al eliminar grupo.");
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>📁 Administrar Grupos de Zonas</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={5} className="border-end">
                        <h6 className="fw-bold mb-3">Crear Nuevo Grupo</h6>
                        <Form onSubmit={handleCreate} className="small">
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bold">Nombre del Grupo</Form.Label>
                                <Form.Control 
                                    size="sm" 
                                    type="text" 
                                    required 
                                    placeholder="Ej: Lima 1" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)} 
                                />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label className="fw-bold">Sucursal/Base</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={baseId} 
                                    onChange={e => setBaseId(e.target.value)}
                                >
                                    <option value="">- Seleccionar -</option>
                                    {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">Descripción (Opcional)</Form.Label>
                                <Form.Control 
                                    size="sm" 
                                    as="textarea" 
                                    rows={2} 
                                    placeholder="Detalles sobre este grupo de zonas..." 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)} 
                                />
                            </Form.Group>
                            <Button size="sm" variant="success" type="submit" className="w-100" disabled={loading}>
                                {loading ? 'Guardando...' : 'Crear Grupo'}
                            </Button>
                        </Form>
                    </Col>
                    <Col md={7}>
                        <h6 className="fw-bold mb-3">Grupos Existentes</h6>
                        <div style={{maxHeight:'320px', overflowY:'auto'}}>
                            <Table striped bordered hover size="sm" className="small">
                                <thead className="bg-dark text-white">
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Sucursal</th>
                                        <th>Descripción</th>
                                        <th style={{width:'50px'}}>Eliminar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groups.length === 0 ? (
                                        <tr><td colSpan={4} className="text-center text-muted">No hay grupos creados</td></tr>
                                    ) : (
                                        groups.map(g => (
                                            <tr key={g.id}>
                                                <td><b>{g.name}</b></td>
                                                <td>{bases.find(b => b.id === g.base_id)?.name || 'N/A'}</td>
                                                <td>{g.description || '---'}</td>
                                                <td className="text-center">
                                                    <Button size="sm" variant="outline-danger" className="py-0 px-1 border-0" onClick={() => handleDelete(g.id)}>
                                                        ✕
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- ZONES MANAGER ---
const ZonesManager = () => {
    const [zones, setZones] = useState([]);
    const [bases, setBases] = useState([]);
    const [zoneGroups, setZoneGroups] = useState([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [mapRef, setMapRef] = useState(null);
    const polygonRefs = useRef({}); 
    const [selectedZone, setSelectedZone] = useState(null);
    const [isMapEditing, setIsMapEditing] = useState(false); // Controla si el polígono es editable
    const [isEditingName, setIsEditingName] = useState(false);
    const [editName, setEditName] = useState('');
    const [editBaseId, setEditBaseId] = useState('');
    const [editGroupId, setEditGroupId] = useState('');
    const [zoneSearch, setZoneSearch] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [showKmlModal, setShowKmlModal] = useState(false);
    const [showGroupsModal, setShowGroupsModal] = useState(false);
    
    const editedPathRef = useRef(null); 
    const geocoderRef = useRef(null);

    const load = useCallback(async () => { 
        const [zData, bData, zgData] = await Promise.all([getZones(), getBases(), getZoneGroups()]);
        setZones(zData); setBases(bData); setZoneGroups(zgData);
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => { if (zoneSearch) setCollapsedGroups({}); }, [zoneSearch]);

    const handleDownloadKML = (baseId = null, baseName = null) => {
        let zonesToExport = zones;
        let filename = "todas_las_zonas.kml";
        
        if (baseId !== null) {
            zonesToExport = zones.filter(z => z.base_id === baseId);
            filename = `zonas_${baseName.toLowerCase().replace(/\s+/g, '_')}.kml`;
        }

        if (zonesToExport.length === 0) {
            alert("⚠️ No hay zonas geográficas para exportar en esta selección.");
            return;
        }

        let kmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${filename.replace(".kml", "")}</name>
    <description>Polígonos exportados desde EmiGo</description>
`;

        // Agrupar zonas por Base/Sucursal para estructurar carpetas en el KML
        const zonesByBase = {};
        zonesToExport.forEach(zone => {
            const baseObj = bases.find(b => b.id === zone.base_id);
            const name = baseObj ? baseObj.name : "Sin Sucursal Asignada";
            if (!zonesByBase[name]) {
                zonesByBase[name] = [];
            }
            zonesByBase[name].push(zone);
        });

        // Generar las carpetas y placemarks en KML
        Object.entries(zonesByBase).forEach(([baseName, baseZones]) => {
            kmlContent += `    <Folder>
      <name>${baseName}</name>
      <description>Zonas pertenecientes a la Sucursal: ${baseName}</description>
`;
            baseZones.forEach(zone => {
                if (!zone.polygon_coords) return;
                try {
                    const coords = JSON.parse(zone.polygon_coords);
                    if (!Array.isArray(coords) || coords.length < 3) return;

                    // En KML un polígono debe cerrarse explícitamente volviendo al primer punto
                    const closedCoords = [...coords];
                    if (
                        coords[0].lat !== coords[coords.length - 1].lat ||
                        coords[0].lng !== coords[coords.length - 1].lng
                    ) {
                        closedCoords.push(coords[0]);
                    }

                    // KML usa el formato: lng,lat,alt (longitud primero!) separados por espacios o saltos de línea
                    const coordString = closedCoords
                        .map(c => `${c.lng},${c.lat},0`)
                        .join("\n                ");

                    kmlContent += `      <Placemark>
        <name>${zone.name}</name>
        <description>Ciudad: ${zone.city || 'S/I'} | País: ${zone.country_code || 'CL'}</description>
        <Style>
          <LineStyle>
            <color>ff0000ff</color> <!-- Borde Rojo Opaco -->
            <width>2.5</width>
          </LineStyle>
          <PolyStyle>
            <color>400000ff</color> <!-- Relleno Rojo Semitransparente -->
          </PolyStyle>
        </Style>
        <Polygon>
          <outerBoundaryIs>
            <LinearRing>
              <coordinates>
                \n                ${coordString}
              </coordinates>
            </LinearRing>
          </outerBoundaryIs>
        </Polygon>
      </Placemark>
`;
                } catch (err) {
                    console.error("Error al procesar coordenadas de zona:", zone.name, err);
                }
            });
            kmlContent += `    </Folder>
`;
        });

        kmlContent += `  </Document>
</kml>`;

        // Gatillar descarga del KML en el cliente
        try {
            const blob = new Blob([kmlContent], { type: "application/vnd.google-earth.kml+xml" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al gatillar la descarga del archivo KML:", error);
            alert("No se pudo descargar el archivo KML.");
        }
    };

    const onMapLoad = (map) => { setMapRef(map); if (window.google && window.google.maps) geocoderRef.current = new window.google.maps.Geocoder(); };
    const onPolygonLoad = (polygon, zone) => { 
        if (!polygon) return;
        polygonRefs.current[zone.id] = polygon;
        const path = typeof polygon.getPath === 'function' ? polygon.getPath() : null;
        if (!path) return;
        const updateRef = () => {
            if (typeof path.getArray === 'function') {
                const newCoords = path.getArray().map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));
                if (selectedZone && selectedZone.id === zone.id) {
                    editedPathRef.current = newCoords;
                }
            }
        };
        if (window.google && window.google.maps && window.google.maps.event) {
            ['set_at', 'insert_at', 'remove_at'].forEach(event => window.google.maps.event.addListener(path, event, updateRef));
            window.google.maps.event.addListener(polygon, 'dragend', updateRef);
        }
    };

    const onOverlayComplete = async (e) => {
        const path = e.overlay.getPath().getArray().map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));
        e.overlay.setMap(null); 
        let detectedCountry = "CL", detectedCity = "General";
        if (geocoderRef.current) {
            const bounds = new window.google.maps.LatLngBounds(); path.forEach(p => bounds.extend(p));
            try {
                const res = await geocoderRef.current.geocode({ location: bounds.getCenter() });
                if (res.results[0]) {
                    const comps = res.results[0].address_components;
                    const c = comps.find(c => c.types.includes('country'));
                    const l = comps.find(c => c.types.includes('locality')) || comps.find(c => c.types.includes('administrative_area_level_1'));
                    if (c) detectedCountry = c.short_name; if (l) detectedCity = l.long_name;
                }
            } catch (err) {}
        }
        const name = prompt(`Zona en ${detectedCountry} - ${detectedCity}.\nNombre:`, "Nueva Zona");
        if (name) { await createZone({ name, country_code: detectedCountry, city: detectedCity, polygon_coords: JSON.stringify(path), is_active: true }); load(); }
        setIsDrawing(false);
    };

    const handleUpdateName = async () => { 
        if (selectedZone) { 
            const payload = { 
                name: editName, 
                base_id: editBaseId ? parseInt(editBaseId) : null,
                group_id: editGroupId ? parseInt(editGroupId) : null
            };
            // FIX MAESTRO: Leer directamente del objeto Polygon del mapa
            const poly = polygonRefs.current[selectedZone.id];
            if (poly) {
                const currentPath = poly.getPath().getArray().map(latLng => ({ lat: latLng.lat(), lng: latLng.lng() }));
                payload.polygon_coords = JSON.stringify(currentPath);
            }
            await updateZone(selectedZone.id, payload); 
            setIsEditingName(false); 
            setIsMapEditing(false); // Bloquear mapa tras guardar
            editedPathRef.current = null; 
            load(); 
        } 
    }; 

    const handleDelete = async (id) => { 
        if (window.confirm("¿Eliminar?")) { 
            try {
                await deleteZone(id); 
                load(); 
            } catch (error) {
                console.error(error);
                const msg = error.response?.data?.detail || "No se pudo eliminar la zona. Verifica si tiene tarifas o reglas asociadas.";
                alert(`⚠️ Error: ${msg}`);
                load();
            }
        } 
    };
    
    const handleBulkDelete = async (zonesToDelete, label) => { 
        if (!zonesToDelete.length) return; 
        if (window.confirm(`¿Eliminar ${zonesToDelete.length} zonas de "${label}"?`)) { 
            const ids = zonesToDelete.map(z => z.id);
            try { 
                await deleteZonesBulk(ids); 
                if (selectedZone && ids.includes(selectedZone.id)) setSelectedZone(null);
                load(); 
            } catch (error) { 
                console.error(error); 
                const msg = error.response?.data?.detail || "No se pudieron eliminar algunas zonas.";
                alert(`⚠️ Error: ${msg}`);
                load(); 
            } 
        } 
    };
    
    const groupedZones = zones.filter(z => z.name.toLowerCase().includes(zoneSearch.toLowerCase()) || (z.city && z.city.toLowerCase().includes(zoneSearch.toLowerCase()))).reduce((acc, zone) => { const country = zone.country_code || 'Desconocido'; const city = zone.city || 'General'; if (!acc[country]) acc[country] = {}; if (!acc[country][city]) acc[country][city] = []; acc[country][city].push(zone); return acc; }, {});

    const handleZoneClick = (z) => {
        setSelectedZone(z);
        setEditName(z.name);
        setEditBaseId(z.base_id || '');
        setEditGroupId(z.group_id || '');
        setIsMapEditing(false); // Al seleccionar una zona, NO entrar en modo edición
        editedPathRef.current = null; 

        const poly = polygonRefs.current[z.id];
        if (mapRef && poly) {
            const bounds = new window.google.maps.LatLngBounds();
            const path = poly.getPath();
            for (let i = 0; i < path.getLength(); i++) bounds.extend(path.getAt(i));
            mapRef.fitBounds(bounds);
        }
    };

    return (
        <Row>
            <Col md={4} className="border-end">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Zonas</h5>
                    <div className="d-flex gap-2">
                        <DropdownButton
                            size="sm"
                            variant="outline-secondary"
                            title="📥 Exportar KML"
                            id="dropdown-download-kml"
                        >
                            <Dropdown.Item onClick={() => handleDownloadKML()}>
                                Todas las Sucursales
                            </Dropdown.Item>
                            {bases.length > 0 && <Dropdown.Divider />}
                            {bases.map(b => (
                                <Dropdown.Item key={b.id} onClick={() => handleDownloadKML(b.id, b.name)}>
                                    {b.name}
                                </Dropdown.Item>
                            ))}
                        </DropdownButton>
                        <Button size="sm" variant="outline-success" onClick={() => setShowKmlModal(true)}>
                            📤 Importar KML
                        </Button>
                        <Button size="sm" variant="outline-primary" onClick={() => setShowGroupsModal(true)}>
                            📁 Grupos
                        </Button>
                        <Button size="sm" variant={isDrawing ? "danger" : "primary"} onClick={() => setIsDrawing(!isDrawing)}>
                            {isDrawing ? 'X' : '+ Dibujar'}
                        </Button>
                    </div>
                </div>
                <Card className="mb-3 bg-light border-0 small p-2"><div className="text-muted small">💡 Autodetectamos ubicación al dibujar.</div></Card>
                <InputGroup size="sm" className="mb-3"><Form.Control placeholder="🔍 Filtrar..." value={zoneSearch} onChange={e => setZoneSearch(e.target.value)} /></InputGroup>
                {selectedZone && (
                    <Card className="mb-3 border-primary shadow-sm">
                        <Card.Header className={`py-1 ${isMapEditing ? 'bg-warning text-dark' : 'bg-primary text-white'} small d-flex justify-content-between align-items-center`}>
                            <span>{isMapEditing ? '✏️ Modo Edición Activo' : '👁️ Vista de Zona'}</span>
                            {!isMapEditing && (
                                <Button size="sm" variant="warning" className="py-0 px-2 fw-bold" style={{fontSize:'11px'}} onClick={() => setIsMapEditing(true)}>
                                    ✏️ Editar Zona
                                </Button>
                            )}
                            {isMapEditing && (
                                <Button size="sm" variant="outline-light" className="py-0 px-2" style={{fontSize:'11px'}} onClick={() => setIsMapEditing(false)}>
                                    ✕ Cancelar
                                </Button>
                            )}
                        </Card.Header>
                        <Card.Body className="p-2">
                            {isEditingName
                                ? <InputGroup size="sm"><Form.Control value={editName} onChange={e => setEditName(e.target.value)} /><Button variant="success" onClick={handleUpdateName}>OK</Button></InputGroup>
                                : <div className="d-flex justify-content-between"><b>{selectedZone.name}</b>{isMapEditing && <Button size="sm" variant="link" onClick={() => setIsEditingName(true)}>✏️ Nombre</Button>}</div>
                            }
                            <Form.Label className="small mt-2">Base/Sucursal</Form.Label>
                            <Form.Select size="sm" value={editBaseId} onChange={e => setEditBaseId(e.target.value)} className="mb-2" disabled={!isMapEditing}>
                                <option value="">- Sin Asignar -</option>
                                {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </Form.Select>
                            <Form.Label className="small mt-1">Grupo de Zonas</Form.Label>
                            <Form.Select 
                                size="sm" 
                                value={editGroupId} 
                                onChange={e => setEditGroupId(e.target.value)} 
                                className="mb-2" 
                                disabled={!isMapEditing}
                            >
                                <option value="">- Sin Grupo -</option>
                                {zoneGroups
                                    .filter(g => !editBaseId || String(g.base_id) === String(editBaseId))
                                    .map(g => <option key={g.id} value={g.id}>{g.name}</option>)
                                }
                            </Form.Select>
                            {isMapEditing && (
                                <>
                                    <div className="text-muted small mb-2" style={{fontSize:'11px'}}>💡 Ahora puedes mover o editar el polígono en el mapa.</div>
                                    <Button size="sm" variant="success" className="w-100 fw-bold" onClick={handleUpdateName}>💾 Guardar Cambios</Button>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                )}
                <div style={{maxHeight:'400px', overflowY:'auto'}}>{Object.keys(groupedZones).map(country => (<div key={country} className="mb-2"><div className="bg-dark text-white px-2 py-1 rounded small fw-bold" onClick={() => setCollapsedGroups({...collapsedGroups, [country]: !collapsedGroups[country]})}><span>{country}</span><Button size="sm" variant="danger" className="py-0 px-1" style={{fontSize:'0.7rem'}} onClick={(e) => { e.stopPropagation(); handleBulkDelete(Object.values(groupedZones[country]).flat(), country); }}>🗑️</Button></div>{!collapsedGroups[country] && Object.keys(groupedZones[country]).map(city => (<div key={city} className="ms-2 mt-1"><div className="bg-light px-2 py-1 small fw-bold d-flex justify-content-between align-items-center"><span>{city}</span><Button size="sm" variant="outline-danger" className="py-0 px-1 border-0" onClick={(e) => { e.stopPropagation(); handleBulkDelete(groupedZones[country][city], city); }}>🗑️</Button></div><ListGroup variant="flush" className="small">{groupedZones[country][city].map(z => (<ListGroup.Item key={z.id} action active={selectedZone?.id === z.id} onClick={() => handleZoneClick(z)} className="d-flex justify-content-between ps-3"><span>{z.name}</span><span className="text-danger" onClick={(e) => { e.stopPropagation(); handleDelete(z.id); }}>×</span></ListGroup.Item>))}</ListGroup></div>))}</div>))}</div>
            </Col>
            <Col md={8}>
                <div style={{height:'600px', border:'1px solid #ddd'}}>
                    <GoogleMap 
                        mapContainerStyle={{width:'100%', height:'100%'}} 
                        center={MAP_CENTER} 
                        zoom={10} 
                        onLoad={onMapLoad}
                    >
                        {zones.map(z => { 
                            try { 
                                if (!z.polygon_coords) return null;
                                const path = JSON.parse(z.polygon_coords); 
                                if (!Array.isArray(path) || path.length === 0) return null;
                                const isSel = selectedZone?.id === z.id; 
                                return (
                                    <PolygonF 
                                        key={z.id} 
                                        paths={path} 
                                        editable={isSel && isMapEditing} 
                                        draggable={isSel && isMapEditing} 
                                        onLoad={(poly) => onPolygonLoad(poly, z)} 
                                        options={{ 
                                            fillColor: isSel ? (isMapEditing ? '#ffc107' : '#0d6efd') : '#FF0000', 
                                            fillOpacity: isSel ? 0.2 : 0.1, 
                                            strokeWeight: isSel ? 2 : 1 
                                        }} 
                                        onClick={() => { 
                                            setSelectedZone(z); 
                                            setEditName(z.name); 
                                            setEditBaseId(z.base_id || ''); 
                                        }} 
                                    />
                                );
                            } catch { 
                                return null; 
                            } 
                        })}
                        {isDrawing && (
                            <DrawingManagerF 
                                onOverlayComplete={onOverlayComplete} 
                                options={{ 
                                    drawingControl: true, 
                                    drawingControlOptions: {
                                        position: window.google?.maps?.ControlPosition?.TOP_CENTER,
                                        drawingModes: [
                                            window.google?.maps?.drawing?.OverlayType?.POLYGON || 'polygon'
                                        ]
                                    }
                                }} 
                            />
                        )}
                    </GoogleMap>
                </div>
            </Col>
            {showKmlModal && (
                <KmlImportModal
                    show={showKmlModal}
                    onHide={() => setShowKmlModal(false)}
                    bases={bases}
                    onRefresh={load}
                />
            )}
            {showGroupsModal && (
                <ZoneGroupsModal
                    show={showGroupsModal}
                    onHide={() => setShowGroupsModal(false)}
                    bases={bases}
                    onRefresh={load}
                />
            )}
        </Row>
    );
};

// --- MATRIX MANAGER ---
const MatrixManager = () => {
    const [tariffs, setTariffs] = useState([]);
    const [zones, setZones] = useState([]);
    const [zoneGroups, setZoneGroups] = useState([]);
    
    const renderGroupTooltip = (group) => {
        const groupZones = zones.filter(z => String(z.group_id) === String(group.id));
        const names = groupZones.map(z => z.name);
        return (
            <Tooltip id={`tooltip-group-${group.id}`}>
                <div className="text-start">
                    <strong>Zonas en {group.name}:</strong>
                    {names.length > 0 ? (
                        <ul className="mb-0 ps-3 mt-1">
                            {names.map((name, i) => <li key={i}>{name}</li>)}
                        </ul>
                    ) : (
                        <div className="small text-muted mt-1">Sin zonas asociadas</div>
                    )}
                </div>
            </Tooltip>
        );
    };

    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [clients, setClients] = useState([]);
    const [bases, setBases] = useState([]);
    const [exchangeRates, setExchangeRates] = useState([]);
    const [providers, setProviders] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    
    // Filtros de tabla
    const [searchOrigin, setSearchOrigin] = useState('');
    const [searchDestination, setSearchDestination] = useState('');
    const [filterBaseId, setFilterBaseId] = useState('');
    const [filterVehicleId, setFilterVehicleId] = useState('');
    const [filterClientId, setFilterClientId] = useState('');
    const [filterProviderId, setFilterProviderId] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [loadingTariffs, setLoadingTariffs] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [simulatedDiscount, setSimulatedDiscount] = useState(0);

    // Modal de Exportación Excel
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportForm, setExportForm] = useState({
        export_type: 'client',
        client_ids: [],
        provider_id: '',
        base_ids: [],
        include_clp: true,
        include_usd: true,
        include_local: true
    });

    const handleExportExcel = async () => {
        setExportLoading(true);
        try {
            const params = {
                export_type: exportForm.export_type,
                include_clp: exportForm.include_clp,
                include_usd: exportForm.include_usd,
                include_local: exportForm.include_local
            };
            if (exportForm.client_ids && exportForm.client_ids.length > 0) params.client_ids = exportForm.client_ids.join(',');
            if (exportForm.provider_id) params.provider_id = parseInt(exportForm.provider_id);
            if (exportForm.base_ids && exportForm.base_ids.length > 0) params.base_ids = exportForm.base_ids.join(',');

            const blob = await exportTariffsToExcel(params);
            
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            
            const dateStr = new Date().toISOString().split('T')[0];
            const suffix = exportForm.export_type === 'client' ? 'Clientes' : (exportForm.export_type === 'provider' ? 'Proveedores' : 'Interno');
            link.setAttribute('download', `Tarifario_${suffix}_${dateStr}.xlsx`);
            
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            
            setShowExportModal(false);
        } catch (e) {
            console.error(e);
            alert("Error al generar y descargar el Excel. Intente nuevamente.");
        } finally {
            setExportLoading(false);
        }
    };

    // Modal Asignar Genius Rápido
    const [showGeniusBulkModal, setShowGeniusBulkModal] = useState(false);
    const [bulkGeniusPercent, setBulkGeniusPercent] = useState(5);
    const [bulkGeniusBaseId, setBulkGeniusBaseId] = useState('');
    const [bulkGeniusProviderId, setBulkGeniusProviderId] = useState('');
    const [isSavingBulkGenius, setIsSavingBulkGenius] = useState(false);

    const handleApplyBulkGenius = async () => {
        const disc = parseFloat(bulkGeniusPercent);
        if (isNaN(disc) || (disc !== 0 && (disc < 5 || disc > 10))) {
            alert("El descuento Genius debe ser 0% (desactivado) o estar entre 5% y 10%.");
            return;
        }
        try {
            setIsSavingBulkGenius(true);
            const payload = {
                genius_discount: disc,
                genius_shared: true
            };
            if (bulkGeniusProviderId) payload.provider_id = parseInt(bulkGeniusProviderId);
            if (bulkGeniusBaseId) payload.base_id = parseInt(bulkGeniusBaseId);

            const res = await bulkUpdateGeniusDiscount(payload);
            alert(res.message || `Descuentos Genius actualizados a ${disc}%.`);
            setShowGeniusBulkModal(false);
            fetchTariffs();
        } catch (e) {
            console.error(e);
            const detail = e?.response?.data?.detail || "Error al actualizar descuentos Genius en lote.";
            alert(`❌ Error: ${detail}`);
        } finally {
            setIsSavingBulkGenius(false);
        }
    };

    const initialForm = { 
        origin_type: 'zone',
        destination_type: 'zone',
        origin_zone_id: '', 
        destination_zone_id: '', 
        origin_group_id: '',
        destination_group_id: '',
        vehicle_type_id: '', 
        selected_vehicle_ids: [],
        prices_map: {}, // { vehicle_id: { price: 0, cost_local: 0, sale_usd: 0, cost_usd: 0, cost_clp: 0 } }
        min_lead_time_minutes: 0, 
        client_ids: 'ALL',
        is_bidirectional: false,
        provider_id: '',
        booking_genius_discount: 0,
        booking_genius_shared: true
    }; 
    const [formData, setFormData] = useState(initialForm);

    const load = useCallback(async () => { 
        try {
            // Ya no cargamos todas las tarifas por defecto para evitar timeouts y demora
            const zData = await getZones(); setZones(zData);
            const zgData = await getZoneGroups(); setZoneGroups(zgData);
            const vtData = await getVehicleTypes(); setVehicleTypes(vtData);
            const cData = await getClients(); setClients(cData);
            const bData = await getBases(); setBases(bData);
            const rData = await getExchangeRates(); setExchangeRates(rData);
            const pData = await getProviders(); setProviders(pData);
        } catch (e) {
            console.error("Error cargando metadatos de matriz:", e);
        }
    }, []);

    const fetchTariffs = async () => {
        setLoadingTariffs(true);
        setHasSearched(true);
        try {
            const params = {};
            if (filterBaseId) params.base_id = parseInt(filterBaseId);
            if (filterClientId) params.client_id = parseInt(filterClientId);
            if (filterProviderId) params.provider_id = parseInt(filterProviderId);
            
            const tData = await getTariffs(params);
            setTariffs(tData);
        } catch (e) {
            console.error("Error al buscar tarifas:", e);
            alert("Error al buscar tarifas. Intente nuevamente.");
        } finally {
            setLoadingTariffs(false);
        }
    };
    useEffect(() => { load(); }, [load]);

    const filteredTariffs = useMemo(() => {
        return tariffs.filter(t => {
            const originName = (t.origin_zone?.name || t.origin_group?.name || '').toLowerCase();
            const destName = (t.destination_zone?.name || t.destination_group?.name || '').toLowerCase();

            const matchesOrigin = !searchOrigin || originName.includes(searchOrigin.toLowerCase().trim());
            const matchesDestination = !searchDestination || destName.includes(searchDestination.toLowerCase().trim());

            const originBaseId = t.origin_zone?.base_id || t.origin_group?.base_id;
            const matchesBase = !filterBaseId || String(originBaseId) === String(filterBaseId);
            const matchesVehicle = !filterVehicleId || String(t.vehicle_type_id) === String(filterVehicleId);
            // Globales (null/'ALL') siempre aparecen al filtrar por cliente
            const matchesClient = !filterClientId ||
                !t.client_ids ||
                t.client_ids === 'ALL' ||
                t.client_ids.split(',').map(s => s.trim()).includes(String(filterClientId));
            const matchesProvider = !filterProviderId || String(t.provider_id) === String(filterProviderId);
            return matchesOrigin && matchesDestination && matchesBase && matchesVehicle && matchesClient && matchesProvider;
        });
    }, [tariffs, searchOrigin, searchDestination, filterBaseId, filterVehicleId, filterClientId, filterProviderId]);

    // Helper para cálculos por fila (vehículo)
    const updateVehiclePrice = (vId, field, value) => {
        const provider = providers.find(p => String(p.id) === String(formData.provider_id));
        const baseId = formData.origin_type === 'zone' 
            ? zones.find(z => String(z.id) === String(formData.origin_zone_id))?.base_id
            : zoneGroups.find(g => String(g.id) === String(formData.origin_group_id))?.base_id;
        const base = bases.find(b => b.id === baseId);
        const localCurrency = provider?.currency_code || base?.currency_code || 'CLP';
        const rateCLP = exchangeRates.find(r => r.currency_from === 'USD' && r.currency_to === 'CLP')?.rate || 1;
        const rateLocal = localCurrency === 'USD' ? 1 : (exchangeRates.find(r => r.currency_from === 'USD' && r.currency_to === localCurrency)?.rate || 1);

        setFormData(prev => {
            const current = prev.prices_map[vId] || { price: 0, cost_local: 0, sale_usd: 0, cost_usd: 0, cost_clp: 0, max_pax: '', max_suitcases: '' };
            const updated = { ...current, [field]: value };

            if (field === 'price') {
                updated.sale_usd = (parseFloat(value || 0) / rateCLP).toFixed(2);
            } else if (field === 'sale_usd') {
                updated.price = Math.round(parseFloat(value || 0) * rateCLP);
            } else if (field === 'cost_local') {
                const costUsd = parseFloat(value || 0) / rateLocal;
                updated.cost_usd = costUsd.toFixed(2);
                updated.cost_clp = Math.round(costUsd * rateCLP);
            }

            return { ...prev, prices_map: { ...prev.prices_map, [vId]: updated } };
        });
    };
    
    const handleSave = async () => {
        try {
            const vehiclesToProcess = isEditing ? [formData.vehicle_type_id.toString()] : formData.selected_vehicle_ids;
            
            for (const vId of vehiclesToProcess) {
                const vData = formData.prices_map[vId] || { price: 0, cost_local: 0, sale_usd: 0, cost_usd: 0, cost_clp: 0, max_pax: '', max_suitcases: '' };

                const payload = { 
                    origin_zone_id: formData.origin_type === 'zone' ? parseInt(formData.origin_zone_id) : null, 
                    destination_zone_id: formData.destination_type === 'zone' ? parseInt(formData.destination_zone_id) : null, 
                    origin_group_id: formData.origin_type === 'group' ? parseInt(formData.origin_group_id) : null, 
                    destination_group_id: formData.destination_type === 'group' ? parseInt(formData.destination_group_id) : null, 
                    vehicle_type_id: parseInt(vId),
                    price: parseFloat(vData.price || 0), 
                    cost_usd: parseFloat(vData.cost_usd || 0), 
                    sale_usd: parseFloat(vData.sale_usd || 0), 
                    cost_local: parseFloat(vData.cost_local || 0), 
                    provider_cost_clp: parseFloat(vData.cost_clp || 0), 
                    client_ids: (formData.client_ids === 'ALL' || formData.client_ids === 'NONE') ? null : formData.client_ids,
                    max_pax: vData.max_pax !== '' && vData.max_pax !== undefined && vData.max_pax !== null ? parseInt(vData.max_pax) : null,
                    max_suitcases: vData.max_suitcases !== '' && vData.max_suitcases !== undefined && vData.max_suitcases !== null ? parseInt(vData.max_suitcases) : null,
                    provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
                    booking_genius_discount: formData.booking_genius_discount !== undefined && formData.booking_genius_discount !== '' ? parseFloat(formData.booking_genius_discount) : 0.0,
                    booking_genius_shared: formData.booking_genius_shared !== undefined ? formData.booking_genius_shared : true
                };

                if (isEditing) {
                    await updateTariff(editId, payload);
                } else {
                    await createTariff(payload);
                    if (formData.is_bidirectional) {
                        await createTariff({ 
                            ...payload, 
                            origin_zone_id: payload.destination_zone_id, 
                            destination_zone_id: payload.origin_zone_id,
                            origin_group_id: payload.destination_group_id,
                            destination_group_id: payload.origin_group_id
                        });
                    }
                }
            }
            setShowModal(false); load(); fetchTariffs();
        } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || 'Error desconocido al guardar la tarifa.';
            console.error('Error guardando tarifa:', e);
            alert(`❌ Error al guardar: ${detail}`);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Borrar tarifa?')) {
            setTariffs(prev => prev.filter(t => t.id !== id));
            try { await deleteTariff(id); load(); fetchTariffs(); } catch(e) { alert("Error al borrar"); load(); fetchTariffs(); }
        }
    };

    const handleEdit = (t) => {
        const vId = t.vehicle_type_id.toString();
        setFormData({ 
            origin_type: t.origin_group_id ? 'group' : 'zone',
            destination_type: t.destination_group_id ? 'group' : 'zone',
            origin_zone_id: t.origin_zone_id || '', 
            destination_zone_id: t.destination_zone_id || '', 
            origin_group_id: t.origin_group_id || '',
            destination_group_id: t.destination_group_id || '',
            vehicle_type_id: t.vehicle_type_id,
            selected_vehicle_ids: [vId],
            prices_map: {
                [vId]: { 
                    price: t.price, 
                    cost_local: t.cost_local, 
                    sale_usd: t.sale_usd, 
                    cost_usd: t.cost_usd, 
                    cost_clp: t.provider_cost_clp,
                    max_pax: t.max_pax !== null && t.max_pax !== undefined ? t.max_pax.toString() : '',
                    max_suitcases: t.max_suitcases !== null && t.max_suitcases !== undefined ? t.max_suitcases.toString() : ''
                }
            },
            client_ids: t.client_ids || 'ALL',
            is_bidirectional: false,
            provider_id: t.provider_id ? t.provider_id.toString() : '',
            booking_genius_discount: t.booking_genius_discount || 0,
            booking_genius_shared: t.booking_genius_shared !== undefined ? t.booking_genius_shared : true
        });
        setSimulatedDiscount(0);
        setIsEditing(true); setEditId(t.id); setShowModal(true);
    };

    const getSelectedBaseId = () => {
        if (formData.origin_type === 'zone') {
            return zones.find(z => String(z.id) === String(formData.origin_zone_id))?.base_id;
        } else {
            return zoneGroups.find(g => String(g.id) === String(formData.origin_group_id))?.base_id;
        }
    };
    const selectedBaseId = getSelectedBaseId();
    const isRegional = selectedBaseId && selectedBaseId !== 1;
    const baseProviders = providers.filter(p => (p.base_id === selectedBaseId || p.bases?.some(b => b.id === selectedBaseId)) && p.is_active);

    return (
        <div>
            {/* BARRA DE FILTROS INTELIGENTE */}
            <Card className="mb-3 border-0 bg-light shadow-sm">
                <Card.Body className="p-3">
                    <Row className="g-2 mb-2 align-items-end">
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Origen</Form.Label>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                                <Form.Control 
                                    className="border-start-0"
                                    placeholder="Ej: SCL, Las Condes..." 
                                    value={searchOrigin} 
                                    onChange={e => setSearchOrigin(e.target.value)} 
                                    onKeyDown={e => { if (e.key === 'Enter') fetchTariffs(); }}
                                />
                                {searchOrigin && (
                                    <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0 bg-white text-muted"
                                        onClick={() => setSearchOrigin('')}
                                        style={{ borderColor: '#ced4da' }}
                                    >
                                        ✕
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Destino</Form.Label>
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-white border-end-0"><Search size={14}/></InputGroup.Text>
                                <Form.Control 
                                    className="border-start-0"
                                    placeholder="Ej: Providencia, Hotel..." 
                                    value={searchDestination} 
                                    onChange={e => setSearchDestination(e.target.value)} 
                                    onKeyDown={e => { if (e.key === 'Enter') fetchTariffs(); }}
                                />
                                {searchDestination && (
                                    <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0 bg-white text-muted"
                                        onClick={() => setSearchDestination('')}
                                        style={{ borderColor: '#ced4da' }}
                                    >
                                        ✕
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Cliente</Form.Label>
                            <SearchableSelect 
                                options={clients}
                                value={filterClientId}
                                onChange={val => setFilterClientId(val)}
                                placeholder="Todos / Global"
                                defaultOptionLabel="-- Todos / Global --"
                                searchPlaceholder="🔍 Buscar cliente..."
                                size="sm"
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Proveedor</Form.Label>
                            <SearchableSelect 
                                options={providers}
                                value={filterProviderId}
                                onChange={val => setFilterProviderId(val)}
                                placeholder="Todos los Proveedores"
                                defaultOptionLabel="-- Todos los Proveedores --"
                                searchPlaceholder="🔍 Buscar proveedor..."
                                size="sm"
                            />
                        </Col>
                    </Row>
                    <Row className="g-2 align-items-end">
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Sucursal / Base</Form.Label>
                            <Form.Select size="sm" value={filterBaseId} onChange={e => setFilterBaseId(e.target.value)}>
                                <option value="">Todas las Bases</option>
                                {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Label className="small fw-bold text-muted ps-1 mb-1">Categoría Vehículo</Form.Label>
                            <Form.Select size="sm" value={filterVehicleId} onChange={e => setFilterVehicleId(e.target.value)}>
                                <option value="">Todos los Vehículos</option>
                                {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                            </Form.Select>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="primary" 
                                    size="sm" 
                                    className="flex-grow-1 fw-bold d-flex align-items-center justify-content-center gap-1" 
                                    onClick={fetchTariffs} 
                                    disabled={loadingTariffs}
                                >
                                    {loadingTariffs ? 'Buscando...' : '🔍 Buscar'}
                                </Button>
                                {(searchOrigin || searchDestination || filterBaseId || filterVehicleId || filterClientId || filterProviderId) && (
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm" 
                                        className="fw-bold d-flex align-items-center justify-content-center gap-1 px-3"
                                        onClick={() => {
                                            setSearchOrigin('');
                                            setSearchDestination('');
                                            setFilterBaseId('');
                                            setFilterVehicleId('');
                                            setFilterClientId('');
                                            setFilterProviderId('');
                                        }}
                                        title="Limpiar todos los filtros"
                                    >
                                        🧹 Limpiar
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 px-1">
                <div className="text-muted small">
                    {hasSearched ? (
                        <span>Mostrando <b>{filteredTariffs.length}</b> de {tariffs.length} tarifas cargadas</span>
                    ) : (
                        <span className="text-muted italic">💡 Seleccione filtros y presione Buscar para listar tarifas.</span>
                    )}
                </div>
                <div className="d-flex gap-2">
                    <Button variant="success" size="sm" className="fw-bold" onClick={() => { setFormData(initialForm); setIsEditing(false); setSimulatedDiscount(0); setShowModal(true); }}>
                        + Nueva Tarifa
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        style={{ backgroundColor: '#003580', borderColor: '#003580' }}
                        className="fw-bold text-white d-flex align-items-center justify-content-center gap-1 shadow-sm"
                        onClick={() => setShowGeniusBulkModal(true)}
                    >
                        ⭐ Asignar Genius Rápido
                    </Button>
                    <Button variant="outline-success" size="sm" className="fw-bold d-flex align-items-center justify-content-center gap-1" onClick={() => {
                        setExportForm({
                            export_type: 'client',
                            client_ids: [],
                            provider_id: filterProviderId || '',
                            base_ids: filterBaseId ? [parseInt(filterBaseId)] : [],
                            include_clp: true,
                            include_usd: true,
                            include_local: true
                        });
                        setShowExportModal(true);
                    }}>
                        📥 Exportar Excel
                    </Button>
                </div>
            </div>

            {loadingTariffs ? (
                <div className="text-center py-5 shadow-sm bg-white rounded border">
                    <div className="spinner-border text-primary mb-2" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                    <div className="text-muted fw-medium mt-2">Cargando tarifas desde la base de datos...</div>
                </div>
            ) : !hasSearched ? (
                <Card className="border-0 shadow-sm bg-white text-center py-5">
                    <Card.Body>
                        <div className="display-4 text-primary opacity-50 mb-3">🔍</div>
                        <h6 className="fw-bold text-dark">Matriz de Tarifas</h6>
                        <p className="text-muted small mx-auto" style={{ maxWidth: '400px' }}>
                            Para mejorar la velocidad de carga, no listamos todas las tarifas al ingresar. Seleccione filtros regionales y haga clic en <strong>Buscar</strong>.
                        </p>
                        <div className="d-flex justify-content-center gap-2 mt-3">
                            <Form.Select size="sm" value={filterBaseId} onChange={e => setFilterBaseId(e.target.value)} style={{ width: '180px' }}>
                                <option value="">Todas las Bases</option>
                                {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </Form.Select>
                            <Button variant="primary" size="sm" className="fw-bold px-3" onClick={fetchTariffs}>
                                Buscar
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            ) : filteredTariffs.length === 0 ? (
                <div className="text-center py-5 shadow-sm bg-white rounded border">
                    <div className="display-5 text-muted opacity-50 mb-3">📭</div>
                    <div className="fw-bold text-dark">No se encontraron tarifas</div>
                    <div className="text-muted small mt-1">Intente cambiando los filtros aplicados.</div>
                </div>
            ) : (
                <Table striped hover responsive size="sm" className="small align-middle shadow-sm bg-white rounded overflow-hidden">
                    <thead className="bg-dark text-white"><tr><th className="ps-3">Ruta</th><th>Vehículo</th><th>Proveedor</th><th>Capacidad</th><th>Venta (CLP)</th><th>Venta (USD)</th><th>Costo (Local)</th><th>Costo (CLP)</th><th>% Genius</th><th>Cliente</th><th className="text-end pe-3">Acciones</th></tr></thead>
                    <tbody>{filteredTariffs.map(t => {
                    const originBaseId = t.origin_zone?.base_id || t.origin_group?.base_id;
                    const currencyCode = t.provider?.currency_code || bases.find(b => b.id === originBaseId)?.currency_code || 'CLP';
                    const costClp = parseFloat(t.provider_cost_clp) || 0;
                    const priceClp = parseFloat(t.price) || 0;
                    const isLoss = costClp > priceClp && costClp > 0 && !!t.provider_id;

                    return (
                        <tr key={t.id} className={isLoss ? "table-danger border-danger" : ""}>
                            <td className="ps-3">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="fw-bold">
                                        {t.origin_zone ? (
                                            t.origin_zone.name
                                        ) : t.origin_group ? (
                                            <OverlayTrigger placement="top" overlay={renderGroupTooltip(t.origin_group)}>
                                                <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                                                    📁 {t.origin_group.name}
                                                </span>
                                            </OverlayTrigger>
                                        ) : '---'}
                                    </span>
                                    <span className="text-primary opacity-50">➔</span>
                                    <span className="fw-bold">
                                        {t.destination_zone ? (
                                            t.destination_zone.name
                                        ) : t.destination_group ? (
                                            <OverlayTrigger placement="top" overlay={renderGroupTooltip(t.destination_group)}>
                                                <span style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                                                    📁 {t.destination_group.name}
                                                </span>
                                            </OverlayTrigger>
                                        ) : '---'}
                                    </span>
                                    {originBaseId && (
                                        <Badge bg={isLoss ? "danger" : "light"} text={isLoss ? "white" : "dark"} className="border ms-1 fw-normal" style={{fontSize:'9px'}}>
                                            {bases.find(b => b.id === originBaseId)?.name}
                                        </Badge>
                                    )}
                                </div>
                            </td>
                            <td><Badge bg="info" text="dark">{t.vehicle_type?.name}</Badge></td>
                            <td>
                                {t.provider?.name ? (
                                    <Badge bg={isLoss ? "danger" : "secondary"} text="white">{t.provider.name}</Badge>
                                ) : (
                                    <span className="text-muted small">General</span>
                                )}
                            </td>
                            <td>
                                <div className="small text-muted">
                                    👤 {t.max_pax !== null && t.max_pax !== undefined ? t.max_pax : (t.vehicle_type?.capacity || '---')}<br/>
                                    🧳 {t.max_suitcases !== null && t.max_suitcases !== undefined ? t.max_suitcases : (t.vehicle_type?.name && (t.vehicle_type.name.toUpperCase().includes("STANDARD") || t.vehicle_type.name.toUpperCase() === "EXECUTIVE") ? '2' : '4')}
                                </div>
                            </td>
                            <td className={isLoss ? "text-danger fw-bold" : "text-success fw-bold"}>
                                ${t.price?.toLocaleString()}
                                {isLoss && <Badge bg="danger" className="ms-1 px-1 py-0.5" style={{ fontSize: '8px' }}>MARGEN NEGATIVO</Badge>}
                            </td>
                            <td className={isLoss ? "text-danger small fw-bold" : "text-success small"}>US$ {t.sale_usd}</td>
                            <td className={isLoss ? "text-danger fw-bold" : ""}>
                                {t.cost_local} <small className="text-muted">{currencyCode}</small>
                                {t.last_cost_change_at && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`change-tooltip-admin-${t.id}`}>
                                                <strong>Costo Editado</strong><br/>
                                                Por: {t.last_cost_change_user || '—'}<br/>
                                                Fecha: {new Date(t.last_cost_change_at).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}<br/>
                                                Valor: {t.last_cost_change_prev !== null && t.last_cost_change_prev !== undefined ? t.last_cost_change_prev.toLocaleString() : '—'} ➔ {t.last_cost_change_new !== null && t.last_cost_change_new !== undefined ? t.last_cost_change_new.toLocaleString() : '—'} {currencyCode}
                                            </Tooltip>
                                        }
                                    >
                                        <span className="ms-1 cursor-help" style={{ color: '#0d6efd', fontSize: '11px' }}>🕒</span>
                                    </OverlayTrigger>
                                )}
                            </td>
                            <td className={isLoss ? "text-danger fw-bold" : "text-muted"}>
                                ${t.provider_cost_clp?.toLocaleString()}
                                {isLoss && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`loss-tooltip-${t.id}`}>
                                                ⚠️ Costo (${costClp.toLocaleString()}) supera a la Venta (${priceClp.toLocaleString()}). Esta opción quedará desactivada en Booking.com.
                                            </Tooltip>
                                        }
                                    >
                                        <span className="ms-1 cursor-help" style={{ color: '#dc3545', fontWeight: 'bold' }}>⚠️ PÉRDIDA</span>
                                    </OverlayTrigger>
                                )}
                            </td>
                            <td>
                                {t.booking_genius_discount && t.booking_genius_discount >= 5 ? (
                                    <Badge bg="primary" style={{ backgroundColor: '#003580' }} className="fw-bold px-2 py-1 shadow-sm">
                                        ⭐ {Math.round(t.booking_genius_discount)}%
                                    </Badge>
                                ) : (
                                    <span className="text-muted" style={{ fontSize: '11px' }}>0%</span>
                                )}
                            </td>
                            <td>{getClientDisplay(t.client_ids, clients)}</td>
                            <td className="text-end pe-3">
                                <Button size="sm" variant="outline-primary" className="py-0 me-1 border-0" onClick={() => handleEdit(t)}>✏️</Button>
                                <Button size="sm" variant="outline-danger" className="py-0 border-0" onClick={() => handleDelete(t.id)}>×</Button>
                            </td>
                        </tr>
                    );
                })}</tbody>
            </Table>
            )}

            <Modal show={showModal} onHide={() => { setShowModal(false); setClientSearch(''); setSimulatedDiscount(0); }} size="xl" backdrop="static">
                <Modal.Header closeButton className="bg-light"><Modal.Title className="h6 fw-bold">{isEditing ? 'Editar' : 'Nueva'} Configuración de Tarifa</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Origen</Form.Label>
                                <div className="d-flex gap-3 small">
                                    <Form.Check 
                                        type="radio" 
                                        label="Zona" 
                                        id="origin_type_zone"
                                        name="origin_type" 
                                        checked={formData.origin_type === 'zone'} 
                                        onChange={() => setFormData({...formData, origin_type: 'zone', origin_group_id: ''})} 
                                        className="mb-0"
                                    />
                                    <Form.Check 
                                        type="radio" 
                                        label="Grupo" 
                                        id="origin_type_group"
                                        name="origin_type" 
                                        checked={formData.origin_type === 'group'} 
                                        onChange={() => setFormData({...formData, origin_type: 'group', origin_zone_id: ''})} 
                                        className="mb-0"
                                    />
                                </div>
                            </div>
                            {formData.origin_type === 'zone' ? (
                                <SearchableSelect 
                                    options={zones} 
                                    value={formData.origin_zone_id} 
                                    onChange={val => setFormData({...formData, origin_zone_id: val})} 
                                />
                            ) : (
                                <Form.Select 
                                    size="sm" 
                                    value={formData.origin_group_id} 
                                    onChange={e => setFormData({...formData, origin_group_id: e.target.value})}
                                >
                                    <option value="">- Seleccionar Grupo -</option>
                                    {zoneGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </Form.Select>
                            )}
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Destino</Form.Label>
                                <div className="d-flex gap-3 small">
                                    <Form.Check 
                                        type="radio" 
                                        label="Zona" 
                                        id="destination_type_zone"
                                        name="destination_type" 
                                        checked={formData.destination_type === 'zone'} 
                                        onChange={() => setFormData({...formData, destination_type: 'zone', destination_group_id: ''})} 
                                        className="mb-0"
                                    />
                                    <Form.Check 
                                        type="radio" 
                                        label="Grupo" 
                                        id="destination_type_group"
                                        name="destination_type" 
                                        checked={formData.destination_type === 'group'} 
                                        onChange={() => setFormData({...formData, destination_type: 'group', destination_zone_id: ''})} 
                                        className="mb-0"
                                    />
                                </div>
                            </div>
                            {formData.destination_type === 'zone' ? (
                                <SearchableSelect 
                                    options={zones} 
                                    value={formData.destination_zone_id} 
                                    onChange={val => setFormData({...formData, destination_zone_id: val})} 
                                />
                            ) : (
                                <Form.Select 
                                    size="sm" 
                                    value={formData.destination_group_id} 
                                    onChange={e => setFormData({...formData, destination_group_id: e.target.value})}
                                >
                                    <option value="">- Seleccionar Grupo -</option>
                                    {zoneGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                </Form.Select>
                            )}
                        </Col>
                    </Row>
                    
                    {isRegional && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label className="small fw-bold">Proveedor de la Base (Regional)</Form.Label>
                                <Form.Select 
                                    size="sm"
                                    value={formData.provider_id || ''} 
                                    onChange={e => setFormData({...formData, provider_id: e.target.value})}
                                >
                                    <option value="">Tarifa General (Ningún Proveedor)</option>
                                    {baseProviders.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted small">
                                    Al seleccionar un proveedor, esta tarifa se considerará como su tarifa de venta/costo específica.
                                </Form.Text>
                            </Col>
                        </Row>
                    )}

                    {!isEditing && (
                        <div className="mb-3 p-2 bg-primary bg-opacity-10 rounded border border-primary border-opacity-25">
                            <Form.Check 
                                type="switch"
                                label={<span className="fw-bold text-primary">Crear también el sentido inverso (Espejo)</span>}
                                checked={formData.is_bidirectional}
                                onChange={e => setFormData({...formData, is_bidirectional: e.target.checked})}
                            />
                            <small className="text-muted d-block ms-4">Si se marca, el sistema creará automáticamente la ruta de vuelta con el mismo precio.</small>
                        </div>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Label className="small fw-bold">Seleccionar Vehículos</Form.Label>
                            {isEditing ? (
                                <Form.Select value={formData.vehicle_type_id} onChange={e => setFormData({...formData, vehicle_type_id: e.target.value})}>
                                    {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </Form.Select>
                            ) : (
                                <div className="border rounded p-2 bg-white d-flex flex-wrap gap-2" style={{maxHeight: '100px', overflowY: 'auto'}}>
                                    {vehicleTypes.map(v => (
                                        <Form.Check 
                                            key={v.id} 
                                            type="checkbox" 
                                            label={v.name} 
                                            id={`vt-${v.id}`} 
                                            checked={formData.selected_vehicle_ids.includes(v.id.toString())} 
                                            onChange={e => {
                                                const vId = v.id.toString();
                                                const isChecked = e.target.checked;
                                                
                                                setFormData(prev => {
                                                    const newIds = isChecked 
                                                        ? [...prev.selected_vehicle_ids, vId] 
                                                        : prev.selected_vehicle_ids.filter(id => id !== vId);
                                                    
                                                    // Inicializar precios para el nuevo vehículo si no existen
                                                    const newPricesMap = { ...prev.prices_map };
                                                    if (isChecked && !newPricesMap[vId]) {
                                                        newPricesMap[vId] = { price: 0, cost_local: 0, cost_clp: 0, sale_usd: 0, max_pax: '', max_suitcases: '' };
                                                    }
                                                    
                                                    return { ...prev, selected_vehicle_ids: newIds, prices_map: newPricesMap };
                                                });
                                            }} 
                                            className="small me-2" 
                                        />
                                    ))}
                                </div>
                            )}
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Clientes / Convenios</Form.Label>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 text-decoration-none small text-primary fw-bold"
                                    onClick={() => {
                                        const allSelected = (formData.client_ids === 'ALL' || !formData.client_ids) || 
                                            (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length);
                                        setFormData(prev => ({ ...prev, client_ids: allSelected ? 'NONE' : 'ALL' }));
                                    }}
                                >
                                    {((formData.client_ids === 'ALL' || !formData.client_ids) || 
                                      (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length)) 
                                        ? '✕ Deseleccionar todos' 
                                        : '✓ Seleccionar todos'}
                                </Button>
                            </div>
                            <Form.Control 
                                size="sm" 
                                placeholder="🔍 Buscar cliente..." 
                                className="mb-2" 
                                value={clientSearch}
                                onChange={e => setClientSearch(e.target.value)}
                            />
                            <div className="border rounded p-2 bg-white d-flex flex-wrap gap-2" style={{maxHeight: '100px', overflowY: 'auto'}}>
                                {clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).map(c => {
                                    const cId = c.id.toString();
                                    const isSelected = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                        ? true 
                                        : (formData.client_ids === 'NONE' ? false : formData.client_ids.split(',').includes(cId));
                                    return (
                                        <Form.Check 
                                            key={c.id} 
                                            type="checkbox" 
                                            label={c.name} 
                                            id={`cli-${c.id}`} 
                                            checked={isSelected} 
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                let currentIds = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                                    ? clients.map(cl => cl.id.toString()) 
                                                    : (formData.client_ids === 'NONE' ? [] : formData.client_ids.split(',').filter(Boolean));
                                                
                                                if (isChecked) {
                                                    if (!currentIds.includes(cId)) currentIds.push(cId);
                                                } else {
                                                    currentIds = currentIds.filter(id => id !== cId);
                                                }
                                                
                                                if (currentIds.length === 0) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'NONE' }));
                                                } else if (currentIds.length === clients.length) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'ALL' }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, client_ids: currentIds.join(',') }));
                                                }
                                            }} 
                                            className="small me-2" 
                                        />
                                    );
                                })}
                            </div>
                        </Col>
                        <Col md={2} className="pt-4 text-end">
                            {!isEditing && <Form.Check type="switch" label={<span className="small fw-bold text-primary">Espejo</span>} checked={formData.is_bidirectional} onChange={e => setFormData({...formData, is_bidirectional: e.target.checked})} />}
                        </Col>
                    </Row>

                    {((!isEditing && formData.selected_vehicle_ids.length > 0) || (isEditing)) && (
                        <div className="mt-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="small fw-bold mb-0">Desglose de Precios por Categoría:</h6>
                                <div className="d-flex align-items-center" style={{ width: '280px' }}>
                                    <span className="small text-muted me-2" style={{ whiteSpace: 'nowrap' }}>Simular Descuento Venta:</span>
                                    <InputGroup size="sm">
                                        <Form.Control 
                                            type="number" 
                                            placeholder="0" 
                                            value={simulatedDiscount || ''} 
                                            onChange={e => setSimulatedDiscount(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))}
                                        />
                                        <InputGroup.Text className="bg-light">%</InputGroup.Text>
                                    </InputGroup>
                                </div>
                            </div>
                            <Table bordered size="sm" className="bg-white shadow-sm">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Vehículo</th>
                                        <th style={{ width: '80px' }}>Pax Máx</th>
                                        <th style={{ width: '90px' }}>Maletas Máx</th>
                                        <th>Venta (CLP)</th>
                                        <th>Costo Local ({
                                            (() => {
                                                const provider = providers.find(p => String(p.id) === String(formData.provider_id));
                                                const originBaseId = formData.origin_type === 'zone' 
                                                    ? zones.find(z => String(z.id) === String(formData.origin_zone_id))?.base_id
                                                    : zoneGroups.find(g => String(g.id) === String(formData.origin_group_id))?.base_id;
                                                const base = bases.find(b => b.id === originBaseId);
                                                return provider?.currency_code || base?.currency_code || 'CLP';
                                            })()
                                        })</th>
                                        <th>Costo (CLP)</th>
                                        <th>Venta (USD)</th>
                                        <th>Utilidad (CLP)</th>
                                        <th>Margen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(isEditing ? [formData.vehicle_type_id.toString()] : formData.selected_vehicle_ids).map(vId => {
                                        const vt = vehicleTypes.find(v => v.id.toString() === vId);
                                        const prices = formData.prices_map[vId] || { price: 0, cost_local: 0, cost_clp: 0, sale_usd: 0, max_pax: '', max_suitcases: '' };
                                        
                                        const ventaNormal = parseFloat(prices.price || 0);
                                        const costoCLP = parseFloat(prices.cost_clp || 0);
                                        
                                        // Apply simulated discount
                                        const ventaDescontada = ventaNormal * (1 - (simulatedDiscount || 0) / 100);
                                        const utilidad = ventaDescontada - costoCLP;
                                        const margen = ventaDescontada > 0 ? (utilidad / ventaDescontada) * 100 : 0;
                                        
                                        return (
                                            <tr key={vId}>
                                                <td className="align-middle fw-bold">{vt?.name}</td>
                                                <td>
                                                    <Form.Control 
                                                        size="sm" 
                                                        type="number" 
                                                        placeholder={vt?.capacity || '4'}
                                                        value={prices.max_pax || ''} 
                                                        onChange={e => updateVehiclePrice(vId, 'max_pax', e.target.value)} 
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control 
                                                        size="sm" 
                                                        type="number" 
                                                        placeholder={vt?.name && (vt.name.toUpperCase().includes("STANDARD") || vt.name.toUpperCase() === "EXECUTIVE") ? '2' : '4'}
                                                        value={prices.max_suitcases || ''} 
                                                        onChange={e => updateVehiclePrice(vId, 'max_suitcases', e.target.value)} 
                                                    />
                                                </td>
                                                <td><Form.Control size="sm" type="number" value={prices.price} onChange={e => updateVehiclePrice(vId, 'price', e.target.value)} /></td>
                                                <td><Form.Control size="sm" type="number" value={prices.cost_local} onChange={e => updateVehiclePrice(vId, 'cost_local', e.target.value)} /></td>
                                                <td className="align-middle text-muted">${prices.cost_clp?.toLocaleString()}</td>
                                                <td>
                                                    <Form.Control
                                                        size="sm"
                                                        type="number"
                                                        step="0.01"
                                                        value={prices.sale_usd}
                                                        onChange={e => updateVehiclePrice(vId, 'sale_usd', e.target.value)}
                                                    />
                                                </td>
                                                <td className={`align-middle fw-bold ${utilidad >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {(utilidad >= 0 ? '+' : '-') + '$' + Math.abs(Math.round(utilidad)).toLocaleString()}
                                                </td>
                                                <td className={`align-middle fw-bold ${margen >= 0 ? 'text-success' : 'text-danger'}`}>
                                                    {margen.toFixed(1)}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <div className="p-3 mb-2 bg-light rounded border">
                        <Row className="align-items-center">
                            <Col md={6}>
                                <Form.Label className="small fw-bold text-primary mb-1">⭐ Descuento Genius Booking.com (%)</Form.Label>
                                <Form.Select 
                                    size="sm"
                                    value={formData.booking_genius_discount || 0}
                                    onChange={e => setFormData(prev => ({ ...prev, booking_genius_discount: parseFloat(e.target.value) }))}
                                    className="fw-bold"
                                >
                                    <option value={0}>0% - Inactivo (Estándar)</option>
                                    <option value={5}>5% - Descuento Genius (Recomendado Booking)</option>
                                    <option value={6}>6% - Promoción Especial</option>
                                    <option value={7}>7% - Promoción Agresiva</option>
                                    <option value={8}>8% - Alta Demanda</option>
                                    <option value={9}>9% - Alta Competitividad</option>
                                    <option value={10}>10% - Máximo Permitido</option>
                                </Form.Select>
                            </Col>
                            <Col md={6}>
                                <small className="text-muted d-block mt-3">
                                    🤝 Co-financiado: El costo del proveedor se reduce en la misma proporción para clientes Booking Genius.
                                </small>
                            </Col>
                        </Row>
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSave} className="fw-bold px-4" disabled={!isEditing && formData.selected_vehicle_ids.length === 0}>
                        {isEditing ? 'Guardar Cambios' : 'Guardar Matriz'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL EXPORTAR TARIFARIO A EXCEL */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="md" backdrop="static">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold">📥 Exportar Tarifario a Excel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Destinatario del Tarifario</Form.Label>
                        <div className="border rounded p-2 bg-light">
                            <Form.Check 
                                type="radio"
                                id="export-client"
                                label={<span className="fw-bold text-dark small">Para Cliente (Ver solo venta)</span>}
                                name="export_type"
                                checked={exportForm.export_type === 'client'}
                                onChange={() => setExportForm({...exportForm, export_type: 'client', provider_id: ''})}
                                className="mb-1"
                            />
                            <Form.Text className="text-muted d-block ms-4 mb-2" style={{fontSize: '11px'}}>
                                Oculta todos los costos internos. El archivo solo mostrará las tarifas de venta al cliente.
                            </Form.Text>

                            <Form.Check 
                                type="radio"
                                id="export-provider"
                                label={<span className="fw-bold text-dark small">Para Proveedor (Ver solo costo)</span>}
                                name="export_type"
                                checked={exportForm.export_type === 'provider'}
                                onChange={() => setExportForm({...exportForm, export_type: 'provider', client_id: ''})}
                                className="mb-1"
                            />
                            <Form.Text className="text-muted d-block ms-4 mb-2" style={{fontSize: '11px'}}>
                                Oculta las tarifas de venta y márgenes. Solo mostrará los costos pagados a los proveedores.
                            </Form.Text>

                            <Form.Check 
                                type="radio"
                                id="export-internal"
                                label={<span className="fw-bold text-dark small">Uso Interno / Administrador (Venta + Costo)</span>}
                                name="export_type"
                                checked={exportForm.export_type === 'internal'}
                                onChange={() => setExportForm({...exportForm, export_type: 'internal'})}
                            />
                            <Form.Text className="text-muted d-block ms-4" style={{fontSize: '11px'}}>
                                Sábana de control administrativo con venta, costo, margen neto (USD) y porcentaje de utilidad.
                            </Form.Text>
                        </div>
                    </Form.Group>

                    <Row className="mb-3">
                        <Col md={12}>
                            <SearchableMultiSelect 
                                label="Sucursal / Base"
                                options={bases}
                                selected={exportForm.base_ids || []}
                                onChange={ids => setExportForm({...exportForm, base_ids: ids})}
                                displayField="name"
                                placeholder="Todas las Bases / Regiones"
                            />
                        </Col>
                    </Row>

                    {exportForm.export_type !== 'provider' && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <SearchableMultiSelect 
                                    label="Filtrar por Cliente / Convenio"
                                    options={clients}
                                    selected={exportForm.client_ids || []}
                                    onChange={ids => setExportForm({...exportForm, client_ids: ids})}
                                    displayField="name"
                                    placeholder="Tarifario Base General (Ningún Cliente)"
                                />
                                <Form.Text className="text-muted d-block small mt-1" style={{fontSize: '11px'}}>
                                    Al elegir clientes, se incluirán sus precios específicos (y tarifas globales para las rutas sin convenio).
                                </Form.Text>
                            </Col>
                        </Row>
                    )}

                    {exportForm.export_type !== 'client' && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Label className="small fw-bold">Filtrar por Proveedor Regional</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={exportForm.provider_id} 
                                    onChange={e => setExportForm({...exportForm, provider_id: e.target.value})}
                                >
                                    <option value="">Todos los Proveedores</option>
                                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </Form.Select>
                            </Col>
                        </Row>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Divisas a Incluir</Form.Label>
                        <div className="d-flex gap-3 bg-light p-2 rounded border">
                            <Form.Check 
                                type="checkbox"
                                id="inc-clp"
                                label={<span className="small">Pesos Chilenos (CLP)</span>}
                                checked={exportForm.include_clp}
                                onChange={e => setExportForm({...exportForm, include_clp: e.target.checked})}
                            />
                            <Form.Check 
                                type="checkbox"
                                id="inc-usd"
                                label={<span className="small">Dólares (USD)</span>}
                                checked={exportForm.include_usd}
                                onChange={e => setExportForm({...exportForm, include_usd: e.target.checked})}
                            />
                            <Form.Check 
                                type="checkbox"
                                id="inc-local"
                                label={<span className="small">Moneda Local (PEN, DOP, etc.)</span>}
                                checked={exportForm.include_local}
                                onChange={e => setExportForm({...exportForm, include_local: e.target.checked})}
                            />
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setShowExportModal(false)} disabled={exportLoading}>
                        Cancelar
                    </Button>
                    <Button variant="success" size="sm" onClick={handleExportExcel} className="fw-bold px-3" disabled={exportLoading}>
                        {exportLoading ? 'Generando Excel...' : '✓ Descargar Excel'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Asignar Genius Rápido */}
            <Modal show={showGeniusBulkModal} onHide={() => setShowGeniusBulkModal(false)} centered>
                <Modal.Header closeButton style={{ backgroundColor: '#003580' }} className="text-white">
                    <Modal.Title className="fw-bold fs-6 text-white d-flex align-items-center gap-2">
                        <span>⭐</span> Asignar Descuento Genius Booking (Masivo)
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 space-y-3">
                    <div className="alert alert-primary py-2 px-3 small mb-3 border-0" style={{ backgroundColor: '#e7f0fa', color: '#003580' }}>
                        <strong>Programa Genius:</strong> Aplica un descuento oficial a las búsquedas de usuarios fidelizados de Booking.com para aumentar drásticamente la tasa de conversión y adjudicación.
                    </div>

                    <div className="bg-light p-3 rounded border mb-3">
                        <small className="text-muted fw-bold d-block mb-1">🤝 Co-financiamiento Proporcional:</small>
                        <small className="text-muted">
                            El descuento se aplica al precio de venta y se comparte proporcionalmente reduciendo el costo proveedor para proteger el margen de EmiGo.
                        </small>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-dark small text-uppercase">Porcentaje de Descuento Genius *</Form.Label>
                        <Form.Select
                            value={bulkGeniusPercent}
                            onChange={e => setBulkGeniusPercent(parseFloat(e.target.value))}
                            className="fw-bold"
                        >
                            <option value={0}>0% - Desactivar Genius</option>
                            <option value={5}>5% - Descuento Estándar Genius (Recomendado)</option>
                            <option value={6}>6% - Promoción Moderada</option>
                            <option value={7}>7% - Promoción Agresiva</option>
                            <option value={8}>8% - Alta Demanda</option>
                            <option value={9}>9% - Alta Competitividad</option>
                            <option value={10}>10% - Máximo Permitido por Booking</option>
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-dark small text-uppercase">Filtrar por Base Operativa (Opcional)</Form.Label>
                        <Form.Select
                            value={bulkGeniusBaseId}
                            onChange={e => setBulkGeniusBaseId(e.target.value)}
                        >
                            <option value="">Todas las Bases Operativas</option>
                            {bases.map(b => (
                                <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted small">
                            Si dejas en "Todas las Bases", se aplicará globalmente según el proveedor.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-bold text-dark small text-uppercase">Filtrar por Proveedor (Opcional)</Form.Label>
                        <Form.Select
                            value={bulkGeniusProviderId}
                            onChange={e => setBulkGeniusProviderId(e.target.value)}
                        >
                            <option value="">Todos los Proveedores / Tarifas Base</option>
                            {providers.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </Form.Select>
                        <Form.Text className="text-muted small">
                            Puedes aplicarlo solo a las tarifas de un proveedor específico (ej. RedGo).
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" size="sm" onClick={() => setShowGeniusBulkModal(false)} disabled={isSavingBulkGenius}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        style={{ backgroundColor: '#003580', borderColor: '#003580' }}
                        className="fw-bold text-white shadow-sm"
                        onClick={handleApplyBulkGenius}
                        disabled={isSavingBulkGenius}
                    >
                        {isSavingBulkGenius ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" />
                                Aplicando...
                            </>
                        ) : (
                            `Aplicar ${bulkGeniusPercent}% Genius`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

// --- NEW DISTANCE TARIFF MANAGER ---
const DistanceTariffManager = () => {
    const [rules, setRules] = useState([]);
    const [clients, setClients] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [bases, setBases] = useState([]); // Nuevo
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [clientSearch, setClientSearch] = useState('');
    const initialForm = { vehicle_type_id: '', client_ids: 'ALL', base_id: '', initial_km: 10, initial_price: 0, medium_km_limit: 30, medium_price_per_km: 0, far_price_per_km: 0 };
    const [formData, setFormData] = useState(initialForm);
    
    const load = useCallback(async () => { 
        try {
            const [rData, cData, vtData, bData] = await Promise.all([getDistanceRules(), getClients(), getVehicleTypes(), getBases()]); 
            setRules(rData); setClients(cData); setVehicleTypes(vtData); setBases(bData);
        } catch (e) { console.error("Error loading distance rules:", e); }
    }, []);
    
    useEffect(() => { load(); }, [load]);
    
    const handleSave = async () => { 
        const payload = { 
            ...formData, 
            vehicle_type_id: parseInt(formData.vehicle_type_id), 
            client_ids: formData.client_ids === 'ALL' ? null : formData.client_ids,
            base_id: formData.base_id ? parseInt(formData.base_id) : null,
            initial_km: parseFloat(formData.initial_km), 
            initial_price: parseFloat(formData.initial_price), 
            medium_km_limit: parseFloat(formData.medium_km_limit), 
            medium_price_per_km: parseFloat(formData.medium_price_per_km), 
            far_price_per_km: parseFloat(formData.far_price_per_km) 
        }; 
        try {
            if (isEditing) await updateDistanceRule(editId, payload); 
            else await createDistanceRule(payload); 
            setShowModal(false); load(); 
        } catch (e) { alert("Error al guardar la regla."); }
    };
    
    const handleEdit = (r) => { 
        setFormData({ 
            vehicle_type_id: r.vehicle_type_id, 
            client_ids: r.client_ids || 'ALL', 
            base_id: r.base_id || '',
            initial_km: r.initial_km, 
            initial_price: r.initial_price, 
            medium_km_limit: r.medium_km_limit, 
            medium_price_per_km: r.medium_price_per_km, 
            far_price_per_km: r.far_price_per_km 
        }); 
        setIsEditing(true); setEditId(r.id); setShowModal(true); 
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de que deseas eliminar esta regla por KM?")) {
            setRules(prev => prev.filter(rule => rule.id !== id)); // Optimistic delete
            try {
                await deleteDistanceRule(id);
                load();
            } catch (e) {
                alert("Error al eliminar la regla.");
                load();
            }
        }
    };
    
    return (
        <div>
            <div className="d-flex justify-content-end mb-2"><Button size="sm" onClick={() => { setFormData(initialForm); setIsEditing(false); setShowModal(true); }}>+ Nueva Regla x Km</Button></div>
            <Table striped hover size="sm" className="small align-middle">
                <thead><tr><th>Base</th><th>Vehículo</th><th>Cliente</th><th>Tramo 1</th><th>Tramo 2</th><th>Tramo 3</th><th className="text-end">Acciones</th></tr></thead>
                <tbody>{rules.map(r => {
                    const baseName = bases.find(b => b.id === r.base_id)?.name || 'Global';
                    return (
                    <tr key={r.id}>
                        <td><Badge bg={r.base_id ? "info" : "secondary"}>{baseName}</Badge></td>
                        <td>{r.vehicle_type?.name}</td>
                        <td>{getClientDisplay(r.client_ids, clients)}</td>
                        <td>${r.initial_price} (0-{r.initial_km}km)</td>
                        <td>${r.medium_price_per_km}/km (hasta {r.medium_km_limit}km)</td>
                        <td>${r.far_price_per_km}/km (+{r.medium_km_limit}km)</td>
                        <td className="text-end">
                            <Button size="sm" variant="outline-primary" className="py-0 me-1" onClick={() => handleEdit(r)}>✏️</Button>
                            <Button size="sm" variant="outline-danger" className="py-0" onClick={() => handleDelete(r.id)}>🗑️</Button>
                        </td>
                    </tr>
                )})}
                </tbody>
            </Table>
            <Modal show={showModal} onHide={() => { setShowModal(false); setClientSearch(''); }} size="lg">
                <Modal.Header closeButton><Modal.Title>{isEditing ? 'Editar' : 'Nueva'} Regla de Distancia</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small fw-bold">Base / Sucursal</Form.Label>
                                <Form.Select value={formData.base_id} onChange={e => setFormData({...formData, base_id: e.target.value})}>
                                    <option value="">-- Global (Todas) --</option>
                                    {bases.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-2 mt-2">
                                <Form.Label className="small fw-bold">Vehículo</Form.Label>
                                <Form.Select value={formData.vehicle_type_id} onChange={e => setFormData({...formData, vehicle_type_id: e.target.value})}>
                                    <option value="">Seleccione...</option>
                                    {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Clientes / Convenios</Form.Label>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 text-decoration-none small text-primary fw-bold"
                                    onClick={() => {
                                        const allSelected = (formData.client_ids === 'ALL' || !formData.client_ids) || 
                                            (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length);
                                        setFormData(prev => ({ ...prev, client_ids: allSelected ? 'NONE' : 'ALL' }));
                                    }}
                                >
                                    {((formData.client_ids === 'ALL' || !formData.client_ids) || 
                                      (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length)) 
                                        ? '✕ Deseleccionar todos' 
                                        : '✓ Seleccionar todos'}
                                </Button>
                            </div>
                            <Form.Control 
                                size="sm" 
                                placeholder="🔍 Buscar cliente..." 
                                className="mb-2" 
                                value={clientSearch}
                                onChange={e => setClientSearch(e.target.value)}
                            />
                            <div className="border rounded p-2 bg-white d-flex flex-wrap gap-2" style={{maxHeight: '120px', overflowY: 'auto'}}>
                                {clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).map(c => {
                                    const cId = c.id.toString();
                                    const isSelected = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                        ? true 
                                        : (formData.client_ids === 'NONE' ? false : formData.client_ids.split(',').includes(cId));
                                    return (
                                        <Form.Check 
                                            key={c.id} 
                                            type="checkbox" 
                                            label={c.name} 
                                            id={`dist-cli-${c.id}`} 
                                            checked={isSelected} 
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                let currentIds = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                                    ? clients.map(cl => cl.id.toString()) 
                                                    : (formData.client_ids === 'NONE' ? [] : formData.client_ids.split(',').filter(Boolean));
                                                
                                                if (isChecked) {
                                                    if (!currentIds.includes(cId)) currentIds.push(cId);
                                                } else {
                                                    currentIds = currentIds.filter(id => id !== cId);
                                                }
                                                
                                                if (currentIds.length === 0) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'NONE' }));
                                                } else if (currentIds.length === clients.length) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'ALL' }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, client_ids: currentIds.join(',') }));
                                                }
                                            }} 
                                            className="small me-2" 
                                        />
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4} className="bg-light p-2 rounded border">
                            <h6 className="small fw-bold">Tramo 1 (Salida Mínima)</h6>
                            <Form.Label className="small">Hasta Km:</Form.Label>
                            <Form.Control type="number" size="sm" value={formData.initial_km} onChange={e => setFormData({...formData, initial_km: e.target.value})} />
                            <Form.Label className="small mt-1">Precio Fijo Total:</Form.Label>
                            <Form.Control type="number" size="sm" value={formData.initial_price} onChange={e => setFormData({...formData, initial_price: e.target.value})} />
                        </Col>
                        <Col md={4} className="bg-light p-2 rounded border mx-2">
                            <h6 className="small fw-bold">Tramo 2 (Media)</h6>
                            <Form.Label className="small">Hasta Km:</Form.Label>
                            <Form.Control type="number" size="sm" value={formData.medium_km_limit} onChange={e => setFormData({...formData, medium_km_limit: e.target.value})} />
                            <Form.Label className="small mt-1">Precio por Km Extra:</Form.Label>
                            <Form.Control type="number" size="sm" value={formData.medium_price_per_km} onChange={e => setFormData({...formData, medium_price_per_km: e.target.value})} />
                        </Col>
                        <Col md={3} className="bg-light p-2 rounded border">
                            <h6 className="small fw-bold">Tramo 3 (Larga)</h6>
                            <Form.Label className="small">Desde Km {formData.medium_km_limit}+</Form.Label>
                            <Form.Label className="small mt-1">Precio por Km Extra:</Form.Label>
                            <Form.Control type="number" size="sm" value={formData.far_price_per_km} onChange={e => setFormData({...formData, far_price_per_km: e.target.value})} />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer><Button onClick={handleSave}>Guardar</Button></Modal.Footer>
            </Modal>
        </div>
    );
};

const formatDateTimeLocal = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    const Y = date.getFullYear();
    const M = pad(date.getMonth() + 1);
    const D = pad(date.getDate());
    const H = pad(date.getHours());
    const m = pad(date.getMinutes());
    return `${Y}-${M}-${D}T${H}:${m}`;
};

const formatDisplayDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const pad = (num) => String(num).padStart(2, '0');
    const Y = date.getFullYear();
    const M = pad(date.getMonth() + 1);
    const D = pad(date.getDate());
    const H = pad(date.getHours());
    const m = pad(date.getMinutes());
    return `${D}/${M}/${Y} ${H}:${m}`;
};

const RulesManager = () => {
    const [rules, setRules] = useState([]);
    const [clients, setClients] = useState([]);
    const [providers, setProviders] = useState([]);
    const [bases, setBases] = useState([]);
    const [zones, setZones] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [clientSearch, setClientSearch] = useState('');
    const [providerSearch, setProviderSearch] = useState('');

    // Filtros para la tabla de Reglas
    const [filterName, setFilterName] = useState('');
    const [filterScope, setFilterScope] = useState('');
    const [filterValidity, setFilterValidity] = useState('');
    const [filterClient, setFilterClient] = useState('');
    const [filterProvider, setFilterProvider] = useState('');
    const [filterVehicle, setFilterVehicle] = useState('');
    const initialRule = { 
        name: '', mode: 'recurrent', type: 'ADJUST', value_type: 'percentage', value: 0, 
        cost_value: 0,
        start_time: '00:00', end_time: '23:59', days_of_week: '', 
        start_datetime: '', end_datetime: '', client_ids: 'ALL', provider_ids: 'ALL', vehicle_type_ids: 'ALL', priority: 10, 
        is_blocking: false, is_exclusive: false,
        geographic_scope: 'global', specific_country: '', specific_zone_id: '',
        max_lead_time_minutes: '',
        min_lead_time_minutes: ''
    };
    const [formData, setFormData] = useState(initialRule);
    const load = useCallback(async () => { 
        const [rData, cData, pData, bData, zData, vtData] = await Promise.all([
            getPriceRules(), getClients(), getProviders(), getBases(), getZones(), getVehicleTypes()
        ]); 
        setRules(rData); 
        setClients(cData);
        setProviders(pData);
        setBases(bData);
        setZones(zData);
        setVehicleTypes(vtData);
    }, []);
    useEffect(() => { load(); }, [load]);
    const handleSave = async () => { 
        const absoluteValue = Math.abs(parseFloat(formData.value));
        const finalValue = formData.adjustment_type === 'discount' ? -absoluteValue : absoluteValue;

        const payload = { 
            ...formData, 
            value: finalValue, 
            cost_value: formData.cost_value !== '' && formData.cost_value !== null && formData.cost_value !== undefined ? parseFloat(formData.cost_value) : 0.0,
            priority: parseInt(formData.priority), 
            client_ids: formData.client_ids === 'ALL' ? null : formData.client_ids, 
            provider_ids: formData.provider_ids === 'ALL' ? null : formData.provider_ids,
            vehicle_type_ids: formData.vehicle_type_ids === 'ALL' ? null : formData.vehicle_type_ids, 
            is_blocking: formData.type === 'BLOCK', 
            is_exclusive: formData.is_exclusive, 
            max_lead_time_minutes: formData.max_lead_time_minutes === '' ? null : parseInt(formData.max_lead_time_minutes),
            min_lead_time_minutes: formData.min_lead_time_minutes === '' ? null : parseInt(formData.min_lead_time_minutes),
            start_datetime: formData.mode === 'specific' && formData.start_datetime ? new Date(formData.start_datetime).toISOString() : null, 
            end_datetime: formData.mode === 'specific' && formData.end_datetime ? new Date(formData.end_datetime).toISOString() : null, 
            days_of_week: formData.mode === 'recurrent' ? formData.days_of_week : null, 
            start_time: formData.mode === 'recurrent' ? formData.start_time : null, 
            end_time: formData.mode === 'recurrent' ? formData.end_time : null,
            specific_country: formData.geographic_scope === 'country' ? formData.specific_country : null,
            specific_zone_id: formData.geographic_scope === 'zone' ? parseInt(formData.specific_zone_id) : null
        }; 
        const sendPayload = { ...payload }; 
        delete sendPayload.mode; 
        delete sendPayload.geographic_scope;
        delete sendPayload.adjustment_type;

        if (isEditing) await updatePriceRule(editId, sendPayload); 
        else await createPriceRule(sendPayload); 
        setShowModal(false); load(); 
    };

    const handleEdit = (r) => { 
        const isSpecific = !!(r.start_datetime && r.end_datetime);
        let gScope = 'global';
        if (r.specific_country) gScope = 'country';
        else if (r.specific_zone_id) gScope = 'zone';

        setFormData({ 
            name: r.name, 
            mode: isSpecific ? 'specific' : 'recurrent', 
            type: r.is_blocking ? 'BLOCK' : 'ADJUST', 
            value_type: r.value_type, 
            value: r.value, 
            cost_value: r.cost_value !== null && r.cost_value !== undefined ? r.cost_value : 0,
            start_time: r.start_time || '00:00', 
            end_time: r.end_time || '23:59', 
            days_of_week: r.days_of_week || '', 
            start_datetime: formatDateTimeLocal(r.start_datetime), 
            end_datetime: formatDateTimeLocal(r.end_datetime), 
            client_ids: r.client_ids || 'ALL', 
            provider_ids: r.provider_ids || 'ALL',
            vehicle_type_ids: r.vehicle_type_ids || 'ALL', 
            priority: r.priority || 10, 
            is_exclusive: r.is_exclusive || false,
            geographic_scope: gScope,
            specific_country: r.specific_country || '',
            specific_zone_id: r.specific_zone_id || '',
            adjustment_type: r.value < 0 ? 'discount' : 'increase',
            max_lead_time_minutes: r.max_lead_time_minutes || '',
            min_lead_time_minutes: r.min_lead_time_minutes || ''
        }); 
        setIsEditing(true); setEditId(r.id); setShowModal(true); 
    };
    const countries = Array.from(new Set(bases.map(b => b.country_code))).sort();

    const filteredRules = useMemo(() => {
        return rules.filter(r => {
            if (filterName.trim()) {
                if (!(r.name || '').toLowerCase().includes(filterName.toLowerCase())) return false;
            }
            if (filterScope) {
                let gScope = 'global';
                if (r.specific_country) gScope = 'country';
                else if (r.specific_zone_id) gScope = 'zone';
                if (filterScope === 'global' && gScope !== 'global') return false;
                if (filterScope === 'country' && gScope !== 'country') return false;
                if (filterScope === 'zone' && gScope !== 'zone') return false;
            }
            if (filterValidity) {
                const isSpecific = !!(r.start_datetime && r.end_datetime);
                if (filterValidity === 'specific' && !isSpecific) return false;
                if (filterValidity === 'recurrent' && isSpecific) return false;
            }
            if (filterClient.trim()) {
                const q = filterClient.toLowerCase();
                const cIds = (!r.client_ids || r.client_ids === 'ALL') ? [] : r.client_ids.toString().split(',').map(s => s.trim()).filter(Boolean);
                const cNames = cIds.length === 0 ? 'global todos' : cIds.map(id => clients.find(c => String(c.id) === String(id))?.name || '').join(' ').toLowerCase();
                if (!cNames.includes(q)) return false;
            }
            if (filterProvider.trim()) {
                const q = filterProvider.toLowerCase();
                const pIds = (!r.provider_ids || r.provider_ids === 'ALL') ? [] : r.provider_ids.toString().split(',').map(s => s.trim()).filter(Boolean);
                const pNames = pIds.length === 0 ? 'todos global' : pIds.map(id => providers.find(p => String(p.id) === String(id))?.name || '').join(' ').toLowerCase();
                if (!pNames.includes(q)) return false;
            }
            if (filterVehicle.trim()) {
                const q = filterVehicle.toLowerCase();
                const vIds = (!r.vehicle_type_ids || r.vehicle_type_ids === 'ALL') ? [] : r.vehicle_type_ids.toString().split(',').map(s => s.trim()).filter(Boolean);
                const vNames = vIds.length === 0 ? 'todos global' : vIds.map(id => vehicleTypes.find(v => String(v.id) === String(id))?.name || '').join(' ').toLowerCase();
                if (!vNames.includes(q)) return false;
            }
            return true;
        });
    }, [rules, filterName, filterScope, filterValidity, filterClient, filterProvider, filterVehicle, clients, providers, vehicleTypes]);

    const hasActiveFilters = filterName || filterScope || filterValidity || filterClient || filterProvider || filterVehicle;

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="small text-muted fw-bold">
                    Mostrando {filteredRules.length} de {rules.length} reglas
                </div>
                <div className="d-flex gap-2">
                    {hasActiveFilters && (
                        <Button 
                            size="sm" 
                            variant="outline-secondary" 
                            onClick={() => {
                                setFilterName('');
                                setFilterScope('');
                                setFilterValidity('');
                                setFilterClient('');
                                setFilterProvider('');
                                setFilterVehicle('');
                            }}
                        >
                            🧹 Limpiar Filtros
                        </Button>
                    )}
                    <Button size="sm" onClick={() => { setFormData(initialRule); setIsEditing(false); setShowModal(true); }}>+ Nueva Regla</Button>
                </div>
            </div>
            <Table striped hover size="sm" align="middle">
                <thead>
                    <tr>
                        <th style={{ minWidth: '180px' }}>
                            <div>Nombre</div>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="🔍 Buscar..."
                                value={filterName}
                                onChange={e => setFilterName(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            />
                        </th>
                        <th>Tipo</th>
                        <th style={{ minWidth: '130px' }}>
                            <div>Alcance</div>
                            <Form.Select
                                size="sm"
                                value={filterScope}
                                onChange={e => setFilterScope(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            >
                                <option value="">Todos</option>
                                <option value="global">Global</option>
                                <option value="country">País</option>
                                <option value="zone">Zona</option>
                            </Form.Select>
                        </th>
                        <th style={{ minWidth: '130px' }}>
                            <div>Vigencia</div>
                            <Form.Select
                                size="sm"
                                value={filterValidity}
                                onChange={e => setFilterValidity(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            >
                                <option value="">Todas</option>
                                <option value="recurrent">Recurrente</option>
                                <option value="specific">Específica</option>
                            </Form.Select>
                        </th>
                        <th>Horario/Fecha</th>
                        <th style={{ minWidth: '140px' }}>
                            <div>Cliente</div>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="🔍 Buscar..."
                                value={filterClient}
                                onChange={e => setFilterClient(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            />
                        </th>
                        <th style={{ minWidth: '140px' }}>
                            <div>Proveedor</div>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="🔍 Buscar..."
                                value={filterProvider}
                                onChange={e => setFilterProvider(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            />
                        </th>
                        <th style={{ minWidth: '140px' }}>
                            <div>Vehículos</div>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="🔍 Buscar..."
                                value={filterVehicle}
                                onChange={e => setFilterVehicle(e.target.value)}
                                style={{ fontSize: '11px', height: '26px', marginTop: '4px' }}
                            />
                        </th>
                        <th>Prio</th>
                        <th>Exc.</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredRules.length === 0 ? (
                        <tr>
                            <td colSpan="11" className="text-center py-4 text-muted">
                                No se encontraron reglas que coincidan con los filtros aplicados.
                            </td>
                        </tr>
                    ) : (
                        filteredRules.map(r => {
                            const isSpecific = !!(r.start_datetime && r.end_datetime);
                            return (
                                <tr key={r.id}>
                                    <td>
                                        {r.name}
                                        {(r.max_lead_time_minutes || r.min_lead_time_minutes) && (
                                            <div style={{fontSize:'10px', color:'red', fontWeight:'bold'}}>
                                                ⏱ Urgencia: {r.min_lead_time_minutes || 0} - {r.max_lead_time_minutes || '∞'} min
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {r.is_blocking ? (
                                            <Badge bg="danger">Bloqueo</Badge>
                                        ) : (
                                            <div>
                                                <div>
                                                    <span className="fw-semibold small">Venta:</span>{' '}
                                                    <Badge bg="primary">{r.value > 0 ? `+${r.value}` : r.value}{r.value_type==='percentage'?'%':'$'}</Badge>
                                                </div>
                                                <div className="mt-1" style={{ fontSize: '11px' }}>
                                                    <span className="text-muted">Chofer:</span>{' '}
                                                    <Badge bg={r.cost_value > 0 ? "success" : "light"} className={r.cost_value > 0 ? "" : "text-dark border"}>
                                                        {r.cost_value > 0 ? `+${r.cost_value}%` : (r.cost_value === 0 ? '0%' : `+${r.cost_value || 0}%`)}
                                                    </Badge>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        {r.specific_country ? <Badge bg="info">País: {r.specific_country}</Badge> : 
                                         r.specific_zone_id ? <Badge bg="dark">Zona: {zones.find(z => z.id === r.specific_zone_id)?.name || r.specific_zone_id}</Badge> : 
                                         <Badge bg="secondary">Global</Badge>}
                                    </td>
                                    <td>{isSpecific ? <Badge bg="info">Específica</Badge> : <Badge bg="secondary">Recurrente</Badge>}</td>
                                    <td>
                                        {isSpecific ? (
                                            <div style={{fontSize:'0.85em'}}>{formatDisplayDateTime(r.start_datetime)} - {formatDisplayDateTime(r.end_datetime)}</div>
                                        ) : (
                                            <div style={{fontSize:'0.85em'}}>{r.days_of_week ? `Días: ${r.days_of_week}` : 'Todos'} <br/> {r.start_time}-{r.end_time}</div>
                                        )}
                                    </td>
                                    <td>{getClientDisplay(r.client_ids, clients)}</td>
                                    <td>{getProviderDisplay(r.provider_ids, providers)}</td>
                                    <td>{getVehicleTypeDisplay(r.vehicle_type_ids, vehicleTypes)}</td>
                                    <td>{r.priority}</td>
                                    <td>{r.is_exclusive && '✅'}</td>
                                    <td className="text-end"><Button size="sm" variant="outline-primary" className="py-0 me-1" onClick={() => handleEdit(r)}>✏️</Button><Button size="sm" variant="outline-danger" className="py-0" onClick={async () => { await deletePriceRule(r.id); load(); }}>×</Button></td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </Table>
            <Modal show={showModal} onHide={() => { setShowModal(false); setClientSearch(''); setProviderSearch(''); }} size="lg">
                <Modal.Header closeButton><Modal.Title>{isEditing ? 'Editar' : 'Nueva'} Regla</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Row className="mb-2"><Col md={6}><Form.Label>Nombre</Form.Label><Form.Control value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} /></Col><Col md={3}><Form.Label>Tipo</Form.Label><Form.Select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}><option value="ADJUST">Ajuste</option><option value="BLOCK">Bloqueo</option></Form.Select></Col><Col md={3}><Form.Label>Prio</Form.Label><Form.Control type="number" value={formData.priority} onChange={e => setFormData({...formData, priority: e.target.value})} /></Col></Row>
                    
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Label className="fw-bold">Alcance Geográfico</Form.Label>
                            <Form.Select value={formData.geographic_scope} onChange={e => setFormData({...formData, geographic_scope: e.target.value, specific_country: '', specific_zone_id: ''})}>
                                <option value="global">Global (Todo)</option>
                                <option value="country">Por País</option>
                                <option value="zone">Por Zona Específica</option>
                            </Form.Select>
                        </Col>
                        {formData.geographic_scope === 'country' && (
                            <Col md={4}>
                                <Form.Label>País</Form.Label>
                                <Form.Select value={formData.specific_country} onChange={e => setFormData({...formData, specific_country: e.target.value})}>
                                    <option value="">Seleccione país...</option>
                                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                                </Form.Select>
                            </Col>
                        )}
                        {formData.geographic_scope === 'zone' && (
                            <Col md={8}>
                                <Form.Label>Zona</Form.Label>
                                <SearchableSelect 
                                    options={zones} 
                                    value={formData.specific_zone_id} 
                                    onChange={val => setFormData({...formData, specific_zone_id: val})} 
                                    placeholder="Buscar zona..." 
                                />
                            </Col>
                        )}
                    </Row>

                    <div className="mb-3 border p-2 rounded bg-light">
                        <Form.Label className="fw-bold">Modo de Vigencia</Form.Label>
                        <div className="d-flex gap-3">
                            <Form.Check type="radio" label="Recurrente" name="mode" checked={formData.mode === 'recurrent'} onChange={() => setFormData({...formData, mode: 'recurrent'})} />
                            <Form.Check type="radio" label="Fecha Específica" name="mode" checked={formData.mode === 'specific'} onChange={() => setFormData({...formData, mode: 'specific'})} />
                        </div>
                    </div>
                    {formData.mode === 'recurrent' ? (
                        <Row className="mb-3">
                            <Col md={3}><Form.Label>Inicio</Form.Label><Form.Control type="time" value={formData.start_time} onChange={e => setFormData({...formData, start_time: e.target.value})} /></Col>
                            <Col md={3}><Form.Label>Fin</Form.Label><Form.Control type="time" value={formData.end_time} onChange={e => setFormData({...formData, end_time: e.target.value})} /></Col>
                            <Col md={6}><Form.Label>Días (1-7)</Form.Label><Form.Control value={formData.days_of_week} onChange={e => setFormData({...formData, days_of_week: e.target.value})} placeholder="1,2,3... (Vacio=Todos)" /></Col>
                        </Row>
                    ) : (
                        <Row className="mb-3">
                            <Col md={6}><Form.Label>Desde</Form.Label><Form.Control type="datetime-local" value={formData.start_datetime} onChange={e => setFormData({...formData, start_datetime: e.target.value})} /></Col>
                            <Col md={6}><Form.Label>Hasta</Form.Label><Form.Control type="datetime-local" value={formData.end_datetime} onChange={e => setFormData({...formData, end_datetime: e.target.value})} /></Col>
                        </Row>
                    )}
                    <Row className="mb-3">
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Clientes / Convenios</Form.Label>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 text-decoration-none small text-primary fw-bold"
                                    onClick={() => {
                                        const allSelected = (formData.client_ids === 'ALL' || !formData.client_ids) || 
                                            (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length);
                                        setFormData(prev => ({ ...prev, client_ids: allSelected ? 'NONE' : 'ALL' }));
                                    }}
                                >
                                    {((formData.client_ids === 'ALL' || !formData.client_ids) || 
                                      (formData.client_ids !== 'NONE' && formData.client_ids.split(',').filter(Boolean).length === clients.length)) 
                                        ? '✕ Deseleccionar todos' 
                                        : '✓ Seleccionar todos'}
                                </Button>
                            </div>
                            <Form.Control 
                                size="sm" 
                                placeholder="🔍 Buscar cliente..." 
                                className="mb-2" 
                                value={clientSearch}
                                onChange={e => setClientSearch(e.target.value)}
                            />
                            <div className="border rounded p-2 bg-white d-flex flex-wrap gap-2" style={{maxHeight: '120px', minHeight: '120px', overflowY: 'auto'}}>
                                {clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())).map(c => {
                                    const cId = c.id.toString();
                                    const isSelected = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                        ? true 
                                        : (formData.client_ids === 'NONE' ? false : formData.client_ids.split(',').includes(cId));
                                    return (
                                        <Form.Check 
                                            key={c.id} 
                                            type="checkbox" 
                                            label={c.name} 
                                            id={`rule-cli-${c.id}`} 
                                            checked={isSelected} 
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                let currentIds = (formData.client_ids === 'ALL' || !formData.client_ids) 
                                                    ? clients.map(cl => cl.id.toString()) 
                                                    : (formData.client_ids === 'NONE' ? [] : formData.client_ids.split(',').filter(Boolean));
                                                
                                                if (isChecked) {
                                                    if (!currentIds.includes(cId)) currentIds.push(cId);
                                                } else {
                                                    currentIds = currentIds.filter(id => id !== cId);
                                                }
                                                
                                                if (currentIds.length === 0) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'NONE' }));
                                                } else if (currentIds.length === clients.length) {
                                                    setFormData(prev => ({ ...prev, client_ids: 'ALL' }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, client_ids: currentIds.join(',') }));
                                                }
                                            }} 
                                            className="small me-2" 
                                        />
                                    );
                                })}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Proveedores</Form.Label>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 text-decoration-none small text-primary fw-bold"
                                    onClick={() => {
                                        const allSelected = (formData.provider_ids === 'ALL' || !formData.provider_ids) || 
                                            (formData.provider_ids !== 'NONE' && formData.provider_ids.split(',').filter(Boolean).length === providers.length);
                                        setFormData(prev => ({ ...prev, provider_ids: allSelected ? 'NONE' : 'ALL' }));
                                    }}
                                >
                                    {((formData.provider_ids === 'ALL' || !formData.provider_ids) || 
                                      (formData.provider_ids !== 'NONE' && formData.provider_ids.split(',').filter(Boolean).length === providers.length)) 
                                        ? '✕ Deseleccionar todos' 
                                        : '✓ Seleccionar todos'}
                                </Button>
                            </div>
                            <Form.Control 
                                size="sm" 
                                placeholder="🔍 Buscar proveedor..." 
                                className="mb-2" 
                                value={providerSearch}
                                onChange={e => setProviderSearch(e.target.value)}
                            />
                            <div className="border rounded p-2 bg-white d-flex flex-wrap gap-2" style={{maxHeight: '120px', minHeight: '120px', overflowY: 'auto'}}>
                                {providers.filter(p => (p.name || '').toLowerCase().includes(providerSearch.toLowerCase())).map(p => {
                                    const pId = p.id.toString();
                                    const isSelected = (formData.provider_ids === 'ALL' || !formData.provider_ids) 
                                        ? true 
                                        : (formData.provider_ids === 'NONE' ? false : formData.provider_ids.split(',').includes(pId));
                                    return (
                                        <Form.Check 
                                            key={p.id} 
                                            type="checkbox" 
                                            label={p.name} 
                                            id={`rule-prov-${p.id}`} 
                                            checked={isSelected} 
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                let currentIds = (formData.provider_ids === 'ALL' || !formData.provider_ids) 
                                                    ? providers.map(pr => pr.id.toString()) 
                                                    : (formData.provider_ids === 'NONE' ? [] : formData.provider_ids.split(',').filter(Boolean));
                                                
                                                if (isChecked) {
                                                    if (!currentIds.includes(pId)) currentIds.push(pId);
                                                } else {
                                                    currentIds = currentIds.filter(id => id !== pId);
                                                }
                                                
                                                if (currentIds.length === 0) {
                                                    setFormData(prev => ({ ...prev, provider_ids: 'NONE' }));
                                                } else if (currentIds.length === providers.length) {
                                                    setFormData(prev => ({ ...prev, provider_ids: 'ALL' }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, provider_ids: currentIds.join(',') }));
                                                }
                                            }} 
                                            className="small me-2" 
                                        />
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <Form.Label className="small fw-bold mb-0">Tipos de Vehículo</Form.Label>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="p-0 text-decoration-none small text-primary fw-bold"
                                    onClick={() => {
                                        const allSelected = (formData.vehicle_type_ids === 'ALL' || !formData.vehicle_type_ids) || 
                                            (formData.vehicle_type_ids !== 'NONE' && formData.vehicle_type_ids.split(',').filter(Boolean).length === vehicleTypes.length);
                                        setFormData(prev => ({ ...prev, vehicle_type_ids: allSelected ? 'NONE' : 'ALL' }));
                                    }}
                                >
                                    {((formData.vehicle_type_ids === 'ALL' || !formData.vehicle_type_ids) || 
                                      (formData.vehicle_type_ids !== 'NONE' && formData.vehicle_type_ids.split(',').filter(Boolean).length === vehicleTypes.length)) 
                                        ? '✕ Deseleccionar todos' 
                                        : '✓ Seleccionar todos'}
                                </Button>
                            </div>
                            <div className="border rounded p-2 bg-white d-flex flex-wrap gap-3" style={{maxHeight: '90px', overflowY: 'auto'}}>
                                {vehicleTypes.map(vt => {
                                    const vtId = vt.id.toString();
                                    const isSelected = (formData.vehicle_type_ids === 'ALL' || !formData.vehicle_type_ids) 
                                        ? true 
                                        : (formData.vehicle_type_ids === 'NONE' ? false : formData.vehicle_type_ids.split(',').includes(vtId));
                                    return (
                                        <Form.Check 
                                            key={vt.id} 
                                            type="checkbox" 
                                            label={vt.name} 
                                            id={`rule-veh-${vt.id}`} 
                                            checked={isSelected} 
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                let currentIds = (formData.vehicle_type_ids === 'ALL' || !formData.vehicle_type_ids) 
                                                    ? vehicleTypes.map(v => v.id.toString()) 
                                                    : (formData.vehicle_type_ids === 'NONE' ? [] : formData.vehicle_type_ids.split(',').filter(Boolean));
                                                
                                                if (isChecked) {
                                                    if (!currentIds.includes(vtId)) currentIds.push(vtId);
                                                } else {
                                                    currentIds = currentIds.filter(id => id !== vtId);
                                                }
                                                
                                                if (currentIds.length === 0) {
                                                    setFormData(prev => ({ ...prev, vehicle_type_ids: 'NONE' }));
                                                } else if (currentIds.length === vehicleTypes.length) {
                                                    setFormData(prev => ({ ...prev, vehicle_type_ids: 'ALL' }));
                                                } else {
                                                    setFormData(prev => ({ ...prev, vehicle_type_ids: currentIds.join(',') }));
                                                }
                                            }} 
                                            className="small me-2" 
                                        />
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Label className="small fw-bold">Operación</Form.Label>
                            <Form.Select value={formData.adjustment_type} onChange={e => setFormData({...formData, adjustment_type: e.target.value})}>
                                <option value="increase">Recargo (+)</option>
                                <option value="discount">Descuento (-)</option>
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Label className="small fw-bold">Valor (Venta / Cliente)</Form.Label>
                            <Form.Control type="number" value={formData.value} onChange={e => setFormData({...formData, value: e.target.value})} />
                        </Col>
                        <Col md={4}>
                            <Form.Label className="small fw-bold">Unidad</Form.Label>
                            <Form.Select value={formData.value_type} onChange={e => setFormData({...formData, value_type: e.target.value})}>
                                <option value="percentage">%</option>
                                <option value="fixed">$</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Label className="small fw-bold text-success">
                                % Recargo Conductor / Proveedor
                            </Form.Label>
                            <div className="input-group input-group-sm">
                                <Form.Control 
                                    type="number" 
                                    placeholder="0" 
                                    value={formData.cost_value} 
                                    onChange={e => setFormData({...formData, cost_value: e.target.value})} 
                                    className="border-success"
                                />
                                <span className="input-group-text bg-success text-white fw-bold">%</span>
                            </div>
                            <small className="text-muted" style={{fontSize:'11px'}}>
                                Porcentaje adicional para el chofer/móvil (ej: 25% para urgencia, o 0% si mantiene costo base).
                            </small>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Label className="text-danger fw-bold">Urgencia Máxima (Minutos)</Form.Label>
                            <Form.Control 
                                type="number" 
                                placeholder="Ej: 360" 
                                value={formData.max_lead_time_minutes} 
                                onChange={e => setFormData({...formData, max_lead_time_minutes: e.target.value})} 
                                className="border-danger shadow-sm"
                            />
                            <small className="text-muted" style={{fontSize:'10px'}}>Aplica si falta menos de estos minutos.</small>
                        </Col>
                        <Col md={5}>
                            <Form.Label className="text-danger fw-bold">Anticipación Mínima (Minutos)</Form.Label>
                            <Form.Control 
                                type="number" 
                                placeholder="Ej: 120" 
                                value={formData.min_lead_time_minutes} 
                                onChange={e => setFormData({...formData, min_lead_time_minutes: e.target.value})} 
                                className="border-danger shadow-sm"
                            />
                            <small className="text-muted" style={{fontSize:'10px'}}>NO aplica si falta menos de estos minutos (piso operativo).</small>
                        </Col>
                    </Row>
                    <Form.Check type="checkbox" label="Regla Exclusiva (STOP)" checked={formData.is_exclusive} onChange={e => setFormData({...formData, is_exclusive: e.target.checked})} />
                </Modal.Body>
                <Modal.Footer><Button onClick={handleSave}>Guardar</Button></Modal.Footer>
            </Modal>
        </div>
    );
};

const MinPricesManager = () => {
    const [rules, setRules] = useState([]);
    const [zones, setZones] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [filterActiveOnly, setFilterActiveOnly] = useState(true);
    
    const initialRule = {
        name: '',
        discount_percentage: 10,
        provider_shares_discount: true,
        match_direction: 'BIDIRECTIONAL',
        is_active: true,
        priority: 10,
        provider_id: null,
        provider: null,
        origin_zone_ids: [],
        destination_zone_ids: [],
        vehicle_type_ids: []
    };
    
    const [formData, setFormData] = useState(initialRule);
    
    const load = useCallback(async () => {
        try {
            const [rData, zData, vtData] = await Promise.all([getMinPriceRules(), getZones(), getVehicleTypes()]);
            setRules(rData);
            setZones(zData);
            setVehicleTypes(vtData);
        } catch (e) {
            console.error(e);
        }
    }, []);
    
    useEffect(() => {
        load();
    }, [load]);

    const displayedRules = useMemo(() => {
        if (filterActiveOnly) {
            return rules.filter(r => r.is_active);
        }
        return rules;
    }, [rules, filterActiveOnly]);
    
    const handleSave = async () => {
        if (!formData.name.trim()) {
            alert("El nombre es obligatorio");
            return;
        }
        
        let discount = parseFloat(formData.discount_percentage);
        if (isNaN(discount) || discount < 0 || discount > 50) {
            alert("El porcentaje de descuento debe estar entre 0% y 50% (límite de seguridad).");
            return;
        }
        
        const payload = {
            name: formData.name,
            discount_percentage: discount,
            provider_shares_discount: formData.provider_shares_discount,
            match_direction: formData.match_direction,
            is_active: formData.is_active,
            priority: parseInt(formData.priority) || 10,
            provider_id: formData.provider_id,
            origin_zone_ids: formData.origin_zone_ids || [],
            destination_zone_ids: formData.destination_zone_ids || [],
            vehicle_type_ids: formData.vehicle_type_ids || []
        };
        
        try {
            if (isEditing) {
                await updateMinPriceRule(editId, payload);
            } else {
                await createMinPriceRule(payload);
            }
            setShowModal(false);
            load();
        } catch (e) {
            alert("Error al guardar la regla.");
        }
    };
    
    const handleEdit = (r) => {
        setFormData({
            name: r.name,
            discount_percentage: r.discount_percentage,
            provider_shares_discount: r.provider_shares_discount,
            match_direction: r.match_direction,
            is_active: r.is_active,
            priority: r.priority,
            provider_id: r.provider_id || null,
            provider: r.provider || null,
            origin_zone_ids: (r.origin_zones || []).map(z => z.id),
            destination_zone_ids: (r.destination_zones || []).map(z => z.id),
            vehicle_type_ids: (r.vehicle_types || []).map(vt => vt.id)
        });
        setIsEditing(true);
        setEditId(r.id);
        setShowModal(true);
    };
    
    const handleDelete = async (id) => {
        if (window.confirm("¿Está seguro de eliminar esta regla de precio mínimo?")) {
            try {
                await deleteMinPriceRule(id);
                load();
            } catch (e) {
                alert("Error al eliminar la regla.");
            }
        }
    };
    
    const getDirectionLabel = (dir) => {
        switch (dir) {
            case 'ORIGIN': return 'Solo Origen';
            case 'DESTINATION': return 'Solo Destino';
            case 'BIDIRECTIONAL': return 'Bidireccional (Ida/Vuelta)';
            default: return dir;
        }
    };
    
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Reglas de Precio Mínimo para Booking.com</h5>
                <div className="d-flex align-items-center gap-3">
                    <Form.Group className="mb-0 d-flex align-items-center gap-2">
                        <Form.Label className="small mb-0 fw-bold" style={{ whiteSpace: 'nowrap' }}>Mostrar:</Form.Label>
                        <Form.Select 
                            size="sm" 
                            value={filterActiveOnly ? 'active' : 'all'} 
                            onChange={e => setFilterActiveOnly(e.target.value === 'active')}
                            style={{ width: '130px' }}
                        >
                            <option value="active">Solo Activas</option>
                            <option value="all">Todas</option>
                        </Form.Select>
                    </Form.Group>
                    <Button size="sm" variant="primary" onClick={() => { setFormData(initialRule); setIsEditing(false); setShowModal(true); }}>
                        + Nueva Regla de Mínimo
                    </Button>
                </div>
            </div>
            
            <Table striped hover size="sm">
                <thead>
                    <tr>
                        <th>Nombre</th>
                        <th>Creador</th>
                        <th>Origen</th>
                        <th>Destino</th>
                        <th>Vehículos</th>
                        <th>Dirección</th>
                        <th>Descuento</th>
                        <th>Prov. Asume</th>
                        <th>Prio</th>
                        <th>Estado</th>
                        <th>Acción</th>
                    </tr>
                </thead>
                <tbody>
                    {displayedRules.length === 0 ? (
                        <tr>
                            <td colSpan="11" className="text-center text-muted py-3">No hay reglas configuradas que coincidan con el filtro.</td>
                        </tr>
                    ) : (
                        displayedRules.map(r => (
                            <tr key={r.id}>
                                <td><span className="fw-bold">{r.name}</span></td>
                                <td>
                                    <Badge bg={r.provider_id ? 'info' : 'secondary'}>
                                        {r.provider_id ? (r.provider?.name || `Proveedor ID ${r.provider_id}`) : 'Admin'}
                                    </Badge>
                                </td>
                                <td>
                                    {(r.origin_zones || []).length === 0 ? (
                                        <Badge bg="success">Todas</Badge>
                                    ) : (
                                        <div style={{ maxWidth: '180px' }} className="text-truncate" title={r.origin_zones.map(z => z.name).join(', ')}>
                                            {r.origin_zones.map(z => z.name).join(', ')}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {(r.destination_zones || []).length === 0 ? (
                                        <Badge bg="success">Todas</Badge>
                                    ) : (
                                        <div style={{ maxWidth: '180px' }} className="text-truncate" title={r.destination_zones.map(z => z.name).join(', ')}>
                                            {r.destination_zones.map(z => z.name).join(', ')}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {(r.vehicle_types || []).length === 0 ? (
                                        <Badge bg="success">Todos</Badge>
                                    ) : (
                                        <div style={{ maxWidth: '150px' }} className="text-truncate" title={r.vehicle_types.map(vt => vt.name).join(', ')}>
                                            {r.vehicle_types.map(vt => vt.name).join(', ')}
                                        </div>
                                    )}
                                </td>
                                <td><Badge bg="secondary">{getDirectionLabel(r.match_direction)}</Badge></td>
                                <td><Badge bg="danger" style={{fontSize: '0.9em'}}>{r.discount_percentage}%</Badge></td>
                                <td>{r.provider_shares_discount ? <Badge bg="warning" text="dark">Sí</Badge> : <Badge bg="light" text="dark">No</Badge>}</td>
                                <td>{r.priority}</td>
                                <td>{r.is_active ? <Badge bg="success">Activo</Badge> : <Badge bg="secondary">Inactivo</Badge>}</td>
                                <td className="text-end">
                                    <Button size="sm" variant="outline-primary" className="py-0 me-1" onClick={() => handleEdit(r)}>✏️</Button>
                                    <Button size="sm" variant="outline-danger" className="py-0" onClick={() => handleDelete(r.id)}>×</Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Editar' : 'Nueva'} Regla de Precio Mínimo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formData.provider_id && (
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Creador (Regla Autogestionada por Proveedor)</Form.Label>
                                    <Form.Control size="sm" type="text" disabled value={formData.provider?.name || `Proveedor ID ${formData.provider_id}`} />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Nombre de la Regla</Form.Label>
                                <Form.Control 
                                    placeholder="Ej: Descuento SCL / Aeropuerto"
                                    value={formData.name} 
                                    onChange={e => setFormData({...formData, name: e.target.value})} 
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Prioridad</Form.Label>
                                <Form.Control 
                                    type="number" 
                                    value={formData.priority} 
                                    onChange={e => setFormData({...formData, priority: parseInt(e.target.value) || 10})} 
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end">
                            <Form.Check 
                                type="checkbox"
                                id="rule-is-active"
                                label="Regla Activa" 
                                checked={formData.is_active} 
                                onChange={e => setFormData({...formData, is_active: e.target.checked})} 
                                className="mb-2"
                            />
                        </Col>
                    </Row>
                    
                    <Row className="mb-3">
                        <Col md={6}>
                            <SearchableMultiSelect 
                                label="Zonas de Origen (Vacio = Todas)" 
                                options={zones} 
                                selected={formData.origin_zone_ids} 
                                onChange={ids => setFormData({...formData, origin_zone_ids: ids})} 
                                placeholder="Buscar origen..." 
                            />
                        </Col>
                        <Col md={6}>
                            <SearchableMultiSelect 
                                label="Zonas de Destino (Vacio = Todas)" 
                                options={zones} 
                                selected={formData.destination_zone_ids} 
                                onChange={ids => setFormData({...formData, destination_zone_ids: ids})} 
                                placeholder="Buscar destino..." 
                            />
                        </Col>
                    </Row>
                    
                    <Row className="mb-3">
                        <Col md={12}>
                            <SearchableMultiSelect 
                                label="Tipos de Vehículos (Vacio = Todos)" 
                                options={vehicleTypes} 
                                selected={formData.vehicle_type_ids} 
                                onChange={ids => setFormData({...formData, vehicle_type_ids: ids})} 
                                placeholder="Buscar tipos de vehículos..." 
                            />
                        </Col>
                    </Row>
                    
                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Dirección de Coincidencia</Form.Label>
                                <Form.Select 
                                    value={formData.match_direction} 
                                    onChange={e => setFormData({...formData, match_direction: e.target.value})}
                                >
                                    <option value="BIDIRECTIONAL">Bidireccional (Ida/Vuelta)</option>
                                    <option value="ORIGIN">Solo Origen (Aplica de A hacia B)</option>
                                    <option value="DESTINATION">Solo Destino (Aplica si llega a B)</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small fw-bold">Descuento (%)</Form.Label>
                                <Form.Control 
                                    type="number" 
                                    min="0"
                                    max="50"
                                    placeholder="0 - 50"
                                    value={formData.discount_percentage} 
                                    onChange={e => setFormData({...formData, discount_percentage: e.target.value})} 
                                />
                                <small className="text-muted" style={{fontSize: '9px'}}>Máximo 50% de descuento permitido.</small>
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-center">
                            <Form.Check 
                                type="checkbox"
                                id="rule-provider-shares"
                                label={
                                    <span>
                                        Proveedor asume descuento
                                    </span>
                                }
                                checked={formData.provider_shares_discount} 
                                onChange={e => setFormData({...formData, provider_shares_discount: e.target.checked})} 
                                className="mt-3"
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                    <Button variant="primary" onClick={handleSave}>Guardar Regla</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const PricingSimulator = () => {
    const [clients, setClients] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sim, setSim] = useState({ client_id: '', pickup_address: '', dropoff_address: '', scheduled_time: new Date().toISOString().slice(0, 16), passenger_count: 1, requested_vehicle_type_id: '' });
    const [pickupLoc, setPickupLoc] = useState(null);
    const [dropoffLoc, setDropoffLoc] = useState(null);

    useEffect(() => { 
        const load = async () => { 
            const [cData, vtData] = await Promise.all([getClients(), getVehicleTypes()]); 
            setClients(cData); 
            setVehicleTypes(vtData);
        }; 
        load(); 
    }, []);

    const handleSimulate = async () => { 
        if (!sim.client_id || !pickupLoc || !dropoffLoc) { alert("Campos incompletos (Cliente, Origen, Destino)."); return; } 
        setLoading(true); 
        try { 
            let distanceKm = 0; 
            if (window.google && window.google.maps) { 
                const service = new window.google.maps.DistanceMatrixService(); 
                const response = await service.getDistanceMatrix({ origins: [pickupLoc], destinations: [dropoffLoc], travelMode: 'DRIVING' }); 
                if (response.rows[0].elements[0].status === "OK") distanceKm = response.rows[0].elements[0].distance.value / 1000; 
            } 
            
            const payload = { 
                ...sim, 
                client_id: parseInt(sim.client_id), 
                pickup_lat: pickupLoc.lat, 
                pickup_lng: pickupLoc.lng, 
                dropoff_lat: dropoffLoc.lat, 
                dropoff_lng: dropoffLoc.lng, 
                distance_km: distanceKm,
                requested_vehicle_type_id: sim.requested_vehicle_type_id ? parseInt(sim.requested_vehicle_type_id) : null
            };

            const quotes = await getQuote(payload); 
            setResults(quotes); 
        } catch (e) { console.error(e); } finally { setLoading(false); } 
    };

    return (
        <Row>
            <Col md={5} className="border-end">
                <h5>Simulador de Tarifas</h5>
                <Form.Group className="mb-2">
                    <Form.Label className="small">Cliente</Form.Label>
                    <Form.Select size="sm" value={sim.client_id} onChange={e => setSim({...sim, client_id: e.target.value})}>
                        <option value="">Seleccione...</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </Form.Select>
                </Form.Group>
                
                <Form.Group className="mb-2">
                    <Form.Label className="small">Fecha/Hora</Form.Label>
                    <Form.Control type="datetime-local" size="sm" value={sim.scheduled_time} onChange={e => setSim({...sim, scheduled_time: e.target.value})} />
                </Form.Group>

                <Row>
                    <Col>
                        <Form.Group className="mb-2">
                            <Form.Label className="small">Vehículo (Opcional)</Form.Label>
                            <Form.Select size="sm" value={sim.requested_vehicle_type_id} onChange={e => setSim({...sim, requested_vehicle_type_id: e.target.value})}>
                                <option value="">Cualquiera</option>
                                {vehicleTypes.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-2">
                            <Form.Label className="small">Pasajeros</Form.Label>
                            <Form.Control type="number" size="sm" min="1" value={sim.passenger_count} onChange={e => setSim({...sim, passenger_count: parseInt(e.target.value)})} />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group className="mb-2"><Form.Label className="small">Origen</Form.Label><AddressAutocomplete value={sim.pickup_address} onChange={v => setSim({...sim, pickup_address: v})} onSelect={p => { setSim({...sim, pickup_address: p.address}); setPickupLoc({lat:p.lat, lng:p.lng}); }} /></Form.Group>
                <Form.Group className="mb-2"><Form.Label className="small">Destino</Form.Label><AddressAutocomplete value={sim.dropoff_address} onChange={v => setSim({...sim, dropoff_address: v})} onSelect={p => { setSim({...sim, dropoff_address: p.address}); setDropoffLoc({lat:p.lat, lng:p.lng}); }} /></Form.Group>
                <Button variant="primary" className="w-100 mt-2" onClick={handleSimulate} disabled={loading}>{loading ? 'Calculando...' : '🔎 Simular Precio'}</Button>
            </Col>
            <Col md={7}>
                <h5 className="mb-3">Resultados</h5>
                {results.length === 0 && !loading && <div className="text-muted text-center mt-5">Configura la ruta y el cliente para ver precios disponibles.</div>}
                {results.map((r, i) => {
                    const venta = Math.round(r.final_price || 0);
                    const costo = Math.round(r.cost_clp || r.cost_local || 0);
                    const margen = venta - costo;
                    const pctMargen = venta > 0 ? Math.round((margen / venta) * 100) : 0;

                    return (
                        <Card key={i} className="mb-2 shadow-sm p-3 border-0 bg-light">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <h6 className="mb-0 text-primary fw-bold">{r.vehicle_name}</h6>
                                        {r.provider_name && (
                                            <Badge bg="secondary" className="fw-normal" style={{ fontSize: '11px' }}>
                                                🚚 Proveedor: {r.provider_name}
                                            </Badge>
                                        )}
                                    </div>
                                    <small className="text-muted d-block mb-1">
                                        📍 {r.origin_zone_name || 'Origen'} ➝ 🏢 {r.dest_zone_name || 'Destino'}
                                    </small>
                                    {r.applied_rules && r.applied_rules.length > 0 && (
                                        <div style={{ fontSize: '0.75em' }} className="text-secondary">
                                            ⚡ Reglas aplicadas: {r.applied_rules.map(rule => rule.name).join(', ')}
                                        </div>
                                    )}
                                </div>
                                <div className="text-end">
                                    <div className="mb-1">
                                        <span className="small text-muted me-1">Valor a Cobrar:</span>
                                        <span className="h5 mb-0 fw-bold text-dark">${venta.toLocaleString('es-CL')}</span>
                                    </div>
                                    <div className="small mb-1 text-danger">
                                        <span className="text-muted">Costo Proveedor: </span>
                                        <span className="fw-bold">${costo.toLocaleString('es-CL')} {r.local_currency_code || 'CLP'}</span>
                                    </div>
                                    <div className="small">
                                        <span className="text-muted">Margen Estimado: </span>
                                        <span className={`fw-bold ${margen >= 0 ? 'text-success' : 'text-danger'}`}>
                                            ${margen.toLocaleString('es-CL')} ({pctMargen}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </Col>
        </Row>
    );
};

const MarginsRulesManager = () => {
    const [rules, setRules] = useState([]);
    const [zones, setZones] = useState([]);
    const [providers, setProviders] = useState([]);
    const [bases, setBases] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        origin_zone_id: '',
        destination_zone_id: '',
        origin_zone_ids: [],
        destination_zone_ids: [],
        provider_id: '',
        base_id: '',
        vehicle_type_id: '',
        vehicle_type_ids: [],
        is_bidirectional: false,
        margin_percentage: 20,
        is_active: true
    });

    const loadData = async () => {
        setLoading(true);
        try {
            const [rList, zList, pList, bList, vList] = await Promise.all([
                getMarginRules(),
                getZones(),
                getProviders(),
                getBases(),
                getVehicleTypes()
            ]);
            setRules(rList);
            setZones(zList);
            setProviders(pList);
            setBases(bList);
            setVehicleTypes(vList || []);
        } catch (e) {
            console.error("Error al cargar datos en gestor de márgenes:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtrar proveedores según la base seleccionada (revisando base principal y bases asociadas)
    const filteredProviders = useMemo(() => {
        if (!formData.base_id) return providers;
        return providers.filter(p => 
            String(p.base_id) === String(formData.base_id) || 
            p.bases?.some(b => String(b.id) === String(formData.base_id))
        );
    }, [providers, formData.base_id]);

    // Limpiar proveedor si cambia la base y deja de ser válido
    useEffect(() => {
        if (formData.base_id && formData.provider_id) {
            const stillValid = providers.some(p => 
                String(p.id) === String(formData.provider_id) && 
                (String(p.base_id) === String(formData.base_id) || 
                 p.bases?.some(b => String(b.id) === String(formData.base_id)))
            );
            if (!stillValid) {
                setFormData(prev => ({ ...prev, provider_id: '' }));
            }
        }
    }, [formData.base_id, formData.provider_id, providers]);

    const handleSave = async (e) => {
        e.preventDefault();
        
        try {
            if (editingRule) {
                const payload = {
                    origin_zone_id: formData.origin_zone_id ? parseInt(formData.origin_zone_id) : null,
                    destination_zone_id: formData.destination_zone_id ? parseInt(formData.destination_zone_id) : null,
                    provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
                    base_id: formData.base_id ? parseInt(formData.base_id) : null,
                    vehicle_type_id: formData.vehicle_type_id ? parseInt(formData.vehicle_type_id) : null,
                    margin_percentage: parseFloat(formData.margin_percentage),
                    is_active: formData.is_active
                };
                await updateMarginRule(editingRule.id, payload);
            } else {
                // Crear combinaciones para selección múltiple
                const originIds = formData.origin_zone_ids.length > 0 ? formData.origin_zone_ids : [null];
                const destIds = formData.destination_zone_ids.length > 0 ? formData.destination_zone_ids : [null];
                const vTypeIds = formData.vehicle_type_ids.length > 0 ? formData.vehicle_type_ids : [null];
                
                // Ejecutar creación secuencialmente
                for (const oId of originIds) {
                    for (const dId of destIds) {
                        for (const vId of vTypeIds) {
                            const payload = {
                                origin_zone_id: oId ? parseInt(oId) : null,
                                destination_zone_id: dId ? parseInt(dId) : null,
                                provider_id: formData.provider_id ? parseInt(formData.provider_id) : null,
                                base_id: formData.base_id ? parseInt(formData.base_id) : null,
                                vehicle_type_id: vId ? parseInt(vId) : null,
                                margin_percentage: parseFloat(formData.margin_percentage),
                                is_active: formData.is_active
                            };
                            await createMarginRule(payload);
                            
                            // Crear la vuelta si es bidireccional y origen/destino existen
                            if (formData.is_bidirectional && oId && dId) {
                                await createMarginRule({
                                    ...payload,
                                    origin_zone_id: parseInt(dId),
                                    destination_zone_id: parseInt(oId)
                                });
                            }
                        }
                    }
                }
            }
            setShowModal(false);
            loadData();
        } catch (e) {
            alert("Error al guardar la regla de margen.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar esta regla de margen?")) return;
        try {
            await deleteMarginRule(id);
            loadData();
        } catch (e) {
            alert("Error al eliminar la regla de margen.");
        }
    };

    const openCreate = () => {
        setEditingRule(null);
        setFormData({
            origin_zone_id: '',
            destination_zone_id: '',
            origin_zone_ids: [],
            destination_zone_ids: [],
            provider_id: '',
            base_id: '',
            vehicle_type_id: '',
            vehicle_type_ids: [],
            is_bidirectional: false,
            margin_percentage: 20,
            is_active: true
        });
        setShowModal(true);
    };

    const openEdit = (rule) => {
        setEditingRule(rule);
        setFormData({
            origin_zone_id: rule.origin_zone_id || '',
            destination_zone_id: rule.destination_zone_id || '',
            origin_zone_ids: rule.origin_zone_id ? [rule.origin_zone_id] : [],
            destination_zone_ids: rule.destination_zone_id ? [rule.destination_zone_id] : [],
            provider_id: rule.provider_id || '',
            base_id: rule.base_id || '',
            vehicle_type_id: rule.vehicle_type_id || '',
            vehicle_type_ids: rule.vehicle_type_id ? [rule.vehicle_type_id] : [],
            is_bidirectional: false,
            margin_percentage: rule.margin_percentage,
            is_active: rule.is_active
        });
        setShowModal(true);
    };

    const getZoneName = (id) => zones.find(z => z.id === id)?.name || 'Cualquiera';
    const getProviderName = (id) => providers.find(p => p.id === id)?.name || 'Cualquiera';
    const getBaseName = (id) => bases.find(b => b.id === id)?.name || 'Cualquiera';

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>📈 Reglas de Margen Dinámico</h5>
                <Button size="sm" variant="success" onClick={openCreate}>+ Crear Regla de Margen</Button>
            </div>
            
            <Table striped hover responsive size="sm" className="small align-middle shadow-sm bg-white rounded overflow-hidden">
                    <thead className="bg-light">
                        <tr>
                            <th>Origen</th>
                            <th>Destino</th>
                            <th>Proveedor</th>
                            <th>Base/País</th>
                            <th>Tipo Vehículo</th>
                            <th>Margen</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" className="text-center py-4 text-muted">Cargando reglas...</td>
                            </tr>
                        ) : (
                            rules.length === 0 ? (
                                <tr><td colSpan="8" className="text-center text-muted py-3">No hay reglas de margen configuradas. Se aplicará el 20% general por defecto.</td></tr>
                            ) : (
                                rules.map(r => (
                                    <tr key={r.id}>
                                        <td>{getZoneName(r.origin_zone_id)}</td>
                                        <td>{getZoneName(r.destination_zone_id)}</td>
                                        <td>{getProviderName(r.provider_id)}</td>
                                        <td>{getBaseName(r.base_id)}</td>
                                        <td>{r.vehicle_type_id ? (vehicleTypes.find(v => v.id === r.vehicle_type_id)?.name || 'N/A') : 'Cualquiera'}</td>
                                        <td><strong>{r.margin_percentage}%</strong></td>
                                        <td>
                                            <Badge bg={r.is_active ? 'success' : 'secondary'}>
                                                {r.is_active ? 'Activa' : 'Inactiva'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Button size="xs" variant="primary" className="me-1 px-2" onClick={() => openEdit(r)}>Editar</Button>
                                            <Button size="xs" variant="danger" className="px-2" onClick={() => handleDelete(r.id)}>Eliminar</Button>
                                        </td>
                                    </tr>
                                ))
                            )
                        )}
                    </tbody>
                </Table>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{editingRule ? 'Editar Regla de Margen' : 'Nueva Regla de Margen'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Alert variant="info" className="small py-2 mb-3">
                            <strong>💡 ¿Cómo aplicar márgenes?</strong><br />
                            • <strong>Margen por Base completa:</strong> Selecciona Base / Sucursal y deja vacío todo lo demás.<br />
                            • <strong>Margen por Proveedor completo:</strong> Selecciona Proveedor y deja vacío todo lo demás.<br />
                            • <strong>Margen Global:</strong> Deja en blanco (Cualquiera) todos los campos de filtrado.
                        </Alert>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Base / Sucursal</Form.Label>
                                    <SearchableSelect 
                                        options={bases} 
                                        value={formData.base_id} 
                                        onChange={val => setFormData({...formData, base_id: val})} 
                                        placeholder="Cualquiera"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Proveedor</Form.Label>
                                    <SearchableSelect 
                                        options={filteredProviders} 
                                        value={formData.provider_id} 
                                        onChange={val => setFormData({...formData, provider_id: val})} 
                                        placeholder={formData.base_id ? "Cualquier proveedor de esta base" : "Cualquiera (Todos)"}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Zona Origen</Form.Label>
                                    {editingRule ? (
                                        <SearchableSelect 
                                            options={zones} 
                                            value={formData.origin_zone_id} 
                                            onChange={val => setFormData({...formData, origin_zone_id: val})} 
                                            placeholder="Cualquiera"
                                        />
                                    ) : (
                                        <SearchableMultiSelect 
                                            options={zones} 
                                            selected={formData.origin_zone_ids} 
                                            onChange={ids => setFormData({...formData, origin_zone_ids: ids})} 
                                            placeholder="Cualquiera (Todas)"
                                        />
                                    )}
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Zona Destino</Form.Label>
                                    {editingRule ? (
                                        <SearchableSelect 
                                            options={zones} 
                                            value={formData.destination_zone_id} 
                                            onChange={val => setFormData({...formData, destination_zone_id: val})} 
                                            placeholder="Cualquiera"
                                        />
                                    ) : (
                                        <SearchableMultiSelect 
                                            options={zones} 
                                            selected={formData.destination_zone_ids} 
                                            onChange={ids => setFormData({...formData, destination_zone_ids: ids})} 
                                            placeholder="Cualquiera (Todas)"
                                        />
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        {!editingRule && (formData.origin_zone_ids?.length > 0 || formData.destination_zone_ids?.length > 0) && (
                            <Form.Group className="mb-3">
                                <Form.Check 
                                    type="checkbox" 
                                    id="margin-bidirectional" 
                                    label="Crear también la ruta de vuelta (Bidireccional)" 
                                    checked={formData.is_bidirectional} 
                                    onChange={e => setFormData({...formData, is_bidirectional: e.target.checked})} 
                                />
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Tipo de Vehículo</Form.Label>
                            {editingRule ? (
                                <Form.Select 
                                    value={formData.vehicle_type_id} 
                                    onChange={e => setFormData({...formData, vehicle_type_id: e.target.value})}
                                >
                                    <option value="">Cualquiera (Todos)</option>
                                    {vehicleTypes.map(v => (
                                        <option key={v.id} value={v.id}>{v.name}</option>
                                    ))}
                                </Form.Select>
                            ) : (
                                <>
                                    <div className="d-flex align-items-center mb-1">
                                        <Form.Check 
                                            type="checkbox" 
                                            id="select-all-vtypes" 
                                            label="Marcar Todos" 
                                            checked={formData.vehicle_type_ids?.length === vehicleTypes.length} 
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setFormData({...formData, vehicle_type_ids: vehicleTypes.map(v => v.id)});
                                                } else {
                                                    setFormData({...formData, vehicle_type_ids: []});
                                                }
                                            }}
                                            className="small text-muted"
                                            style={{ fontSize: '11px' }}
                                        />
                                    </div>
                                    <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #dee2e6', padding: '8px', borderRadius: '4px', background: '#fff' }}>
                                        {vehicleTypes.map(v => (
                                            <Form.Check 
                                                key={v.id}
                                                type="checkbox"
                                                id={`new-vtype-${v.id}`}
                                                label={v.name}
                                                checked={formData.vehicle_type_ids?.includes(v.id)}
                                                onChange={e => {
                                                    const currentIds = formData.vehicle_type_ids || [];
                                                    if (e.target.checked) {
                                                        setFormData({ ...formData, vehicle_type_ids: [...currentIds, v.id] });
                                                    } else {
                                                        setFormData({ ...formData, vehicle_type_ids: currentIds.filter(id => id !== v.id) });
                                                    }
                                                }}
                                                style={{ fontSize: '12px', margin: '3px 0' }}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="small fw-bold">Margen de Ganancia (%)</Form.Label>
                                    <Form.Control size="sm" type="number" step="0.1" min="0" max="99" required value={formData.margin_percentage} onChange={e => setFormData({...formData, margin_percentage: e.target.value})} />
                                </Form.Group>
                            </Col>
                            <Col md={6} className="d-flex align-items-center">
                                <Form.Group className="mt-3">
                                    <Form.Check type="checkbox" id="margin-rule-active" label="Regla Activa" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button size="sm" variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button size="sm" variant="success" type="submit">Guardar Regla</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

const TariffChangeLogsManager = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filtros seleccionados
    const [selectedProviders, setSelectedProviders] = useState([]);
    const [selectedOrigins, setSelectedOrigins] = useState([]);
    const [selectedDestinations, setSelectedDestinations] = useState([]);
    const [selectedVehicles, setSelectedVehicles] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const data = await getTariffChangeLogs();
            setLogs(data || []);
        } catch (e) {
            console.error("Error al cargar logs históricos:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, []);

    const formatDateTime = (dtStr) => {
        if (!dtStr) return '---';
        try {
            const date = new Date(dtStr);
            return date.toLocaleString('es-CL');
        } catch {
            return dtStr;
        }
    };

    const formatCurrency = (val) => {
        if (val === null || val === undefined) return '---';
        return `$${Number(val).toLocaleString('es-CL')}`;
    };

    // Extraer opciones únicas para los filtros basados en la data
    const uniqueProviders = useMemo(() => {
        const names = logs.map(l => l.provider?.name || 'Cualquiera');
        return Array.from(new Set(names)).sort();
    }, [logs]);

    const uniqueOrigins = useMemo(() => {
        const names = logs.map(l => l.origin_label || 'Cualquiera');
        return Array.from(new Set(names)).sort();
    }, [logs]);

    const uniqueDestinations = useMemo(() => {
        const names = logs.map(l => l.destination_label || 'Cualquiera');
        return Array.from(new Set(names)).sort();
    }, [logs]);

    const uniqueVehicles = useMemo(() => {
        const names = logs.map(l => l.vehicle_type_name || 'Cualquiera');
        return Array.from(new Set(names)).sort();
    }, [logs]);

    const uniqueUsers = useMemo(() => {
        const names = logs.map(l => l.user_email || 'system');
        return Array.from(new Set(names)).sort();
    }, [logs]);

    // Filtrar los logs en base a la selección
    const filteredLogs = useMemo(() => {
        return logs.filter(l => {
            const providerName = l.provider?.name || 'Cualquiera';
            const originLabel = l.origin_label || 'Cualquiera';
            const destLabel = l.destination_label || 'Cualquiera';
            const vehicleName = l.vehicle_type_name || 'Cualquiera';
            const userEmail = l.user_email || 'system';

            if (selectedProviders.length > 0 && !selectedProviders.includes(providerName)) return false;
            if (selectedOrigins.length > 0 && !selectedOrigins.includes(originLabel)) return false;
            if (selectedDestinations.length > 0 && !selectedDestinations.includes(destLabel)) return false;
            if (selectedVehicles.length > 0 && !selectedVehicles.includes(vehicleName)) return false;
            if (selectedUsers.length > 0 && !selectedUsers.includes(userEmail)) return false;

            return true;
        });
    }, [logs, selectedProviders, selectedOrigins, selectedDestinations, selectedVehicles, selectedUsers]);

    const hasActiveFilters = selectedProviders.length > 0 || selectedOrigins.length > 0 || selectedDestinations.length > 0 || selectedVehicles.length > 0 || selectedUsers.length > 0;

    const clearAllFilters = () => {
        setSelectedProviders([]);
        setSelectedOrigins([]);
        setSelectedDestinations([]);
        setSelectedVehicles([]);
        setSelectedUsers([]);
    };

    // Componente interno para dropdown multiselección
    const MultiSelectDropdown = ({ title, options, selectedValues, onChange }) => {
        const handleToggle = (val) => {
            if (selectedValues.includes(val)) {
                onChange(selectedValues.filter(v => v !== val));
            } else {
                onChange([...selectedValues, val]);
            }
        };

        return (
            <Dropdown className="me-2 mb-2">
                <Dropdown.Toggle size="sm" variant="outline-secondary" className="d-flex align-items-center">
                    {title} {selectedValues.length > 0 && <Badge bg="primary" className="ms-1">{selectedValues.length}</Badge>}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: '300px', overflowY: 'auto', padding: '10px', minWidth: '220px' }}>
                    {selectedValues.length > 0 && (
                        <>
                            <div className="d-flex justify-content-between align-items-center mb-2 px-1">
                                <span className="small text-muted fw-bold">Seleccionados</span>
                                <Button 
                                    size="sm" 
                                    variant="link" 
                                    className="text-danger p-0 text-decoration-none small fw-bold"
                                    onClick={(e) => { e.stopPropagation(); onChange([]); }}
                                >
                                    Limpiar
                                </Button>
                            </div>
                            <Dropdown.Divider />
                        </>
                    )}
                    {options.map(opt => (
                        <div key={opt} className="px-1 py-1" onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                                type="checkbox"
                                id={`filter-${title}-${opt}`}
                                label={opt}
                                checked={selectedValues.includes(opt)}
                                onChange={() => handleToggle(opt)}
                                className="small cursor-pointer"
                            />
                        </div>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>📜 Historial de Auditoría de Costos y Ventas de Tarifas</h5>
                <Button size="sm" variant="outline-primary" onClick={loadLogs}>🔄 Actualizar</Button>
            </div>

            {/* Panel de Filtros */}
            <div className="bg-light p-3 rounded mb-3 border">
                <div className="fw-bold text-muted small text-uppercase mb-2">Filtros de Auditoría:</div>
                <div className="d-flex flex-wrap align-items-center">
                    <MultiSelectDropdown 
                        title="Proveedor" 
                        options={uniqueProviders} 
                        selectedValues={selectedProviders} 
                        onChange={setSelectedProviders} 
                    />
                    <MultiSelectDropdown 
                        title="Origen" 
                        options={uniqueOrigins} 
                        selectedValues={selectedOrigins} 
                        onChange={setSelectedOrigins} 
                    />
                    <MultiSelectDropdown 
                        title="Destino" 
                        options={uniqueDestinations} 
                        selectedValues={selectedDestinations} 
                        onChange={setSelectedDestinations} 
                    />
                    <MultiSelectDropdown 
                        title="Vehículo" 
                        options={uniqueVehicles} 
                        selectedValues={selectedVehicles} 
                        onChange={setSelectedVehicles} 
                    />
                    <MultiSelectDropdown 
                        title="Modificado por" 
                        options={uniqueUsers} 
                        selectedValues={selectedUsers} 
                        onChange={setSelectedUsers} 
                    />

                    {hasActiveFilters && (
                        <Button size="sm" variant="outline-danger" className="mb-2 ms-2" onClick={clearAllFilters}>
                            ❌ Limpiar Todos los Filtros
                        </Button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4 text-muted">Cargando historial...</div>
            ) : (
                <div className="table-responsive">
                    <Table striped hover size="sm" className="small align-middle shadow-sm bg-white rounded overflow-hidden">
                        <thead className="bg-light">
                            <tr>
                                <th>Fecha/Hora</th>
                                <th>Proveedor</th>
                                <th>Ruta (Origen ➝ Destino)</th>
                                <th>Vehículo</th>
                                <th>Costo Anterior</th>
                                <th>Costo Nuevo</th>
                                <th>Venta Anterior (CLP)</th>
                                <th>Venta Nueva (CLP)</th>
                                <th>Detalles del Cambio</th>
                                <th>Modificado Por</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLogs.length === 0 ? (
                                <tr><td colSpan="10" className="text-center text-muted py-3">No hay registros de cambios en las tarifas que coincidan con los filtros.</td></tr>
                            ) : (
                                filteredLogs.map(l => (
                                    <tr key={l.id}>
                                        <td>{formatDateTime(l.timestamp)}</td>
                                        <td><strong>{l.provider?.name || 'Cualquiera'}</strong></td>
                                        <td>{l.origin_label} ➝ {l.destination_label}</td>
                                        <td><Badge bg="dark">{l.vehicle_type_name}</Badge></td>
                                        <td className="text-muted">{formatCurrency(l.old_cost_local)}</td>
                                        <td className="fw-bold">{formatCurrency(l.new_cost_local)}</td>
                                        <td className="text-muted">{formatCurrency(l.old_sale_clp)}</td>
                                        <td className="text-success fw-bold">{formatCurrency(l.new_sale_clp)}</td>
                                        <td>
                                            {l.change_description ? (
                                                <span className="text-primary small fw-semibold d-block" style={{ maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                    {l.change_description}
                                                </span>
                                            ) : (
                                                <span className="text-muted small italic">Solo Costo/Venta</span>
                                            )}
                                        </td>
                                        <td><small className="text-muted">{l.user_email}</small></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
};

const MinPriceChangeLogsManager = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getMinPriceChangeLogs();
            setLogs(data || []);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    const formatDateTime = (dtStr) => {
        if (!dtStr) return '---';
        try {
            const date = new Date(dtStr);
            return date.toLocaleString('es-CL');
        } catch {
            return dtStr;
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>📜 Auditoría de Mínimos Booking (Descuentos de Proveedores)</h5>
                <Button size="sm" variant="outline-primary" onClick={loadLogs}>🔄 Actualizar</Button>
            </div>

            {loading ? (
                <div className="text-center py-4 text-muted">Cargando historial...</div>
            ) : (
                <div className="table-responsive">
                    <Table striped hover size="sm" className="small align-middle shadow-sm bg-white rounded overflow-hidden">
                        <thead className="bg-light">
                            <tr>
                                <th>Fecha/Hora</th>
                                <th>Proveedor</th>
                                <th>Ruta (Origen ➝ Destino)</th>
                                <th>Vehículo</th>
                                <th>Descuento Anterior</th>
                                <th>Descuento Nuevo</th>
                                <th>Estado Regla</th>
                                <th>Modificado Por</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr><td colSpan="8" className="text-center text-muted py-3">No hay registros de cambios en los mínimos de Booking.</td></tr>
                            ) : (
                                logs.map(l => (
                                    <tr key={l.id}>
                                        <td>{formatDateTime(l.timestamp)}</td>
                                        <td><strong>{l.provider?.name || `Proveedor ID ${l.provider_id}`}</strong></td>
                                        <td>{l.origin_label} ➝ {l.destination_label}</td>
                                        <td><Badge bg="dark">{l.vehicle_type_name}</Badge></td>
                                        <td className="text-muted">{l.old_discount_percentage}%</td>
                                        <td className="fw-bold text-danger">{l.new_discount_percentage}%</td>
                                        <td>
                                            {l.is_active ? (
                                                <Badge bg="success">Activa</Badge>
                                            ) : (
                                                <Badge bg="secondary">Inactiva</Badge>
                                            )}
                                        </td>
                                        <td><small className="text-muted">{l.user_email}</small></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            )}
        </div>
    );
};

const BookingAutoAssignManager = () => {
    const [bases, setBases] = useState([]);
    const [providers, setProviders] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [onlyWithRules, setOnlyWithRules] = useState(false);

    const [showRuleModal, setShowRuleModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [savingRule, setSavingRule] = useState(false);
    const [deletingRuleId, setDeletingRuleId] = useState(null);
    const [formError, setFormError] = useState('');
    const [ruleForm, setRuleForm] = useState({
        base_id: '',
        provider_id: '',
        vehicle_type_id: '',
        vehicle_id: '',
        apply_to_general_tariffs: false
    });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [bData, pData, vData, dData, vtData, rData] = await Promise.all([
                getBases(),
                getProviders(),
                getVehicles({ limit: 1000 }),
                getDrivers({ limit: 1000 }),
                getVehicleTypes(),
                getBookingAutoRules()
            ]);
            setBases(bData || []);
            setProviders(pData || []);
            setVehicles(vData || []);
            setDrivers(dData || []);
            setVehicleTypes(vtData || []);
            setRules(rData || []);
        } catch (e) {
            console.error("Error cargando datos de Auto-Asignación Booking:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const getVehiclesForProvider = useCallback((providerIdStr) => {
        if (!providerIdStr) return [];
        const provId = Number(providerIdStr);
        const provObj = providers.find(p => Number(p.id) === provId);
        const driverIdsOfProvider = new Set(
            drivers.filter(d => Number(d.provider_id) === provId).map(d => Number(d.id))
        );
        if (provObj && provObj.owner_id) {
            driverIdsOfProvider.add(Number(provObj.owner_id));
        }

        const matched = vehicles.filter(v => {
            if (v.is_active === false) return false;
            if (Number(v.provider_id) === provId) return true;
            if (v.current_driver_id && driverIdsOfProvider.has(Number(v.current_driver_id))) return true;
            if (v.owner_id && driverIdsOfProvider.has(Number(v.owner_id))) return true;
            if (provObj && provObj.mobile_range_start !== null && provObj.mobile_range_end !== null && v.internal_code) {
                const codeNum = parseInt(v.internal_code, 10);
                if (!isNaN(codeNum) && codeNum >= provObj.mobile_range_start && codeNum <= provObj.mobile_range_end) {
                    return true;
                }
            }
            return false;
        });

        return matched.sort((a, b) => String(a.internal_code || '').localeCompare(String(b.internal_code || ''), undefined, { numeric: true }));
    }, [providers, vehicles, drivers]);

    const getDriverForSelection = useCallback((providerIdStr, vehicleIdStr) => {
        if (!vehicleIdStr && !providerIdStr) return null;
        if (vehicleIdStr) {
            const veh = vehicles.find(v => Number(v.id) === Number(vehicleIdStr));
            if (veh) {
                const dId = veh.current_driver_id || veh.owner_id;
                if (dId) {
                    const drv = drivers.find(d => Number(d.id) === Number(dId));
                    if (drv) return drv;
                }
            }
        }
        if (providerIdStr) {
            const prov = providers.find(p => Number(p.id) === Number(providerIdStr));
            if (prov && prov.owner_id) {
                const drv = drivers.find(d => Number(d.id) === Number(prov.owner_id));
                if (drv) return drv;
            }
            const firstDrv = drivers.find(d => Number(d.provider_id) === Number(providerIdStr) && d.is_active !== false);
            if (firstDrv) return firstDrv;
        }
        return null;
    }, [vehicles, drivers, providers]);

    const getDefaultVehicleIdForProvider = useCallback((providerIdStr) => {
        if (!providerIdStr) return '';
        const prov = providers.find(p => Number(p.id) === Number(providerIdStr));
        const provVehicles = getVehiclesForProvider(providerIdStr);
        if (prov && prov.booking_auto_vehicle_id && provVehicles.some(v => Number(v.id) === Number(prov.booking_auto_vehicle_id))) {
            return String(prov.booking_auto_vehicle_id);
        }
        if (provVehicles.length === 1) {
            return String(provVehicles[0].id);
        }
        if (prov && prov.owner_id) {
            const ownerVeh = provVehicles.find(v => Number(v.current_driver_id) === Number(prov.owner_id) || Number(v.owner_id) === Number(prov.owner_id));
            if (ownerVeh) return String(ownerVeh.id);
        }
        return '';
    }, [providers, getVehiclesForProvider]);

    const rulesByBase = useMemo(() => {
        const map = {};
        rules.forEach(r => {
            if (r.is_active === false) return;
            const bId = Number(r.base_id);
            if (!map[bId]) map[bId] = [];
            map[bId].push(r);
        });
        return map;
    }, [rules]);

    const openCreateModal = (preselectedBaseId = '') => {
        const baseIdStr = preselectedBaseId ? String(preselectedBaseId) : (bases[0] ? String(bases[0].id) : '');
        const baseProviders = providers.filter(p =>
            p.is_active !== false && (
                Number(p.base_id) === Number(baseIdStr) ||
                (p.bases && p.bases.some(pb => Number(pb.id) === Number(baseIdStr)))
            )
        );
        const defaultProvId = baseProviders.length === 1 ? String(baseProviders[0].id) : '';
        const defaultVehId = defaultProvId ? getDefaultVehicleIdForProvider(defaultProvId) : '';
        setEditingRule(null);
        setFormError('');
        setRuleForm({
            base_id: baseIdStr,
            provider_id: defaultProvId,
            vehicle_type_id: '',
            vehicle_id: defaultVehId,
            apply_to_general_tariffs: Number(baseIdStr) === 1 && !(rulesByBase[Number(baseIdStr)] || []).some(r => r.apply_to_general_tariffs)
        });
        setShowRuleModal(true);
    };

    const openEditModal = (ruleObj) => {
        setEditingRule(ruleObj);
        setFormError('');
        setRuleForm({
            base_id: String(ruleObj.base_id || ''),
            provider_id: String(ruleObj.provider_id || ''),
            vehicle_type_id: ruleObj.vehicle_type_id ? String(ruleObj.vehicle_type_id) : '',
            vehicle_id: String(ruleObj.vehicle_id || ''),
            apply_to_general_tariffs: Boolean(ruleObj.apply_to_general_tariffs)
        });
        setShowRuleModal(true);
    };

    const handleModalBaseChange = (newBaseIdStr) => {
        setFormError('');
        setRuleForm(prev => ({
            ...prev,
            base_id: newBaseIdStr,
            apply_to_general_tariffs: Number(newBaseIdStr) === 1 && !editingRule
        }));
    };

    const handleModalProviderChange = (newProvIdStr) => {
        setFormError('');
        const defaultVehId = getDefaultVehicleIdForProvider(newProvIdStr);
        setRuleForm(prev => {
            const currentVt = vehicleTypes.find(vt => String(vt.id) === String(prev.vehicle_type_id));
            const keepVt = !currentVt || !currentVt.provider_id || Number(currentVt.provider_id) === Number(newProvIdStr);
            return {
                ...prev,
                provider_id: newProvIdStr,
                vehicle_type_id: keepVt ? prev.vehicle_type_id : '',
                vehicle_id: defaultVehId
            };
        });
    };

    const handleSubmitRule = async (e) => {
        e.preventDefault();
        if (savingRule) return;

        if (!ruleForm.base_id) {
            setFormError('Selecciona la sucursal o ciudad para esta regla.');
            return;
        }
        if (!ruleForm.provider_id) {
            setFormError('Selecciona el proveedor ganador de la tarifa.');
            return;
        }
        if (!ruleForm.vehicle_id) {
            setFormError('Selecciona el vehículo (móvil) al que se le asignará automáticamente la reserva.');
            return;
        }

        setFormError('');
        setSavingRule(true);
        try {
            const payload = {
                base_id: parseInt(ruleForm.base_id, 10),
                provider_id: parseInt(ruleForm.provider_id, 10),
                vehicle_type_id: ruleForm.vehicle_type_id ? parseInt(ruleForm.vehicle_type_id, 10) : null,
                vehicle_id: parseInt(ruleForm.vehicle_id, 10),
                apply_to_general_tariffs: Boolean(ruleForm.apply_to_general_tariffs),
                is_active: true
            };

            if (editingRule) {
                await updateBookingAutoRule(editingRule.id, payload);
            } else {
                await createBookingAutoRule(payload);
            }
            setShowRuleModal(false);
            await loadData();
        } catch (err) {
            console.error(err);
            setFormError('No se pudo guardar la regla de auto-asignación. Intenta nuevamente.');
        } finally {
            setSavingRule(false);
        }
    };

    const handleDeleteRule = async (ruleObj) => {
        const prov = providers.find(p => Number(p.id) === Number(ruleObj.provider_id));
        const veh = vehicles.find(v => Number(v.id) === Number(ruleObj.vehicle_id));
        const vt = ruleObj.vehicle_type_id
            ? vehicleTypes.find(t => Number(t.id) === Number(ruleObj.vehicle_type_id))
            : null;
        const vtLabel = vt ? vt.name : 'Todos sus vehículos';
        if (!window.confirm(`¿Eliminar la regla automática de "${prov?.name || 'Proveedor'}" (${vtLabel} → Móvil ${veh?.internal_code || ruleObj.vehicle_id})?`)) {
            return;
        }
        setDeletingRuleId(ruleObj.id);
        try {
            await deleteBookingAutoRule(ruleObj.id);
            await loadData();
        } catch (err) {
            console.error(err);
            alert('Error al eliminar la regla de auto-asignación.');
        } finally {
            setDeletingRuleId(null);
        }
    };

    const filteredBases = useMemo(() => {
        const q = searchQuery.toLowerCase().trim();
        return bases.filter(b => {
            const bRules = rulesByBase[Number(b.id)] || [];
            if (onlyWithRules && bRules.length === 0) return false;
            if (!q) return true;
            if ((b.name || '').toLowerCase().includes(q) || (b.country_code || '').toLowerCase().includes(q)) {
                return true;
            }
            return bRules.some(r => {
                const prov = providers.find(p => Number(p.id) === Number(r.provider_id));
                const veh = vehicles.find(v => Number(v.id) === Number(r.vehicle_id));
                const vt = r.vehicle_type_id ? vehicleTypes.find(t => Number(t.id) === Number(r.vehicle_type_id)) : null;
                return (
                    (prov?.name || '').toLowerCase().includes(q) ||
                    String(veh?.internal_code || '').toLowerCase().includes(q) ||
                    (veh?.plate || '').toLowerCase().includes(q) ||
                    (vt?.name || '').toLowerCase().includes(q) ||
                    (vt?.booking_category || '').toLowerCase().includes(q)
                );
            });
        });
    }, [bases, searchQuery, onlyWithRules, rulesByBase, providers, vehicles, vehicleTypes]);

    // Opciones del modal
    const modalBaseProviders = useMemo(() => {
        if (!ruleForm.base_id) return [];
        return providers.filter(p =>
            p.is_active !== false && (
                Number(p.base_id) === Number(ruleForm.base_id) ||
                (p.bases && p.bases.some(pb => Number(pb.id) === Number(ruleForm.base_id)))
            )
        );
    }, [providers, ruleForm.base_id]);

    const modalOtherProviders = useMemo(() => {
        return providers.filter(p =>
            p.is_active !== false && !modalBaseProviders.some(bp => Number(bp.id) === Number(p.id))
        );
    }, [providers, modalBaseProviders]);

    const modalProviderVehicles = useMemo(() => {
        return getVehiclesForProvider(ruleForm.provider_id);
    }, [getVehiclesForProvider, ruleForm.provider_id]);

    const modalLinkedDriver = useMemo(() => {
        return getDriverForSelection(ruleForm.provider_id, ruleForm.vehicle_id);
    }, [getDriverForSelection, ruleForm.provider_id, ruleForm.vehicle_id]);

    const generalVehicleTypes = useMemo(() => {
        return vehicleTypes.filter(vt => !vt.provider_id);
    }, [vehicleTypes]);

    const providerCustomVehicleTypes = useMemo(() => {
        if (!ruleForm.provider_id) return [];
        return vehicleTypes.filter(vt => Number(vt.provider_id) === Number(ruleForm.provider_id));
    }, [vehicleTypes, ruleForm.provider_id]);

    const totalActiveRules = useMemo(() => {
        return rules.filter(r => r.is_active !== false).length;
    }, [rules]);

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                    <h5 id="booking-auto-assign-heading" className="mb-1">
                        🤖 Mantenedor de Auto-Asignación Booking.com{' '}
                        <Badge bg="success" pill className="ms-1 align-middle" style={{ fontSize: '0.72rem' }}>
                            {totalActiveRules} {totalActiveRules === 1 ? 'regla activa' : 'reglas activas'}
                        </Badge>
                    </h5>
                    <p className="text-muted small mb-0">
                        <strong>Quien gana con su tarifa toma la reserva.</strong> Define por sucursal a qué <strong>Móvil</strong> se asigna automáticamente según el <strong>Proveedor ganador</strong> y el <strong>Tipo de Vehículo</strong> (o todos sus vehículos).
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                    <div>
                        <Form.Label htmlFor="booking-auto-search" className="visually-hidden">
                            Buscar sucursal, proveedor, tipo de vehículo o móvil
                        </Form.Label>
                        <Form.Control
                            id="booking-auto-search"
                            name="booking_auto_search"
                            type="search"
                            size="sm"
                            placeholder="Buscar sucursal, proveedor o móvil..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            style={{ width: '260px' }}
                        />
                    </div>
                    <Button
                        size="sm"
                        variant={onlyWithRules ? 'primary' : 'outline-secondary'}
                        onClick={() => setOnlyWithRules(prev => !prev)}
                    >
                        {onlyWithRules ? '✅ Solo con reglas' : '🏢 Todas las sucursales'}
                    </Button>
                    <Button size="sm" variant="success" onClick={() => openCreateModal('')}>
                        ➕ Nueva Regla
                    </Button>
                    <Button size="sm" variant="outline-primary" onClick={loadData} disabled={loading}>
                        🔄 Actualizar
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-4 text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando reglas de auto-asignación...
                </div>
            ) : (
                <div
                    className="table-responsive border rounded bg-white shadow-sm"
                    tabIndex={0}
                    role="region"
                    aria-labelledby="booking-auto-assign-heading"
                    style={{ maxHeight: '68vh' }}
                >
                    <Table hover bordered size="sm" className="align-middle small mb-0">
                        <thead className="bg-light" style={{ position: 'sticky', insetBlockStart: 0, zIndex: 1 }}>
                            <tr>
                                <th style={{ width: '20%' }}>Sucursal / Ciudad</th>
                                <th style={{ width: '13%' }}>Modo Actual</th>
                                <th style={{ width: '55%' }}>Reglas por Proveedor Ganador y Tipo de Vehículo</th>
                                <th style={{ width: '12%' }} className="text-center">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBases.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center py-4 text-muted">
                                        No se encontraron sucursales con ese filtro.
                                    </td>
                                </tr>
                            ) : (
                                filteredBases.map(b => {
                                    const bRules = rulesByBase[Number(b.id)] || [];
                                    const hasRules = bRules.length > 0;

                                    return (
                                        <tr key={b.id} className={hasRules ? 'table-success bg-opacity-10' : ''}>
                                            <td>
                                                <div className="fw-bold text-dark">{b.name}</div>
                                                <div className="text-muted" style={{ fontSize: '11px' }}>
                                                    País: {b.country_code} · Moneda: {b.currency_code}
                                                </div>
                                            </td>
                                            <td>
                                                {hasRules ? (
                                                    <Badge bg="success" className="px-2 py-1">
                                                        🤖 {bRules.length} {bRules.length === 1 ? 'regla activa' : 'reglas activas'}
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="secondary" className="px-2 py-1">
                                                        👤 Manual (Locución)
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {!hasRules ? (
                                                    <span className="text-muted">
                                                        Sin reglas automáticas — si gana un proveedor queda asignado a su empresa para que Locución elija móvil.
                                                    </span>
                                                ) : (
                                                    <div className="d-flex flex-column gap-2 py-1">
                                                        {bRules.map(rule => {
                                                            const prov = providers.find(p => Number(p.id) === Number(rule.provider_id));
                                                            const veh = vehicles.find(v => Number(v.id) === Number(rule.vehicle_id));
                                                            const vt = rule.vehicle_type_id
                                                                ? vehicleTypes.find(t => Number(t.id) === Number(rule.vehicle_type_id))
                                                                : null;
                                                            const linkedDrv = getDriverForSelection(rule.provider_id, rule.vehicle_id);

                                                            return (
                                                                <div
                                                                    key={rule.id}
                                                                    className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-2 py-1 rounded border bg-white"
                                                                >
                                                                    <div className="d-flex align-items-center flex-wrap gap-2">
                                                                        <span className="fw-bold text-dark">
                                                                            🚚 {prov ? prov.name : `Proveedor #${rule.provider_id}`}
                                                                        </span>
                                                                        {vt ? (
                                                                            <Badge bg="dark" className="fw-normal">
                                                                                🚙 {vt.name}
                                                                                {vt.booking_category && vt.booking_category !== vt.name
                                                                                    ? ` · ${vt.booking_category}`
                                                                                    : ''}
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge bg="primary" className="fw-normal">
                                                                                🌟 Todos sus vehículos
                                                                            </Badge>
                                                                        )}
                                                                        {rule.apply_to_general_tariffs && (
                                                                            <Badge
                                                                                bg="warning"
                                                                                text="dark"
                                                                                title="También aplica cuando gana una tarifa general de la sucursal sin proveedor en la matriz"
                                                                            >
                                                                                + Tarifas generales
                                                                            </Badge>
                                                                        )}
                                                                        <span className="text-muted">→</span>
                                                                        <Badge bg="success" className="fw-semibold">
                                                                            🚕 Móvil {veh ? veh.internal_code : `#${rule.vehicle_id}`}
                                                                            {veh?.plate ? ` (${veh.plate})` : ''}
                                                                        </Badge>
                                                                        {linkedDrv && (
                                                                            <Badge bg="info" text="dark" className="fw-semibold">
                                                                                👤 {linkedDrv.full_name}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className="d-flex gap-1">
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline-primary"
                                                                            className="py-0 px-2"
                                                                            title="Editar esta regla"
                                                                            onClick={() => openEditModal(rule)}
                                                                        >
                                                                            ✏️ Editar
                                                                        </Button>
                                                                        <Button
                                                                            size="sm"
                                                                            variant="outline-danger"
                                                                            className="py-0 px-2"
                                                                            title="Eliminar esta regla"
                                                                            disabled={deletingRuleId === rule.id}
                                                                            onClick={() => handleDeleteRule(rule)}
                                                                        >
                                                                            {deletingRuleId === rule.id ? (
                                                                                <Spinner animation="border" size="sm" />
                                                                            ) : (
                                                                                '🗑️'
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    size="sm"
                                                    variant="outline-success"
                                                    onClick={() => openCreateModal(b.id)}
                                                >
                                                    ➕ Agregar
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showRuleModal} onHide={() => !savingRule && setShowRuleModal(false)} centered>
                <Modal.Header closeButton={!savingRule}>
                    <Modal.Title className="fs-6 fw-bold">
                        {editingRule ? '✏️ Editar Regla de Auto-Asignación Booking' : '➕ Nueva Regla de Auto-Asignación Booking'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitRule} noValidate>
                    <Modal.Body>
                        {formError && (
                            <Alert variant="danger" role="alert" aria-live="polite" className="py-2 small">
                                {formError}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="rule-base-id" className="small fw-semibold">
                                1. Sucursal / Ciudad
                            </Form.Label>
                            <Form.Select
                                id="rule-base-id"
                                name="base_id"
                                size="sm"
                                value={ruleForm.base_id}
                                onChange={e => handleModalBaseChange(e.target.value)}
                            >
                                <option value="">— Selecciona una sucursal —</option>
                                {bases.map(b => (
                                    <option key={b.id} value={b.id}>
                                        {b.name} ({b.country_code} · {b.currency_code})
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="rule-provider-id" className="small fw-semibold">
                                2. Proveedor Ganador de la Tarifa
                            </Form.Label>
                            <Form.Select
                                id="rule-provider-id"
                                name="provider_id"
                                size="sm"
                                value={ruleForm.provider_id}
                                onChange={e => handleModalProviderChange(e.target.value)}
                            >
                                <option value="">— Selecciona el proveedor —</option>
                                {modalBaseProviders.length > 0 && (
                                    <optgroup label="Proveedores de esta sucursal">
                                        {modalBaseProviders.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                                {modalOtherProviders.length > 0 && (
                                    <optgroup label="Otros Proveedores">
                                        {modalOtherProviders.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                            </Form.Select>
                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                La regla solo actuará cuando la reserva de Booking sea ganada por la tarifa de este proveedor.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="rule-vehicle-type-id" className="small fw-semibold">
                                3. Tipo de Vehículo
                            </Form.Label>
                            <Form.Select
                                id="rule-vehicle-type-id"
                                name="vehicle_type_id"
                                size="sm"
                                value={ruleForm.vehicle_type_id}
                                onChange={e => {
                                    setFormError('');
                                    setRuleForm(prev => ({ ...prev, vehicle_type_id: e.target.value }));
                                }}
                            >
                                <option value="">🌟 Todos los vehículos que gane este proveedor (Recomendado)</option>
                                {generalVehicleTypes.length > 0 && (
                                    <optgroup label="Categorías Generales Booking">
                                        {generalVehicleTypes.map(vt => (
                                            <option key={vt.id} value={vt.id}>
                                                {vt.name} (hasta {vt.capacity} pax)
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                                {providerCustomVehicleTypes.length > 0 && (
                                    <optgroup label="Tipos específicos de este proveedor">
                                        {providerCustomVehicleTypes.map(vt => (
                                            <option key={vt.id} value={vt.id}>
                                                {vt.name} — Homologado a {vt.booking_category || 'STANDARD'} (hasta {vt.capacity} pax)
                                            </option>
                                        ))}
                                    </optgroup>
                                )}
                            </Form.Select>
                            <Form.Text className="text-muted" style={{ fontSize: '11px' }}>
                                Puedes dejar <strong>"Todos los vehículos"</strong> o crear reglas distintas si un mismo proveedor usa móviles diferentes según el tamaño del vehículo.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="rule-vehicle-id" className="small fw-semibold">
                                4. Vehículo (Móvil) a Asignar
                            </Form.Label>
                            <Form.Select
                                id="rule-vehicle-id"
                                name="vehicle_id"
                                size="sm"
                                value={ruleForm.vehicle_id}
                                disabled={!ruleForm.provider_id}
                                onChange={e => {
                                    setFormError('');
                                    setRuleForm(prev => ({ ...prev, vehicle_id: e.target.value }));
                                }}
                            >
                                <option value="">
                                    {!ruleForm.provider_id
                                        ? '— Primero selecciona un proveedor —'
                                        : modalProviderVehicles.length === 0
                                            ? '— Este proveedor no tiene móviles activos asociados —'
                                            : '— Selecciona el móvil —'}
                                </option>
                                {modalProviderVehicles.map(v => {
                                    const drv = drivers.find(d => Number(d.id) === Number(v.current_driver_id || v.owner_id));
                                    return (
                                        <option key={v.id} value={v.id}>
                                            Móvil {v.internal_code} — {v.plate || 'S/P'}{drv ? ` (${drv.full_name})` : ''}
                                        </option>
                                    );
                                })}
                            </Form.Select>
                            {ruleForm.vehicle_id && (
                                <div className="mt-2 small">
                                    {modalLinkedDriver ? (
                                        <Badge bg="info" text="dark" className="fw-semibold">
                                            👤 Conductor que recibirá el viaje: {modalLinkedDriver.full_name}
                                        </Badge>
                                    ) : (
                                        <Badge bg="warning" text="dark">
                                            ⚠️ Este móvil no tiene conductor titular vinculado actualmente
                                        </Badge>
                                    )}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-1">
                            <Form.Check
                                type="checkbox"
                                id="rule-general-tariffs"
                                name="apply_to_general_tariffs"
                                checked={Boolean(ruleForm.apply_to_general_tariffs)}
                                onChange={e => setRuleForm(prev => ({ ...prev, apply_to_general_tariffs: e.target.checked }))}
                                label={
                                    <span className="small">
                                        Aplicar también si gana una <strong>tarifa general de la sucursal</strong> (sin proveedor específico en la matriz, ej. CL - Santiago)
                                    </span>
                                }
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="py-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline-secondary"
                            disabled={savingRule}
                            onClick={() => setShowRuleModal(false)}
                        >
                            Cancelar
                        </Button>
                        <Button type="submit" size="sm" variant="success" disabled={savingRule}>
                            {savingRule ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-1" />
                                    Guardando...
                                </>
                            ) : (
                                '💾 Guardar Regla'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

const TariffManager = () => {
    const isLoaded = useGoogleMapsLoaded();
    const [activeTab, setActiveKey] = useState('zones');
    if (!isLoaded) return <div className="p-5 text-center text-muted">Cargando...</div>;
    return (
        <div className="p-3"><Card className="shadow-sm border-0"><Card.Header className="bg-white"><Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveKey(k)}><Nav.Item><Nav.Link eventKey="zones">📍 Zonas</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="bases">🏢 Sucursales</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="tariffs">💰 Matriz</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="distance">📏 Tarifas x Km</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="rules">⚡ Reglas</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="min_prices">📉 Mínimos Booking</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="margins">📈 Márgenes</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="tariff_logs">📜 Auditoría Costos</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="min_price_logs">📜 Auditoría Mínimos</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="rates">💱 Divisas</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="providers">🚚 Proveedores</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="booking_auto" className="fw-bold text-success">🤖 Auto-Asignación Booking</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="vehicle_types">🚙 Tipos Vehículo</Nav.Link></Nav.Item><Nav.Item><Nav.Link eventKey="simulator" className="text-primary fw-bold">🧪 Simulador</Nav.Link></Nav.Item></Nav></Card.Header><Card.Body>{activeTab === 'zones' && <ZonesManager />}{activeTab === 'bases' && <BasesManager />}{activeTab === 'tariffs' && <MatrixManager />}{activeTab === 'distance' && <DistanceTariffManager />}{activeTab === 'rules' && <RulesManager />}{activeTab === 'min_prices' && <MinPricesManager />}{activeTab === 'margins' && <MarginsRulesManager />}{activeTab === 'tariff_logs' && <TariffChangeLogsManager />}{activeTab === 'min_price_logs' && <MinPriceChangeLogsManager />}{activeTab === 'rates' && <ExchangeRatesManager />}{activeTab === 'providers' && <ProvidersManager />}{activeTab === 'booking_auto' && <BookingAutoAssignManager />}{activeTab === 'vehicle_types' && <VehicleTypesManager />}{activeTab === 'simulator' && <PricingSimulator />}</Card.Body></Card></div>
    );
};

export default TariffManager;

